import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  MessageSquare, Users, Calendar, Award, Menu, LogOut, Loader2, User, Hash, 
  Building2, BadgeCheck, Save, Edit, X, ThumbsUp, MessageCircle, Send, 
  ArrowLeft, Lock, Bell, ExternalLink, Shield, Trash2, Search, MapPin, 
  CalendarPlus, Clock, Filter, ChevronDown, ChevronUp, SlidersHorizontal, 
  ShieldAlert, Info, AlertTriangle, Beer, Navigation as NavIcon, Map as MapIcon, 
  Plus, CheckCircle2, LogIn, Megaphone, UserPlus, UserMinus, Eye, EyeOff, Activity,
  UserCheck, ShieldCheck, Lightbulb, Bug, ClipboardList, Settings, GraduationCap, BookOpen, FileJson, PlusCircle, Rocket, List,
  Flame, Flag, CheckSquare, Square, Download, Share, Smartphone, Swords, Trophy, Target, XCircle, PenTool, 
  Database, ChevronLeft, ChevronRight, Layers, Mail, Key,
  NotebookPen, BarChart3, TrendingUp, Star,
  Printer, BellRing, Repeat, Handshake, CircleCheck, Film
} from 'lucide-react';

// ==================================================================================
// ⚠️ GESTIONE IMPORTAZIONI
// ==================================================================================

import { createClient } from '@supabase/supabase-js';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';

// ==================================================================================
// CONFIGURAZIONE SUPABASE & GEOCODING
// ==================================================================================
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const LOCATIONIQ_KEY = process.env.REACT_APP_LOCATIONIQ_KEY;

// Inizializzazione Client
const supabase = (SUPABASE_URL && SUPABASE_ANON_KEY) 
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        flowType: 'implicit',
        // I token arrivano nel frammento dalla pagina di autenticazione e
        // vengono consumati da consumeAuthFragment: lasciarli gestire in
        // automatico li terrebbe nella barra degli indirizzi.
        detectSessionInUrl: false,
        persistSession: true,
        autoRefreshToken: true
      }
    }) 
  : null;

// ==================================================================================
// 🔐 ACCESSO CENTRALIZZATO (auth.refpublic.it)
// ==================================================================================

const AUTH_ORIGIN = 'https://auth.refpublic.it';

// Requisiti minimi della password, gli stessi di KekkoTech ID.
const checkPasswordPolicy = (pass) => {
    if (!pass || pass.length < 8) return "La password deve avere almeno 8 caratteri.";
    if (!/[A-Z]/.test(pass)) return "La password deve contenere almeno una lettera maiuscola.";
    if (!/[a-z]/.test(pass)) return "La password deve contenere almeno una lettera minuscola.";
    if (!/[0-9]/.test(pass)) return "La password deve contenere almeno un numero.";
    if (!/[^A-Za-z0-9]/.test(pass)) return "La password deve contenere almeno un simbolo (!@#$%…).";
    return null;
};

// In sviluppo si resta sul login locale: la pagina di autenticazione accetta
// solo i sottodomini di refpublic.it e localhost.
// L'accesso avviene solo su auth.refpublic.it: le app non hanno piu'
// una loro schermata di login.
const isCentralAuth = () => typeof window !== 'undefined';

// Guard anti-rimbalzo. Se per qualsiasi motivo i token non vengono consumati,
// l'app torna alla pagina di accesso, che avendo ancora la sessione la
// rispedisce subito indietro: ping-pong infinito e nessun messaggio. Dopo
// MAX_REDIRECTS tentativi ravvicinati ci si ferma e si offre una via manuale,
// come gia' fa beachvolleymanager con KekkoTech ID.
const REDIRECT_KEY = 'rp_auth_redirects';
const MAX_REDIRECTS = 3;
const REDIRECT_WINDOW_MS = 25000;

const readRedirectState = () => {
    try {
        const o = JSON.parse(sessionStorage.getItem(REDIRECT_KEY) || '{}');
        return { count: o.count || 0, ts: o.ts || 0 };
    } catch (e) { return { count: 0, ts: 0 }; }
};

const bumpRedirect = () => {
    const now = Date.now();
    let { count, ts } = readRedirectState();
    if (!ts || now - ts > REDIRECT_WINDOW_MS) { count = 0; ts = now; }
    try { sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ count: count + 1, ts })); } catch (e) { /* storage negato */ }
};

const clearRedirect = () => {
    try { sessionStorage.removeItem(REDIRECT_KEY); } catch (e) { /* storage negato */ }
};

// Vero finche' ha senso tentare ancora un rimando automatico.
const canRedirectToLogin = () => {
    const { count, ts } = readRedirectState();
    if (!ts || Date.now() - ts > REDIRECT_WINDOW_MS) return true;
    return count < MAX_REDIRECTS;
};

const goToLogin = () => {
    bumpRedirect();
    const back = window.location.origin + window.location.pathname;
    window.location.replace(`${AUTH_ORIGIN}/?redirect_uri=${encodeURIComponent(back)}`);
};

const goToLogout = () => {
    clearRedirect();
    const back = window.location.origin + window.location.pathname;
    window.location.replace(`${AUTH_ORIGIN}/logout.html?redirect_uri=${encodeURIComponent(back)}`);
};

// Legge i token lasciati nel frammento dalla pagina di autenticazione.
// Restituisce true se una sessione e' stata stabilita.
const consumeAuthFragment = async () => {
    if (!supabase || typeof window === 'undefined') return false;
    const hash = window.location.hash || '';
    if (!hash.includes('access_token')) return false;

    const p = new URLSearchParams(hash.slice(1));
    const access_token = p.get('access_token');
    const refresh_token = p.get('refresh_token');

    // Il frammento sparisce dalla barra degli indirizzi prima di qualunque
    // altra cosa: non deve restare leggibile ne' finire nella cronologia.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (!access_token || !refresh_token) return false;

    // setSession prima, refresh dopo: setSession riusa il token appena emesso e
    // lascia intatta la copia della sessione su auth.refpublic.it. Il refresh
    // invece ruota il refresh_token e invaliderebbe quella copia, costringendo
    // a rifare il login al giro successivo. Resta pero' il ripiego quando
    // setSession fallisce, per esempio se l'access_token viene rifiutato per
    // differenza di orologio fra i due domini.
    try {
        const { data, error } = await supabase.auth.setSession({
            access_token: access_token.trim(),
            refresh_token: refresh_token.trim()
        });
        if (!error && data && data.session) { clearRedirect(); return true; }
    } catch (e) { /* si prova col refresh */ }

    try {
        const { data, error } = await supabase.auth.refreshSession({ refresh_token: refresh_token.trim() });
        if (!error && data && data.session) { clearRedirect(); return true; }
        console.error('Sessione non stabilita:', error);
    } catch (e) {
        console.error('Sessione non stabilita:', e);
    }
    return false;
};

// Mostrata quando i rimandi automatici si ripetono senza esito: meglio una
// via d'uscita manuale che un rimbalzo infinito fra app e pagina di accesso.
const AuthStuck = () => (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-5 p-8 text-center">
        <p className="max-w-sm text-slate-600">
            Non riusciamo a stabilire la sessione. Prova ad accedere di nuovo; se il problema resta, esci del tutto e rientra.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => { clearRedirect(); goToLogin(); }}
                className="px-5 py-2.5 rounded-xl bg-orange-500 text-white font-semibold shadow-lg shadow-orange-500/25">
                Accedi
            </button>
            <button onClick={goToLogout}
                className="px-5 py-2.5 rounded-xl bg-slate-200 text-slate-700 font-semibold">
                Esci del tutto
            </button>
        </div>
    </div>
);

// ==================================================================================
// DATI STATICI E COSTANTI
// ==================================================================================
// Elenco di riserva: usato solo se la tabella `committees` non è
// raggiungibile o è vuota. La lista reale arriva dal database (gestita da
// manage.refpublic.it), così aggiungere un comitato non richiede un rilascio.
const COMITATI_FALLBACK = [
    "Comitato Territoriale di Bergamo", 
    "Comitato Territoriale di Brescia", 
    "Comitato Territoriale di Como", 
    "Comitato Territoriale di Cremona-Lodi", 
    "Comitato Territoriale di Mantova", 
    "Comitato Territoriale di Milano-Monza-Lecco", 
    "Comitato Territoriale di Pavia", 
    "Comitato Territoriale di Sondrio", 
    "Comitato Territoriale di Varese"
];

const RUOLI = [
    "Arbitro Nazionale", 
    "Arbitro Regionale", 
    "Arbitro Territoriale", 
    "Osservatore"
];

// --- Comitati dal database ------------------------------------------------
const fetchCommittees = async () => {
    if (!supabase) return COMITATI_FALLBACK;
    const { data, error } = await supabase
        .from('committees')
        .select('name')
        .eq('active', true)
        .order('name', { ascending: true });
    if (error || !data || data.length === 0) {
        if (error) console.warn("Comitati non caricati dal DB, uso l'elenco di riserva:", error.message);
        return COMITATI_FALLBACK;
    }
    return data.map(c => c.name);
};

// Context così ogni sezione legge lo stesso elenco senza passarlo come prop.
const CommitteesContext = React.createContext(COMITATI_FALLBACK);
const useCommittees = () => React.useContext(CommitteesContext);

const LIVELLI_GARA = ["Nazionale", "Regionale", "Territoriale"];
const GENERI_GARA = ["Maschile", "Femminile", "Misto"];

// ==================================================================================
// 📡 API HELPER FUNCTIONS
// ==================================================================================

// --- API SFIDE (CHALLENGES) ---

const fetchMyChallenges = async (userId) => {
    if (!supabase) return [];
    // Prende sfide dove sei creatore o avversario, non finite
    const { data, error } = await supabase
        .from('quiz_challenges')
        .select(`
            *,
            challenger:challenger_id(full_name, avatar_url),
            opponent:opponent_id(full_name, avatar_url)
        `)
        .or(`challenger_id.eq.${userId},opponent_id.eq.${userId}`)
        .order('created_at', { ascending: false });
    
    if (error) { console.error(error); return []; }
    return data;
};

const createChallenge = async (challengerId, opponentId) => {
    if (!supabase) return;
    const { error } = await supabase.from('quiz_challenges').insert([{
        challenger_id: challengerId,
        opponent_id: opponentId,
        status: 'pending'
    }]);
    return error;
};

const updateChallengeRound = async (challengeId, roundData, winnerId = null) => {
    if (!supabase) return;
    const updatePayload = { rounds_data: roundData };
    if (winnerId) {
        updatePayload.winner_id = winnerId;
        updatePayload.status = 'finished';
    }
    const { error } = await supabase.from('quiz_challenges').update(updatePayload).eq('id', challengeId);
    return error;
};

const updateScores = async (challengeId, p1Wins, p2Wins, nextRound, winnerId = null) => {
    if (!supabase) return;
    const payload = {
        p1_sets_won: p1Wins,
        p2_sets_won: p2Wins,
        current_round: nextRound
    };
    if (winnerId) {
        payload.winner_id = winnerId;
        payload.status = 'finished';
    }
    
    // Se c'è un vincitore, aggiorniamo anche le stats dei profili
    if (winnerId) {
        await supabase.rpc('increment_duels', { winner_uuid: winnerId }); // (Opzionale: meglio fare due update separati se non sai fare RPC)
        
        // Update manuale profili (più semplice)
        const { data: winnerProfile } = await supabase.from('profiles').select('duels_won, duels_total').eq('id', winnerId).single();
        if (winnerProfile) {
            await supabase.from('profiles').update({ 
                duels_won: (winnerProfile.duels_won || 0) + 1,
                duels_total: (winnerProfile.duels_total || 0) + 1
            }).eq('id', winnerId);
        }
        
        // Update perdente (solo total)
        const { data: challenge } = await supabase.from('quiz_challenges').select('challenger_id, opponent_id').eq('id', challengeId).single();
        if (challenge) {
            const loserId = challenge.challenger_id === winnerId ? challenge.opponent_id : challenge.challenger_id;
            const { data: loserProfile } = await supabase.from('profiles').select('duels_total').eq('id', loserId).single();
            if (loserProfile) {
                await supabase.from('profiles').update({ duels_total: (loserProfile.duels_total || 0) + 1 }).eq('id', loserId);
            }
        }
    }

    await supabase.from('quiz_challenges').update(payload).eq('id', challengeId);
};

// --- API MOTD (MESSAGE OF THE DAY) ---

const fetchActiveMOTD = async () => {
    if (!supabase) return null;
    try {
        const { data, error } = await supabase
            .from('motd')
            .select('*')
            .eq('active', true)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(); 
        
        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error fetching MOTD:', error);
        return null;
    }
};

const saveMOTD = async (message, userId) => {
    if (!supabase) return { error: 'No client' };
    
    // 1. Disattiva tutti i vecchi messaggi
    await supabase.from('motd').update({ active: false }).neq('id', 0);

    // 2. Se il messaggio non è vuoto, inseriscine uno nuovo attivo
    if (message && message.trim() !== "") {
        const { error } = await supabase
            .from('motd')
            .insert([{ message, created_by: userId, active: true }]);
        return error;
    }
    return null;
};

// --- API QUIZ QUESTIONS ---

const createQuizQuestions = async (questionsData) => {
    if (!supabase) return { error: 'No client' };
    // Gestisce sia array (JSON import) che oggetto singolo (manuale)
    const payload = Array.isArray(questionsData) ? questionsData : [questionsData];
    
    const { error } = await supabase
        .from('quiz_questions')
        .insert(payload);
        
    return error;
};

const reportQuestion = async (questionId, userId, reason) => {
    if (!supabase) return { error: 'No client' };
    
    // 1. Inserisci il report nella tabella corretta (quiz_reports)
    const { error: reportError } = await supabase
        .from('quiz_reports')
        .insert([{ 
            question_id: questionId, 
            user_id: userId,  // CORRETTO: corrisponde alla tua colonna DB
            reason: reason,
            status: 'pending' // Imposto lo status iniziale
        }]);

    if (reportError) {
        console.error("Errore Report:", reportError);
        return reportError;
    }

    // 2. Flagga la domanda per visibilità rapida (opzionale ma consigliato per performance)
    const { error: flagError } = await supabase
        .from('quiz_questions')
        .update({ is_flagged: true })
        .eq('id', questionId);

    return flagError;
};

// Funzione per l'Admin: Risolvi i report di una domanda
const resolveQuestionReports = async (questionId) => {
    if (!supabase) return;
    
    // 1. Aggiorna lo stato in quiz_reports
    await supabase
        .from('quiz_reports')
        .update({ status: 'resolved' })
        .eq('question_id', questionId);

    // 2. Rimuovi il flag dalla domanda
    await supabase
        .from('quiz_questions')
        .update({ is_flagged: false })
        .eq('id', questionId);
};

// Funzione per l'Admin: Leggi i report di una specifica domanda
const fetchReportsForQuestion = async (questionId) => {
    if (!supabase) return [];
    const { data, error } = await supabase
        .from('quiz_reports')
        .select(`
            id, 
            reason, 
            created_at, 
            user_id, 
            profiles:user_id (full_name)
        `) // Join per vedere chi ha segnalato
        .eq('question_id', questionId)
        .order('created_at', { ascending: false });
        
    if (error) console.error(error);
    return data || [];
};

const QuizAdminManager = () => {
    const [mode, setMode] = useState('manual'); 
    const [loading, setLoading] = useState(false);
    
    // Dati
    const [existingQuestions, setExistingQuestions] = useState([]);
    const [dbQuestions, setDbQuestions] = useState([]);
    const [dbPage, setDbPage] = useState(0);
    const [dbTotal, setDbTotal] = useState(0);
    const [dbSearch, setDbSearch] = useState('');
    const ROWS_PER_PAGE = 20;

    const [reports, setReports] = useState([]); 
    const [editingId, setEditingId] = useState(null);

    // STATI DUPLICATI
    const [duplicates, setDuplicates] = useState([]); // Array di gruppi di duplicati
    const [showDupModal, setShowDupModal] = useState(false);
    const [scanning, setScanning] = useState(false);

    const initialForm = {
        source_doc: 'Regole di Gioco', category: '', difficulty: 'Medio', question: '',
        option0: '', option1: '', option2: '', option3: '', correct_index: 0, explanation: ''
    };

    const [manualForm, setManualForm] = useState(initialForm);
    const [jsonInput, setJsonInput] = useState('');

    const AI_PROMPT = `Agisci come un Istruttore Tecnico. Genera un array JSON valido di 20 domande basate ESCLUSIVAMENTE sul testo del documento che ti fornirò.
REGOLE TASSATIVE:
1. Usa SOLO le informazioni presenti nel testo fornito.
2. "source_doc": Deve essere UNA di queste stringhe esatte:
   - "Regole di Gioco"
   - "Regolamento Video-Check"
   - "Casistica Ufficiale"
   - "Normative Federali"
   - "Indicazioni Tecniche"
   - "Manuale Segnapunti"
3. "category": Specifica e tecnica (es. "Il Libero", "Falli a Rete"), NON generica.
4. "explanation": DEVE citare il riferimento normativo (es. "Vedi Regola 9.1.3").

Struttura JSON obbligatoria:
[
  {
    "source_doc": "Scegli dalla lista sopra",
    "category": "...",
    "difficulty": "Difficile", 
    "question": "...",
    "options": ["A", "B", "C", "D"],
    "correct_index": 0,
    "explanation": "..."
  }
]`;

    // --- CARICAMENTI ---
    const refreshRecent = async () => {
        if (!supabase) return;
        const { data } = await supabase.from('quiz_questions').select('*').order('id', { ascending: false }).limit(50);
        setExistingQuestions(data || []);
    };

    const fetchFullDatabase = async (page = 0) => {
        setLoading(true);
        if (!supabase) return;
        let query = supabase.from('quiz_questions').select('*', { count: 'exact' });
        if (dbSearch.trim() !== '') query = query.ilike('question', `%${dbSearch}%`);
        const from = page * ROWS_PER_PAGE;
        const to = from + ROWS_PER_PAGE - 1;
        const { data, count, error } = await query.range(from, to).order('id', { ascending: false });
        if (!error) { setDbQuestions(data || []); setDbTotal(count || 0); setDbPage(page); }
        setLoading(false);
    };

    const refreshReports = async () => {
        setLoading(true);
        const { data } = await supabase.from('quiz_reports').select('*, quiz_questions(*)').eq('status', 'pending').order('created_at', { ascending: false });
        setReports(data || []);
        setLoading(false);
    };

    useEffect(() => { 
        if(mode === 'reports') refreshReports();
        else if(mode === 'database') fetchFullDatabase(0);
        else refreshRecent(); 
    }, [mode]);

    useEffect(() => {
        if (mode === 'database') { const timer = setTimeout(() => { fetchFullDatabase(0); }, 500); return () => clearTimeout(timer); }
    }, [dbSearch]);

    // --- GESTIONE DUPLICATI ---
    const handleScanDuplicates = async () => {
        setScanning(true);
        // Scarica tutte le domande (solo ID e testo per leggerezza)
        const { data, error } = await supabase.from('quiz_questions').select('id, question, source_doc').limit(3000);
        
        if (error || !data) {
            alert("Errore scansione.");
            setScanning(false);
            return;
        }

        const groups = {};
        data.forEach(q => {
            // Normalizza testo (rimuove spazi e minuscolo)
            const key = q.question.trim().toLowerCase();
            if (!groups[key]) groups[key] = [];
            groups[key].push(q);
        });

        // Filtra solo i gruppi con più di 1 elemento
        const dupGroups = Object.values(groups).filter(g => g.length > 1);
        
        setDuplicates(dupGroups);
        setScanning(false);
        
        if (dupGroups.length > 0) {
            setShowDupModal(true);
        } else {
            alert("Nessun duplicato trovato! Il database è pulito.");
        }
    };

    const handleFixDuplicate = async (group) => {
        // Mantiene il primo (ID più basso = più vecchio), cancella gli altri
        const sorted = group.sort((a, b) => a.id - b.id);
        const toKeep = sorted[0];
        const toDelete = sorted.slice(1).map(q => q.id);

        if (!window.confirm(`Mantengo la domanda ID ${toKeep.id} ed elimino ${toDelete.length} copie?`)) return;

        const { error } = await supabase.from('quiz_questions').delete().in('id', toDelete);
        
        if (error) alert("Errore eliminazione: " + error.message);
        else {
            // Rimuovi gruppo dalla lista locale
            setDuplicates(prev => prev.filter(g => g !== group));
            // Se non ci sono più duplicati, chiudi modale
            if (duplicates.length <= 1) setShowDupModal(false);
            // Aggiorna liste
            if(mode === 'database') fetchFullDatabase(dbPage); else refreshRecent();
        }
    };

    // --- AZIONI STANDARD ---
    const handleResolveReport = async (rid, status) => { await supabase.from('quiz_reports').update({ status }).eq('id', rid); refreshReports(); };
    const handleDeleteFromReport = async (rid, qid) => {
        if (!window.confirm("Eliminare domanda?")) return;
        const { error } = await supabase.from('quiz_questions').delete().eq('id', qid);
        if (!error) { await supabase.from('quiz_reports').update({ status: 'resolved' }).eq('id', rid); refreshReports(); }
    };
    const handleEditFromReport = (r) => { if (!r.quiz_questions) return alert("Domanda persa."); handleEditStart({ id: r.question_id, ...r.quiz_questions }); };
    
    const handleEditStart = (q) => {
        setEditingId(q.id);
        setManualForm({
            source_doc: q.source_doc, category: q.category, difficulty: q.difficulty,
            question: q.question, option0: q.options[0], option1: q.options[1], option2: q.options[2], option3: q.options[3],
            correct_index: q.correct_index, explanation: q.explanation
        });
        setMode('manual'); 
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault(); setLoading(true);
        const payload = {
            source_doc: manualForm.source_doc, category: manualForm.category, difficulty: manualForm.difficulty,
            question: manualForm.question, options: [manualForm.option0, manualForm.option1, manualForm.option2, manualForm.option3],
            correct_index: parseInt(manualForm.correct_index), explanation: manualForm.explanation
        };
        if (editingId) await supabase.from('quiz_questions').update(payload).eq('id', editingId);
        else await supabase.from('quiz_questions').insert([payload]);
        setLoading(false); alert(editingId ? "Aggiornata!" : "Creata!"); setManualForm(initialForm); setEditingId(null);
        refreshRecent(); 
    };

    const handleDelete = async (id, fromDbView = false) => {
        if (!window.confirm("Eliminare definitivamente?")) return;
        await supabase.from('quiz_questions').delete().eq('id', id);
        if (fromDbView) fetchFullDatabase(dbPage); else refreshRecent();
    };

    const handleJsonSubmit = async () => {
        if (!jsonInput.trim()) return;
        setLoading(true);
        try {
            const parsed = JSON.parse(jsonInput);
            if (!Array.isArray(parsed)) throw new Error("Non è un array JSON.");
            const normalizeDiff = (d) => {
                const val = d ? String(d).toLowerCase() : 'medio';
                if (val.includes('diffic') || val.includes('hard')) return 'Difficile';
                if (val.includes('facil') || val.includes('easy')) return 'Facile';
                return 'Medio';
            };
            const normalizeSource = (s) => {
                const val = s ? String(s) : 'Regole di Gioco';
                const valid = ['Regole di Gioco', 'Casistica Ufficiale', 'Normative Federali', 'Indicazioni Tecniche', 'Regolamento Video-Check', 'Manuale Segnapunti'];
                const match = valid.find(v => val.toLowerCase().includes(v.toLowerCase()));
                return match || 'Regole di Gioco';
            };
            const cleanData = parsed.map(item => ({
                source_doc: normalizeSource(item.source_doc),
                category: item.category || 'Generale',
                difficulty: normalizeDiff(item.difficulty),
                question: item.question,
                options: item.options,
                correct_index: parseInt(item.correct_index),
                explanation: item.explanation || ''
            }));
            const { error } = await supabase.from('quiz_questions').insert(cleanData);
            if (error) throw error;
            alert(`✅ Successo! Importate ${cleanData.length} domande.`);
            setJsonInput('');
            if(mode === 'database') fetchFullDatabase(0); else refreshRecent();
        } catch(e) { console.error(e); alert("Errore importazione: " + e.message); }
        setLoading(false);
    };

    const copyToClipboard = () => { navigator.clipboard.writeText(AI_PROMPT); alert("Prompt copiato!"); };

    return (
        <div className="max-w-5xl mx-auto h-[calc(100vh-140px)] flex flex-col gap-4 animate-fadeIn pb-10 relative">
            
            {/* MODALE DUPLICATI */}
            {showDupModal && (
                <div className="fixed inset-0 bg-black/60 z-[1300] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="bg-red-600 p-4 text-white flex justify-between items-center shrink-0">
                            <h3 className="font-bold flex items-center gap-2"><Layers className="h-5 w-5"/> Duplicati Rilevati ({duplicates.length})</h3>
                            <button onClick={() => setShowDupModal(false)}><X className="h-5 w-5 hover:text-red-200"/></button>
                        </div>
                        <div className="p-4 overflow-y-auto bg-slate-50 space-y-4 flex-1">
                            {duplicates.map((group, idx) => (
                                <div key={idx} className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
                                    <p className="font-bold text-slate-800 text-sm mb-2">"{group[0].question}"</p>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {group.map(q => (
                                            <span key={q.id} className="text-[10px] bg-slate-100 px-2 py-1 rounded text-slate-500 border">ID: {q.id} ({q.source_doc})</span>
                                        ))}
                                    </div>
                                    <button onClick={() => handleFixDuplicate(group)} className="w-full py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg font-bold text-xs hover:bg-red-100 transition-colors flex justify-center items-center gap-2">
                                        <Trash2 className="h-4 w-4"/> Tieni l'originale (ID {Math.min(...group.map(g=>g.id))}) e cancella {group.length - 1} copie
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 shrink-0">
                <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2"><BookOpen className="h-7 w-7 text-indigo-600" /> Gestione Quiz</h2>
                <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-lg w-full md:w-auto justify-center">
                    <button onClick={() => setMode('manual')} className={`px-3 py-2 text-xs font-bold rounded-md flex gap-2 items-center ${mode === 'manual' ? 'bg-white shadow text-indigo-600' : 'text-slate-500'}`}><Edit className="h-3 w-3"/> Editor</button>
                    <button onClick={() => setMode('json')} className={`px-3 py-2 text-xs font-bold rounded-md flex gap-2 items-center ${mode === 'json' ? 'bg-white shadow text-indigo-600' : 'text-slate-500'}`}><FileJson className="h-3 w-3"/> AI/Import</button>
                    <button onClick={() => setMode('database')} className={`px-3 py-2 text-xs font-bold rounded-md flex gap-2 items-center ${mode === 'database' ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}><Database className="h-3 w-3"/> Database</button>
                    <button onClick={() => setMode('reports')} className={`px-3 py-2 text-xs font-bold rounded-md flex gap-2 items-center ${mode === 'reports' ? 'bg-white shadow text-red-600' : 'text-slate-500'}`}><Flag className="h-3 w-3"/> Report</button>
                    {/* TASTO SCANNER DUPLICATI */}
                    <button onClick={handleScanDuplicates} disabled={scanning} className={`px-3 py-2 text-xs font-bold rounded-md flex gap-2 items-center bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors ml-1 border border-orange-200`}>
                        {scanning ? <Loader2 className="h-3 w-3 animate-spin"/> : <Layers className="h-3 w-3"/>} Scan
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
                {/* CONTENUTO TAB DATABASE (Identico a prima) */}
                {mode === 'database' && (
                    <div className="flex flex-col h-full rp-card rounded-2xl overflow-hidden">
                        <div className="p-4 border-b border-slate-100 flex gap-4 items-center bg-slate-50">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400"/>
                                <input className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-300 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Cerca nel DB..." value={dbSearch} onChange={(e) => setDbSearch(e.target.value)}/>
                            </div>
                            <span className="text-xs font-bold text-slate-500 whitespace-nowrap">Tot: {dbTotal}</span>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                            {loading ? <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-blue-500"/></div> : dbQuestions.length === 0 ? <div className="text-center p-10 text-slate-400 italic">Nessuna domanda trovata.</div> : (
                                <div className="space-y-2">
                                    {dbQuestions.map(q => (
                                        <div key={q.id} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors group bg-white">
                                            <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5">#{q.id}</span>
                                            <div className="flex-1">
                                                <div className="flex flex-wrap gap-2 mb-1"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${q.difficulty==='Difficile'?'bg-red-50 text-red-600 border-red-100':'bg-green-50 text-green-600 border-green-100'}`}>{q.difficulty}</span><span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">{q.source_doc}</span></div>
                                                <p className="text-sm font-bold text-slate-800 line-clamp-2">{q.question}</p>
                                                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{q.explanation}</p>
                                            </div>
                                            <div className="flex flex-col gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => handleEditStart(q)} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded"><Edit className="h-4 w-4"/></button><button onClick={() => handleDelete(q.id, true)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded"><Trash2 className="h-4 w-4"/></button></div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                            <button disabled={dbPage === 0} onClick={() => fetchFullDatabase(dbPage - 1)} className="p-2 rounded-lg hover:bg-white disabled:opacity-30 border border-transparent hover:border-slate-200 transition-all"><ChevronLeft className="h-5 w-5 text-slate-600"/></button>
                            <span className="text-xs font-bold text-slate-600">Pagina {dbPage + 1} di {Math.ceil(dbTotal / ROWS_PER_PAGE) || 1}</span>
                            <button disabled={(dbPage + 1) * ROWS_PER_PAGE >= dbTotal} onClick={() => fetchFullDatabase(dbPage + 1)} className="p-2 rounded-lg hover:bg-white disabled:opacity-30 border border-transparent hover:border-slate-200 transition-all"><ChevronRight className="h-5 w-5 text-slate-600"/></button>
                        </div>
                    </div>
                )}

                {mode === 'reports' && (
                    <div className="flex-1 overflow-y-auto bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                        <h3 className="font-bold text-lg flex items-center gap-2"><Flag className="h-5 w-5 text-red-500"/> Segnalazioni ({reports.length})</h3>
                        {reports.length === 0 && <p className="text-slate-400 italic">Tutto pulito.</p>}
                        {reports.map(r => (
                            <div key={r.id} className="p-4 border border-red-100 bg-red-50 rounded-xl">
                                <p className="font-bold text-slate-800 text-sm mb-1">"{r.quiz_questions?.question || 'Domanda eliminata'}"</p>
                                <div className="bg-white p-3 rounded-lg border border-red-100 text-xs text-red-800 font-medium mb-3">Motivo: {r.reason}</div>
                                <div className="flex gap-2"><button onClick={() => handleEditFromReport(r)} className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold">Correggi</button><button onClick={() => handleDeleteFromReport(r.id, r.question_id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs font-bold">Elimina</button><button onClick={() => handleResolveReport(r.id, 'ignored')} className="bg-slate-200 text-slate-700 px-3 py-1 rounded text-xs font-bold">Ignora</button></div>
                            </div>
                        ))}
                    </div>
                )}

                {mode === 'manual' && (
                    <div className="flex flex-col md:flex-row gap-4 h-full overflow-hidden">
                        <div className="w-full md:w-1/3 bg-slate-50 p-4 rounded-2xl border border-slate-200 overflow-y-auto">
                            <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">{editingId ? <Edit className="h-4 w-4"/> : <PlusCircle className="h-4 w-4"/>} {editingId ? 'Modifica' : 'Nuova'}</h3>
                            <form onSubmit={handleManualSubmit} className="space-y-3">
                                <div className="grid grid-cols-1 gap-2">
                                    <select className="border p-2 rounded text-sm w-full" value={manualForm.source_doc} onChange={e=>setManualForm({...manualForm, source_doc: e.target.value})}>{['Regole di Gioco','Casistica Ufficiale','Normative Federali','Indicazioni Tecniche','Regolamento Video-Check','Manuale Segnapunti'].map(o=><option key={o} value={o}>{o}</option>)}</select>
                                    <select className="border p-2 rounded text-sm w-full" value={manualForm.difficulty} onChange={e=>setManualForm({...manualForm, difficulty: e.target.value})}>{['Facile','Medio','Difficile'].map(o=><option key={o} value={o}>{o}</option>)}</select>
                                </div>
                                <input className="w-full border p-2 rounded text-sm" placeholder="Categoria" value={manualForm.category} onChange={e=>setManualForm({...manualForm, category: e.target.value})}/>
                                <textarea className="w-full border p-2 rounded text-sm h-20" placeholder="Domanda" value={manualForm.question} onChange={e=>setManualForm({...manualForm, question: e.target.value})}/>
                                <div className="space-y-2">{[0,1,2,3].map(i => (<div key={i} className="flex items-center gap-2"><span className="text-xs font-bold w-4">{String.fromCharCode(65+i)}</span><input className="flex-1 border p-2 rounded text-sm" placeholder={`Opzione ${i+1}`} value={manualForm[`option${i}`]} onChange={e=>setManualForm({...manualForm, [`option${i}`]: e.target.value})}/><input type="radio" name="correct" checked={manualForm.correct_index === i} onChange={()=>setManualForm({...manualForm, correct_index: i})}/></div>))}</div>
                                <textarea className="w-full border p-2 rounded text-sm h-16" placeholder="Spiegazione" value={manualForm.explanation} onChange={e=>setManualForm({...manualForm, explanation: e.target.value})}/>
                                <div className="flex gap-2 pt-2">{editingId && <button type="button" onClick={() => {setEditingId(null); setManualForm(initialForm)}} className="bg-white border border-slate-300 text-slate-600 px-4 rounded-lg text-sm font-bold">Annulla</button>}<button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700">Salva</button></div>
                            </form>
                        </div>
                        <div className="flex-1 rp-card rounded-2xl p-4 overflow-hidden flex flex-col">
                            <h3 className="font-bold text-slate-800 mb-4 flex justify-between items-center"><span>Recenti (ultime 50)</span> <button onClick={refreshRecent} className="text-xs text-blue-500 hover:underline">Aggiorna</button></h3>
                            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                                {existingQuestions.map(q => (
                                    <div key={q.id} className={`p-3 border rounded-lg flex justify-between items-start hover:bg-indigo-50 cursor-pointer transition-colors ${editingId === q.id ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-100'}`} onClick={() => handleEditStart(q)}>
                                        <div className="flex-1 pr-2"><div className="flex flex-wrap gap-1 mb-1"><span className="text-[9px] font-bold bg-slate-200 text-slate-600 px-1.5 rounded">{q.source_doc}</span><span className={`text-[9px] font-bold px-1.5 rounded text-white ${q.difficulty === 'Difficile' ? 'bg-red-400' : 'bg-green-500'}`}>{q.difficulty}</span></div><p className="text-xs font-bold text-slate-800 line-clamp-2">{q.question}</p></div>
                                        <button onClick={(e) => {e.stopPropagation(); handleDelete(q.id)}} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="h-4 w-4"/></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {mode === 'json' && (
                    <div className="flex-1 overflow-y-auto space-y-6">
                        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl text-white shadow-lg text-center border border-slate-700 mx-auto max-w-2xl">
                            <Rocket className="h-12 w-12 text-yellow-400 mx-auto mb-4 animate-bounce-in"/>
                            <h3 className="font-black text-2xl mb-2">Generatore AI (x20)</h3>
                            <p className="text-slate-400 text-sm mb-6">Crea 20 domande con un clic. Copia il prompt e incollalo su ChatGPT/Gemini/Google AI Studio.</p>
                            <button onClick={copyToClipboard} className="bg-indigo-500 hover:bg-indigo-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-3 mx-auto transform active:scale-95 group"><ClipboardList className="h-5 w-5 group-hover:rotate-12 transition-transform"/> Copia Prompt</button>
                        </div>
                        <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm max-w-2xl mx-auto">
                            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Download className="h-5 w-5 text-slate-500"/> Incolla JSON qui</h3>
                            <textarea className="w-full h-48 p-4 border border-slate-300 rounded-xl font-mono text-xs bg-slate-50 focus:bg-white outline-none focus:ring-2 focus:ring-indigo-500" placeholder='[ { "question": "...", ... } ]' value={jsonInput} onChange={e=>setJsonInput(e.target.value)}/>
                            <button onClick={handleJsonSubmit} className="w-full mt-4 py-3 bg-slate-800 text-white rounded-lg font-bold hover:bg-slate-900 transition-colors">Importa Domande</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const updateQuizQuestion = async (id, questionData) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('quiz_questions')
        .update(questionData)
        .eq('id', id);
    return error;
};

const deleteQuizQuestion = async (id) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', id);
    return error;
};

const fetchAllQuestionsForAdmin = async () => {
    if (!supabase) return [];
    // Recupera le ultime 100 domande per gestirle
    const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
    
    if (error) {
        console.error(error);
        return [];
    }
    return data;
};

// Recupera gli eventi creati dall'utente
const fetchUserCreatedMeetups = async (userId) => {
    if (!userId || !supabase) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const { data, error } = await supabase
        .from('meetups')
        .select('*')
        .eq('created_by', userId)
        .gte('event_date', today.toISOString()) // Mostra solo eventi futuri o odierni? Rimuovi .gte se vuoi lo storico
        .order('event_date', { ascending: true });
    if (error) return [];
    return data;
};

// --- API FOLLOWER & PREFERENZE ---

const fetchFollowing = async (userId) => {
    if (!supabase) return [];
    const { data, error } = await supabase
        .from('followers')
        .select('followed_id')
        .eq('follower_id', userId);
    if (error) {
        console.error('Error fetching following:', error);
        return [];
    }
    return data.map(f => f.followed_id);
};

const fetchFollowers = async (userId) => {
    if (!supabase) return [];
    const { data, error } = await supabase.from('followers').select('follower_id').eq('followed_id', userId);
    if (error) return [];
    return data.map(f => f.follower_id);
};

const fetchUserStats = async (userId) => {
    if (!supabase) return { followers: 0, following: 0 };
    const { count: followers } = await supabase.from('followers').select('*', { count: 'exact', head: true }).eq('followed_id', userId);
    const { count: following } = await supabase.from('followers').select('*', { count: 'exact', head: true }).eq('follower_id', userId);
    return { followers, following };
};

// Per Admin: Recupera la lista completa con nomi e avatar
const fetchFollowDetails = async (userId, type) => {
    if (!supabase) return [];
    try {
        const columnToSearch = type === 'followers' ? 'followed_id' : 'follower_id';
        const columnToSelect = type === 'followers' ? 'follower_id' : 'followed_id';

        // Step 1: Prendi gli ID
        const { data: relationData, error: relationError } = await supabase
            .from('followers')
            .select(columnToSelect)
            .eq(columnToSearch, userId);

        if (relationError) throw relationError;
        
        const userIds = relationData.map(row => row[columnToSelect]);
        if (userIds.length === 0) return [];

        // Step 2: Prendi i profili completi
        const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, full_name, role, avatar_url, matricola, comitato, special_text, is_admin, anno_inizio, is_super_admin')
            .in('id', userIds);

        if (profilesError) throw profilesError;
        return profilesData;
    } catch (e) {
        console.error(e);
        return [];
    }
};


// Per Super Admin: Rimuovi forzatamente una relazione
const forceRemoveFollow = async (followerId, followedId) => {
    if(!supabase) return;
    await supabase.from('followers').delete().eq('follower_id', followerId).eq('followed_id', followedId);
};

const followUser = async (followerId, followedId) => {
    if (!supabase) return;
    await supabase.from('followers').insert([{ follower_id: followerId, followed_id: followedId }]);
};

const unfollowUser = async (followerId, followedId) => {
    if (!supabase) return;
    await supabase.from('followers').delete().eq('follower_id', followerId).eq('followed_id', followedId);
};

const updateViewPreference = async (userId, viewMode) => {
    if (!supabase) return;
    const { data: profile } = await supabase.from('profiles').select('preferences').eq('id', userId).single();
    const currentPrefs = profile?.preferences || {};
    
    await supabase.from('profiles').update({ 
        preferences: { ...currentPrefs, view_mode: viewMode } 
    }).eq('id', userId);
};

// --- UTILITY GEOCODING (LOCATIONIQ) ---
const geocodeAddress = async (city, address) => {
    if (!LOCATIONIQ_KEY) {
        console.warn("LocationIQ Key mancante. Geocoding disabilitato.");
        return null;
    }
    
    const query = [address, city, "Italia"].filter(Boolean).join(', ');
    
    if (!query || query.length < 3) return null;

    try {
        await new Promise(r => setTimeout(r, 300));

        const url = `https://us1.locationiq.com/v1/search?key=${LOCATIONIQ_KEY}&q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=it`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
             console.warn("LocationIQ Error:", response.statusText);
             return null;
        }

        const data = await response.json();
        
        if (data && data.length > 0) {
            return {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon)
            };
        }
    } catch (error) {
        console.error("Errore Geocoding:", error);
    }
    return null;
};

// --- API FORUM (ARENA) ---

const fetchThreads = async () => {
  if (!supabase) return [];
  try {
      const { data, error } = await supabase
        .from('threads')
        .select('*, author:created_by(full_name, role, special_text, is_admin)') 
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data;
  } catch (error) {
      console.error('Error fetching threads:', error);
      return [];
  }
};

const fetchRecentThreads = async () => {
    if (!supabase) return [];
    try {
        const { data, error } = await supabase
          .from('threads')
          .select('*, author:created_by(full_name)') 
          .order('created_at', { ascending: false })
          .limit(20);
          
        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error fetching recent threads:', error);
        return [];
    }
};

// --- Carica messaggi ---
const fetchMessages = async (threadId) => {
  if (!supabase) return [];
  try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          profile:inviato_da (
            id, full_name, role, matricola, comitato, anno_inizio, special_text, is_admin, avatar_url 
          )
        `) // <--- AGGIUNTO avatar_url QUI SOPRA
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
  } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
  }
};


const createThread = async (titolo, userId, targetComitato, targetRuolo) => {
  if (!supabase) return [];
  try {
      const { data, error } = await supabase
        .from('threads')
        .insert([{ 
            titolo, 
            created_by: userId,
            target_comitato: targetComitato === 'Tutti' ? null : targetComitato,
            target_ruolo: targetRuolo === 'Tutti' ? null : targetRuolo
        }])
        .select();
      if (error) throw error;
      return data;
  } catch (error) {
      console.error('Error creating thread:', error);
      return null;
  }
};

const deleteThread = async (threadId) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('threads')
        .delete()
        .eq('id', threadId);
    return error;
};

const sendMessage = async ({ threadId, testo }) => {
  if (!supabase) return;
  try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('messages')
        .insert([{ 
          thread_id: threadId, 
          testo: testo, 
          inviato_da: user.id 
        }]);

      if (error) throw error;
  } catch (error) {
      console.error('Error sending message:', error);
  }
};

// Le impostazioni offrivano quattro interruttori per le notifiche dell'Arena
// ma nessuna notifica di thread veniva mai creata: erano toggle inerti.
const notifyNewThread = async (thread, author) => {
    if (!supabase || !thread) return;
    const followers = await fetchFollowers(author.id);
    const recipients = followers.filter(id => id !== author.id);
    await pushNotifications(recipients.map(id => ({
        user_id: id,
        type: 'thread_new',
        title: 'Nuova discussione',
        text: `${author.full_name} ha aperto: ${thread.titolo}`,
        link_tab: 'forum',
        link_id: thread.id
    })));
};

const notifyThreadReply = async (threadId, author) => {
    if (!supabase || !threadId) return;
    try {
        const { data: thread } = await supabase
            .from('threads')
            .select('id, titolo, created_by')
            .eq('id', threadId)
            .single();
        if (!thread) return;

        // Chi ha già scritto nella discussione (esclusi autore del messaggio e
        // proprietario, che riceve la notifica dedicata).
        const { data: participants } = await supabase
            .from('messages')
            .select('inviato_da')
            .eq('thread_id', threadId);

        const contributors = [...new Set((participants || []).map(m => m.inviato_da))]
            .filter(id => id && id !== author.id && id !== thread.created_by);

        const rows = [];
        if (thread.created_by && thread.created_by !== author.id) {
            rows.push({
                user_id: thread.created_by,
                type: 'thread_reply_own',
                title: 'Nuova risposta',
                text: `${author.full_name} ha risposto in: ${thread.titolo}`,
                link_tab: 'forum',
                link_id: thread.id
            });
        }
        contributors.forEach(id => rows.push({
            user_id: id,
            type: 'thread_reply_contrib',
            title: 'Nuovo messaggio',
            text: `${author.full_name} ha scritto in: ${thread.titolo}`,
            link_tab: 'forum',
            link_id: thread.id
        }));
        await pushNotifications(rows);
    } catch (e) { console.error("Notifiche Arena non inviate:", e); }
};

const deleteMessage = async (messageId) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
    return error;
};

// --- API UTENTI ---

// --- AGGIORNAMENTO API UTENTI ---

const updateUserEmail = async (newEmail) => {
    if (!supabase) return { error: 'No client' };
    // Supabase invierà una mail di conferma al nuovo indirizzo (e spesso anche al vecchio)
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    return error;
};

// Modificata per maggiore robustezza
const updateUserPassword = async (newPassword) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return error;
};

const signInWithMagicLink = async (email) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
            emailRedirectTo: window.location.origin, // Reindirizza qui dopo il click
        },
    });
    return error;
};

const searchUsers = async (query) => {
    if (!query || query.length < 2 || !supabase) return [];
    try {
        const { data, error } = await supabase
            .from('profiles')
            // Assicuriamoci di selezionare tutti i campi necessari, incluso avatar_url
            .select('id, full_name, matricola, role, comitato, special_text, is_admin, anno_inizio, avatar_url')
            // La sintassi .or deve essere corretta: cerca nel nome O nella matricola
            .or(`full_name.ilike.%${query}%,matricola.ilike.%${query}%`)
            .limit(10);

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error("Search error:", error);
        return [];
    }
};

const fetchUserThreads = async (userId) => {
    if (!userId || !supabase) return [];
    const { data, error } = await supabase
        .from('threads')
        .select('*')
        .eq('created_by', userId)
        .order('created_at', { ascending: false })
        .limit(5);
    if (error) return [];
    return data;
};

const fetchUserDesignations = async (targetUserId, viewerProfile) => {
    if (!targetUserId || !supabase) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const viewerId = viewerProfile?.id;
    const viewerIsAdmin = !!(viewerProfile?.is_admin || viewerProfile?.is_super_admin);
    const isMe = viewerId && viewerId === targetUserId;

    let query = supabase
        .from('designations')
        .select('*')
        .gte('match_date', today.toISOString())
        .order('match_date', { ascending: true });
    // altri utenti -> SOLO pubbliche e SOLO dove l’utente è owner
    if (!viewerIsAdmin && !isMe) {
        query = query
            .eq('user_id', targetUserId)
            .eq('is_private', false);
    } else {
        // Io o admin -> logica completa (owner + partner accettato)
        query = query.or(
            `user_id.eq.${targetUserId},and(partner_id.eq.${targetUserId},partner_status.eq.accepted)`
        );
    }

    const { data, error } = await query;
    if (error) {
        console.error("fetchUserDesignations error:", error);
        return [];
    }
    return data || [];
};



// --- STREAK: utility condivise -------------------------------------------
// Tutte le date della streak sono calcolate sul fuso orario italiano, così il
// "giorno" cambia a mezzanotte in Italia e non in UTC.
const getItalyDateStr = (dateObj) => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
}).format(dateObj);

// Il DB può restituire last_quiz_date come `date` ("2026-09-04") oppure come
// timestamp ISO: normalizziamo sempre a "YYYY-MM-DD".
const normalizeQuizDate = (lastDate) => {
    if (!lastDate) return null;
    if (typeof lastDate === 'string') return lastDate.split('T')[0];
    try { return getItalyDateStr(new Date(lastDate)); } catch { return null; }
};

const getTodayStr = () => getItalyDateStr(new Date());

const getYesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return getItalyDateStr(d);
};

const getDaysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return getItalyDateStr(d);
};

// Stati possibili di una streak:
//   'none'   mai giocato
//   'done'   quiz di oggi fatto, tutto a posto
//   'active' ultimo quiz ieri: viva, ma va confermata oggi
//   'frozen' saltato un giorno: il contatore è congelato e si può riprendere
//   'broken' saltati due o più giorni: si riparte da capo
//
// Il giorno di grazia esiste davvero anche nel calcolo (vedi
// checkAndIncrementStreak): la fiamma congelata non è solo un colore.
const getStreakState = (profile) => {
    const last = normalizeQuizDate(profile?.last_quiz_date);
    if (!last || !(profile?.streak_count > 0)) return last ? 'broken' : 'none';
    if (last === getTodayStr()) return 'done';
    if (last === getYesterdayStr()) return 'active';
    if (last === getDaysAgoStr(2)) return 'frozen';
    return 'broken';
};

const isStreakAlive = (lastDate) => {
    const last = normalizeQuizDate(lastDate);
    if (!last) return false;
    return last === getTodayStr() || last === getYesterdayStr() || last === getDaysAgoStr(2);
};

// Il numero resta a schermo anche da congelata: azzerarlo faceva sembrare
// perso un lavoro di settimane.
const getDisplayStreak = (profile) => {
    if (!profile) return 0;
    const state = getStreakState(profile);
    return (state === 'broken' || state === 'none') ? 0 : (profile.streak_count || 0);
};

const hasPlayedToday = (profile) =>
    !!profile && normalizeQuizDate(profile.last_quiz_date) === getTodayStr();

const checkAndIncrementStreak = async (userId, currentStreak, lastDate) => {
    if (!supabase) return null;

    const lastDateStr = normalizeQuizDate(lastDate);
    const todayStr = getTodayStr();

    // Già giocato oggi: non tocchiamo nulla.
    if (lastDateStr === todayStr) {
        return { streak: (currentStreak ?? 0), last_quiz_date: todayStr, alreadyPlayed: true };
    }

    const yesterdayStr = getYesterdayStr();
    const cur = (currentStreak ?? 0);

    // Aver giocato oggi vale sempre almeno 1 giorno di streak.
    // Un solo giorno saltato non azzera: la streak era congelata e riprende
    // da dove si era fermata. Da due giorni in su si ricomincia.
    const twoDaysAgoStr = getDaysAgoStr(2);
    const resumed = lastDateStr === twoDaysAgoStr;
    const newStreak = (lastDateStr === yesterdayStr || resumed) ? cur + 1 : 1;

    const { error } = await supabase
        .from('profiles')
        .update({
            streak_count: newStreak,
            last_quiz_date: todayStr
        })
        .eq('id', userId);

    if (error) {
        console.error("Errore aggiornamento streak:", error);
        return { streak: cur, last_quiz_date: lastDateStr ?? null };
    }

    return {
        streak: newStreak,
        last_quiz_date: todayStr,
        alreadyPlayed: false,
        resumed,
        restarted: newStreak === 1 && cur > 1,
        isNewRecord: newStreak > cur
    };
};


const fetchDesignations = async () => {
    if (!supabase) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);

    try {
        const { data, error } = await supabase
            .from('designations')
            .select(`
                *,
                profile:user_id (full_name, role, matricola, comitato, avatar_url),
                partner:partner_id (full_name, role, avatar_url, matricola),
                second_referee:second_referee_id (full_name, role, avatar_url, matricola),
                scorekeeper:scorekeeper_id (full_name, role, avatar_url, matricola),
                videocheck:videocheck_id (full_name, role, avatar_url, matricola)
            `) 
            .gte('match_date', today.toISOString()) 
            .order('match_date', { ascending: true });
        
        if (error) throw error;
        return data;
    } catch (error) {
        console.error("Error fetching designations", error);
        return [];
    }
};

// Helper unico per creare notifiche. Filtra i destinatari nulli e non
// notifica mai l'utente per un'azione che ha compiuto lui stesso.
const pushNotifications = async (rows) => {
    if (!supabase) return;
    const payload = (rows || []).filter(r => r && r.user_id);
    if (payload.length === 0) return;
    const { error } = await supabase.from('notifications').insert(payload.map(r => ({ read: false, type: 'info', ...r })));
    if (error) console.error("Errore invio notifica:", error);
};

const ROLE_LABELS = {
    partner: 'collega di gara',
    second_referee: '2° Arbitro',
    scorekeeper: 'Segnapunti',
    videocheck: 'Addetto al VideoCheck'
};

const respondToInvite = async (designationId, roleType, status) => {
    if (!supabase) return;
    
    let updateField = {};
    if (roleType === 'partner') updateField = { partner_status: status };
    else if (roleType === 'second_referee') updateField = { second_referee_status: status };
    else if (roleType === 'scorekeeper') updateField = { scorekeeper_status: status };
    else if (roleType === 'videocheck') updateField = { videocheck_status: status };

    const { error } = await supabase
        .from('designations')
        .update(updateField)
        .eq('id', designationId);

    // Chi ha creato la gara non veniva avvisato dell'esito dell'invito:
    // doveva riaprire la scheda per scoprirlo.
    if (!error) {
        try {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            const { data: d } = await supabase
                .from('designations')
                .select('user_id, home_team, away_team')
                .eq('id', designationId)
                .single();
            if (d && authUser && d.user_id !== authUser.id) {
                const { data: me } = await supabase.from('profiles').select('full_name').eq('id', authUser.id).single();
                const who = me?.full_name || 'Un collega';
                const label = ROLE_LABELS[roleType] || 'ufficiale di gara';
                await pushNotifications([{
                    user_id: d.user_id,
                    type: 'designation',
                    title: status === 'accepted' ? 'Invito accettato' : 'Invito rifiutato',
                    text: `${who} ${status === 'accepted' ? 'ha accettato' : 'ha rifiutato'} il ruolo di ${label} per ${d.home_team} - ${d.away_team}.`,
                    link_tab: 'designations'
                }]);
            }
        } catch (e) { console.error("Notifica esito invito non inviata:", e); }
    }
    return error;
};

const fetchMyDesignations = async (userId) => {
    if (!supabase) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    // Stessa logica OR per la dashboard
    const { data, error } = await supabase
        .from('designations')
        .select('*')
        .or(`user_id.eq.${userId},and(partner_id.eq.${userId},partner_status.eq.accepted)`)
        .gte('match_date', today.toISOString())
        .order('match_date', { ascending: true })
        .limit(5);
    if (error) return [];
    return data;
};

const createDesignation = async (designationData) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('designations')
        .insert([designationData]);
    return error;
};

const updateDesignation = async (id, data) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('designations')
        .update(data)
        .eq('id', id);
    return error;
};

const deleteDesignation = async (id) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('designations')
        .delete()
        .eq('id', id);
    return error;
};

// --- API TERZO TEMPO (MEETUPS) ---

const fetchMeetups = async () => {
    if (!supabase) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);

    try {
        const { data, error } = await supabase
            .from('meetups')
            .select(`
                *,
                organizer:created_by (id, full_name, role, matricola, comitato, special_text, is_admin, anno_inizio, avatar_url),
                meetup_participants (
                    user_id,
                    profiles:user_id (id, full_name, role, matricola, comitato, anno_inizio, avatar_url)
                )
            `) // <--- ORA SCARICA TUTTO DEL PROFILO PARTECIPANTE
            .gte('event_date', today.toISOString())
            .order('event_date', { ascending: true });

        if (error) throw error;
        return data;
    } catch (error) {
        console.error("Error fetching meetups", error);
        return [];
    }
};

const fetchJoinedMeetups = async (userId) => {
    if (!supabase || !userId) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    try {
        const { data: participations, error: partError } = await supabase
            .from('meetup_participants')
            .select('meetup_id')
            .eq('user_id', userId);
            
        if (partError) throw partError;
        
        const ids = participations?.map(p => p.meetup_id) || [];
        if (ids.length === 0) return [];

        const { data, error } = await supabase
            .from('meetups')
            .select('*')
            .in('id', ids)
            .gte('event_date', today.toISOString())
            .order('event_date', { ascending: true });
            
        if (error) throw error;
        return data;
    } catch (error) {
        console.error("Error fetching joined meetups", error);
        return [];
    }
};

const createMeetup = async (meetupData) => {
    if (!supabase) return { error: 'No client' };
    const { data, error } = await supabase
        .from('meetups')
        .insert([meetupData])
        .select('id')
        .single();
    if (error) return error;

    // Chi organizza il ritrovo partecipa: prima l'organizzatore risultava
    // assente dal suo stesso evento finche' non premeva "Partecipa".
    const joinError = await joinMeetup(data.id, meetupData.created_by);
    if (joinError) console.error("Organizzatore non iscritto al proprio evento:", joinError);
    return null;
};

const updateMeetup = async (id, data) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('meetups')
        .update(data)
        .eq('id', id);
    return error;
};

const deleteMeetup = async (id) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase
        .from('meetups')
        .delete()
        .eq('id', id);
    return error;
};

const joinMeetup = async (meetupId, userId) => {
    if (!supabase) return;
    const { error } = await supabase
        .from('meetup_participants')
        .insert([{ meetup_id: meetupId, user_id: userId }]);
    if (error) console.error("Join error", error);
};

const leaveMeetup = async (meetupId, userId) => {
    if (!supabase) return;
    const { error } = await supabase
        .from('meetup_participants')
        .delete()
        .eq('meetup_id', meetupId)
        .eq('user_id', userId);
    if (error) console.error("Leave error", error);
};

// --- ALTRE API (LIKES, PROFILI) ---

const countLikes = async (messageId) => {
  if (!supabase) return 0;
  const { count, error } = await supabase
    .from('likes')
    .select('*', { count: 'exact', head: true })
    .eq('message_id', messageId);
  if (error) return 0;
  return count;
};

const userLikedMessage = async (messageId, userId) => {
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('likes')
    .select('*')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .maybeSingle();
  
  if (error) return false;
  return !!data;
};

const toggleLike = async (messageId, userId, isLiked) => {
  if (!supabase) return;
  if (isLiked) {
    await supabase.from('likes').delete().eq('message_id', messageId).eq('user_id', userId);
  } else {
    await supabase.from('likes').insert([{ message_id: messageId, user_id: userId }]);
  }
};

const updateProfile = async (userId, newData) => {
    if (!supabase) return { error: 'No client' };
    const { is_admin, ...safeData } = newData;
    const { error } = await supabase
      .from('profiles')
      .update(safeData)
      .eq('id', userId);
    return error;
};

// ==================================================================================
// 🛠️ UTILITIES
// ==================================================================================

const getInitials = (name) => {
  if (!name) return '??';
  // I segmenti vuoti (doppi spazi, spazi finali) davano `undefined` e le
  // iniziali uscivano come "UN".
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  return parts.map(n => n[0]).slice(0, 2).join('').toUpperCase();
};

// Il file è servito da /favicon/favicon-96x96.png: il vecchio '/favicon.ico'
// non esiste e le notifiche di sistema uscivano senza icona.
const NOTIFICATION_ICON = '/favicon/web-app-manifest-192x192.png';

const sendSystemNotification = (title, body) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
        new Notification(title, { body, icon: NOTIFICATION_ICON, badge: NOTIFICATION_ICON });
    } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
            if (permission === "granted") {
                new Notification(title, { body, icon: NOTIFICATION_ICON, badge: NOTIFICATION_ICON });
            }
        });
    }
};

const addToGoogleCalendar = (match) => {
    const start = new Date(match.match_date || match.event_date);
    const end = new Date(start.getTime() + (2 * 60 * 60 * 1000)); 
    const format = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g,"");
    let title = "";
    let details = "";
    let location = match.location || "";

    if (match.title) {
        title = `📅 Terzo Tempo: ${match.title}`;
        if (match.description) details += `${match.description}\n\n`;
        if (match.organizer?.full_name) details += `Organizzato da: ${match.organizer.full_name}\n`;
    } else {
        const prefix = match.series ? `${match.series}: ` : '';
        const isFriendly = match.is_friendly ? "[AMICHEVOLE] " : "";
        title = `🏐 ${isFriendly}${prefix}${match.home_team} vs ${match.away_team}`;
        
        details += `🏆 Campionato: ${match.level} ${match.category ? '- ' + match.category : ''}\n`;
        if (match.is_friendly) details += `ℹ️ Gara Amichevole / Allenamento\n`;
        details += `\n⚖️ Ufficiali di Gara:\n`;

        // Logica per elencare gli arbitri correttamente in base a chi ha creato la gara
        
        // 1. Creatore
        if (match.profile?.full_name) details += `- ${match.my_role}: ${match.profile.full_name}\n`;

        // 2. Partner (Di solito l'altro arbitro o il 1° se creatore è ausiliario)
        if (match.partner && match.partner_status === 'accepted') {
            details += `- ${match.partner_role || 'Collega'}: ${match.partner.full_name}\n`;
        }

        // 3. Secondo Arbitro (Slot specifico)
        if (match.second_referee && match.second_referee_status === 'accepted') {
            details += `- 2° Arbitro: ${match.second_referee.full_name}\n`;
        }

        // 4. Segnapunti
        if (match.scorekeeper && match.scorekeeper_status === 'accepted') {
            details += `- Segnapunti: ${match.scorekeeper.full_name}\n`;
        }

        // 5. VideoCheck
        if (match.videocheck && match.videocheck_status === 'accepted') {
            details += `- VideoCheck: ${match.videocheck.full_name}\n`;
        }
    }
    
    details += `\n📲 Info su RefPublic`;
    const url = new URL("https://www.google.com/calendar/render");
    url.searchParams.append("action", "TEMPLATE");
    url.searchParams.append("text", title);
    url.searchParams.append("dates", `${format(start)}/${format(end)}`);
    url.searchParams.append("details", details);
    url.searchParams.append("location", location);
    window.open(url.toString(), '_blank');
};

const isThreadVisible = (thread, user) => {
    if (!user || !thread) return false;
    if (thread.created_by === user.id) return true;
    const comitatoMatch = !thread.target_comitato || thread.target_comitato === 'Tutti' || thread.target_comitato === user.comitato;
    const roleMatch = !thread.target_ruolo || thread.target_ruolo === 'Tutti' || thread.target_ruolo === user.role;
    return comitatoMatch && roleMatch;
};

const isMeetupVisible = (meetup, user) => {
    if (!user || !meetup) return false;
    if (meetup.created_by === user.id) return true; 
    const comitatoMatch = !meetup.target_comitato || meetup.target_comitato === 'Tutti' || meetup.target_comitato === user.comitato;
    const roleMatch = !meetup.target_ruolo || meetup.target_ruolo === 'Tutti' || meetup.target_ruolo === user.role;
    return comitatoMatch && roleMatch;
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; 
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c; 
    return d;
};

const deg2rad = (deg) => deg * (Math.PI/180);

// --- Avatar ---
const Avatar = ({ name, size = "md", onClick, className = "", src = null }) => {
    const sizes = { sm: "w-8 h-8 text-xs", md: "w-10 h-10 text-sm", lg: "w-20 h-20 text-2xl" };
    
    // Se l'avatar Google non è più raggiungibile l'immagine rotta restava a
    // schermo: ora si ripiega sulle iniziali.
    const [imgFailed, setImgFailed] = useState(false);
    useEffect(() => { setImgFailed(false); }, [src]);

    if (src && !imgFailed) {
        return (
            <img 
                src={src} 
                alt={name || 'Avatar'}
                onClick={onClick}
                onError={() => setImgFailed(true)}
                referrerPolicy="no-referrer"
                className={`${sizes[size] || sizes.md} ${className} rounded-full object-cover border border-slate-300 shadow-sm cursor-pointer select-none bg-slate-200`}
            />
        );
    }

    return (
        <div 
            onClick={onClick}
            // RIMOSSO: hover:bg-slate-300 (per coerenza, ma puoi lasciarlo se vuoi effetto sul placeholder)
            className={`${sizes[size] || sizes.md} ${className} rounded-full bg-slate-200 text-slate-600 font-bold flex items-center justify-center select-none cursor-pointer border border-slate-300 shadow-sm`}>
            {getInitials(name)}
        </div>
    );
};

// Sfondo dell'app: le sfumature stanno nel CSS, qui c'e' solo il fischietto.
// Serve anche al vetro: senza qualcosa dietro, le superfici traslucide
// non hanno nulla da sfocare e sembrano semplicemente grigie.
const AppBackdrop = () => (
    <div className="rp-backdrop rp-backdrop-right" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 14H18.5a1.5 1.5 0 0 0 1.5-1.5v-1a1.5 1.5 0 0 0-1.5-1.5H15" />
            <path d="M15 10a5 5 0 1 0-6 5" />
            <circle cx="9" cy="10" r="1.5" />
            <path d="M11 5V3a1.5 1.5 0 0 0-3 0v2" />
        </svg>
    </div>
);

// --- Footer unificato (stesso testo su tutti i servizi KekkoTech) ---
const SiteFooter = ({ className = "" }) => (
    <footer className={`rp-nav text-blue-50/80 mt-auto border-t md:mx-auto md:mb-4 md:w-[calc(100%-2rem)] md:max-w-7xl md:border md:rounded-2xl ${className}`}>
        <div className="max-w-7xl mx-auto px-4 py-6 text-center text-xs leading-relaxed">
            <p>RefPublic - Copyright &copy; {new Date().getFullYear()} KekkoTech. Tutti i diritti riservati.</p>
            <p className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <a href="https://refpublic.it/privacy.html" target="_blank" rel="noopener noreferrer" className="text-white hover:underline transition-colors">Privacy Policy</a>
                <span className="text-white/40" aria-hidden="true">&middot;</span>
                <a href="https://app.refpublic.it/changelog.html" target="_blank" rel="noopener noreferrer" className="text-white hover:underline transition-colors">Novit&agrave;</a>
                <span className="text-white/40" aria-hidden="true">&middot;</span>
                <a href="https://support.kekkotech.com" target="_blank" rel="noopener noreferrer" className="text-white hover:underline transition-colors">Supporto e Feedback</a>
            </p>
        </div>
    </footer>
);

// Celebrazione della streak: compare a tutto schermo quando il contatore
// avanza, e si chiude da sola. Il quiz aggiornava la streak in silenzio,
// quindi il momento in cui si guadagna un giorno non si vedeva.
const StreakCelebration = ({ result, onClose }) => {
    const [n, setN] = useState(Math.max(0, (result?.streak || 1) - 1));

    useEffect(() => {
        // Il numero sale di uno con un attimo di ritardo: e' quello che
        // rende leggibile "hai guadagnato un giorno".
        const bump = setTimeout(() => setN(result?.streak || 1), 620);
        const close = setTimeout(onClose, 4200);
        return () => { clearTimeout(bump); clearTimeout(close); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const streak = result?.streak || 1;
    const titolo = result?.resumed ? 'Streak ripresa!'
        : result?.restarted ? 'Nuova streak'
        : streak === 1 ? 'Streak avviata!'
        : 'Streak allungata!';
    const sottotitolo = result?.resumed
        ? 'Ci sei rientrato in tempo.'
        : streak === 1
            ? 'Torna domani per farla crescere.'
            : `${streak} giorni di fila. Non fermarti adesso.`;

    // Scintille sfasate: senza il ritardo salirebbero tutte insieme.
    const sparks = [
        { left: '18%', delay: '0s',    size: 'h-2 w-2' },
        { left: '32%', delay: '0.45s', size: 'h-1.5 w-1.5' },
        { left: '48%', delay: '0.15s', size: 'h-2.5 w-2.5' },
        { left: '63%', delay: '0.75s', size: 'h-1.5 w-1.5' },
        { left: '78%', delay: '0.3s',  size: 'h-2 w-2' },
        { left: '88%', delay: '0.95s', size: 'h-1 w-1' }
    ];

    return (
        <div
            onClick={onClose}
            role="dialog"
            aria-live="polite"
            aria-label={`${titolo} ${streak} giorni`}
            className="fixed inset-0 z-[2000] flex flex-col items-center justify-center cursor-pointer overflow-hidden bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 animate-fadeIn"
        >
            {/* Scintille */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                {sparks.map((sp, i) => (
                    <span
                        key={i}
                        className={`rp-spark absolute bottom-1/3 ${sp.size} rounded-full bg-orange-400`}
                        style={{ left: sp.left, animationDelay: sp.delay }}
                    />
                ))}
            </div>

            {/* Aloni concentrici dietro la fiamma */}
            <div className="relative flex items-center justify-center" aria-hidden="true">
                <span className="rp-halo absolute h-40 w-40 rounded-full bg-orange-500/25" />
                <span className="rp-halo absolute h-40 w-40 rounded-full bg-orange-400/20" style={{ animationDelay: '0.5s' }} />
                <div className="rp-flame-in">
                    <Flame className="rp-flame-breathe h-32 w-32 text-orange-400 fill-orange-500" />
                </div>
            </div>

            <div className="rp-count-pop text-center mt-6 px-6">
                <p className="text-7xl font-black text-white leading-none tabular-nums">{n}</p>
                <p className="text-orange-300 font-black uppercase tracking-[0.2em] text-xs mt-2">
                    {streak === 1 ? 'giorno' : 'giorni di fila'}
                </p>
                <h2 className="text-2xl font-black text-white mt-6">{titolo}</h2>
                <p className="text-slate-300 text-sm mt-1 max-w-xs mx-auto">{sottotitolo}</p>
            </div>

            <p className="absolute bottom-10 text-[11px] text-slate-400 uppercase tracking-widest">Tocca per continuare</p>
        </div>
    );
};

// --- Badge Streak ---
// La fiamma è accesa solo se la streak è davvero viva (quiz di oggi o di ieri).
// Se è scaduta mostra 0 spenta, con un tooltip che spiega il perché.
const StreakBadge = ({ profile, className = "" }) => {
    const value = getDisplayStreak(profile);
    const state = getStreakState(profile);

    const look = {
        done:   { wrap: 'bg-white/15 border-white/20', icon: 'text-orange-300 fill-orange-400', num: 'text-orange-50', hint: null },
        active: { wrap: 'bg-white/15 border-white/20', icon: 'text-orange-300 fill-orange-400 animate-pulse', num: 'text-orange-50', hint: 'oggi' },
        frozen: { wrap: 'bg-sky-400/20 border-sky-200/30', icon: 'text-sky-200 fill-sky-300', num: 'text-sky-50', hint: 'congelata' },
        broken: { wrap: 'bg-white/5 border-white/10', icon: 'text-white/30', num: 'text-white/40', hint: null },
        none:   { wrap: 'bg-white/5 border-white/10', icon: 'text-white/30', num: 'text-white/40', hint: null }
    }[state];

    const title = {
        done:   `Streak di ${value} ${value === 1 ? 'giorno' : 'giorni'} — quiz di oggi completato`,
        active: `Streak di ${value} ${value === 1 ? 'giorno' : 'giorni'} — gioca oggi per non perderla`,
        frozen: `Streak di ${value} ${value === 1 ? 'giorno' : 'giorni'} congelata — hai ancora oggi per riprenderla`,
        broken: 'Streak interrotta — completa un quiz per ricominciare',
        none:   'Nessuna streak attiva — completa un quiz per iniziare'
    }[state];

    return (
        <div
            title={title}
            className={`flex items-center gap-1.5 px-3 py-0.5 rounded-full border backdrop-blur-sm ${look.wrap} ${className}`}
        >
            <Flame className={`h-4 w-4 ${look.icon}`} />
            <span className={`text-xs font-bold ${look.num}`}>{value}</span>
            {look.hint && (
                <span className={`text-[9px] font-bold uppercase tracking-wide ${state === 'frozen' ? 'text-sky-100/90' : 'text-amber-200/90'}`}>
                    {look.hint}
                </span>
            )}
        </div>
    );
};

// ==================================================================================
// 🧩 COMPONENTI UI
// ==================================================================================

const InstallBanner = ({ deferredPrompt, showBanner, onClose, onInstall, isIos }) => {
    if (!showBanner && !isIos) return null;

    // Se siamo su iOS e abbiamo cliccato installa, mostriamo le istruzioni
    if (isIos && showBanner) {
        return (
            <div className="fixed inset-0 bg-black/60 z-[2000] flex items-end sm:items-center justify-center p-4 animate-fadeIn backdrop-blur-sm" onClick={onClose}>
                <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Smartphone className="h-6 w-6 text-blue-500"/> Installa su iOS
                        </h3>
                        <button onClick={onClose}><X className="h-5 w-5 text-slate-400"/></button>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">Su iPhone/iPad, l'installazione è manuale:</p>
                    <div className="space-y-3 text-sm font-medium text-slate-800">
                        <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="bg-slate-200 text-slate-600 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold">1</span>
                            <span>Tocca il tasto <span className="font-bold text-blue-600 inline-flex items-center gap-1">Condividi <Share className="h-3 w-3"/></span></span>
                        </div>
                        <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="bg-slate-200 text-slate-600 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold">2</span>
                            <span>Scorri e seleziona <span className="font-bold">"Aggiungi alla schermata Home"</span></span>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-full mt-6 py-3 bg-blue-600 text-white rounded-xl font-bold">Ho capito</button>
                </div>
            </div>
        );
    }

    // Banner Android/Chrome standard
    if (!deferredPrompt && !showBanner) return null;

    return (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:w-96 bg-slate-900 text-white p-4 rounded-xl shadow-2xl z-[1500] flex items-center justify-between gap-4 animate-slide-in border border-slate-700">
            <div className="flex-1">
                <p className="font-bold text-sm text-orange-400 mb-0.5">Installa RefPublic</p>
                <p className="text-xs text-slate-300 leading-snug">Aggiungi l'app alla home per un accesso rapido e notifiche migliori.</p>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors"><X className="h-5 w-5"/></button>
                <button onClick={onInstall} className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 shadow-lg">
                    <Download className="h-4 w-4"/> Installa
                </button>
            </div>
        </div>
    );
};

const SimpleMap = ({ markers, center, radius }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const circleRef = useRef(null);

    useEffect(() => {
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
        }
        if (!window.L) {
            const script = document.createElement('script'); script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; script.onload = initMap; document.body.appendChild(script);
        } else { initMap(); }
        return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
    }, []);

    useEffect(() => {
        if (mapInstance.current && window.L) {
            setTimeout(() => { mapInstance.current.invalidateSize(); }, 300);
            mapInstance.current.eachLayer((layer) => { if (layer instanceof window.L.Marker) { mapInstance.current.removeLayer(layer); } });
            addMarkers();
        }
    }, [markers]);

    useEffect(() => {
        if (mapInstance.current && circleRef.current && radius) {
            circleRef.current.setRadius(radius * 1000);
        }
    }, [radius]);

    const initMap = () => {
        if (!mapRef.current || mapInstance.current) return;
        const lat = center ? center.lat : 45.4642; const lng = center ? center.lng : 9.1900;
        mapInstance.current = window.L.map(mapRef.current).setView([lat, lng], 10);
        
        const tileLayerUrl = LOCATIONIQ_KEY 
            ? `https://{s}-tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${LOCATIONIQ_KEY}`
            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'; 

        window.L.tileLayer(tileLayerUrl, { 
            attribution: '© LocationIQ © OpenStreetMap contributors',
            maxZoom: 18
        }).addTo(mapInstance.current);
        
        if (center) {
            circleRef.current = window.L.circle([center.lat, center.lng], { color: 'blue', fillColor: '#30f', fillOpacity: 0.1, radius: (radius || 50) * 1000 }).addTo(mapInstance.current).bindPopup("La tua posizione");
        }
        addMarkers();
    };

    const addMarkers = () => {
        if (!window.L || !mapInstance.current) return;
        markers.forEach(m => {
            if (m.lat && m.lng) {
                window.L.marker([m.lat, m.lng]).addTo(mapInstance.current).bindPopup(`<b>${m.title}</b><br>${m.location}`);
            }
        });
    };

    return <div ref={mapRef} className="w-full h-full z-0" />;
}; 

const NotificationToast = ({ message, onClose }) => {
    if (!message) return null;
    return (
        <div className="fixed bottom-4 right-4 bg-slate-800 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-3 z-[200] animate-bounce-in">
            <div className="bg-orange-500 p-1 rounded-full"><Bell className="h-4 w-4 text-white" /></div>
            <span className="text-sm font-medium">{message}</span>
            <button onClick={onClose} className="ml-2 hover:bg-slate-700 rounded-full p-1"><X className="h-4 w-4"/></button>
        </div>
    );
};

const UserSearchModal = ({ onClose, onOpenProfile }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Debounce manuale o semplice gestione al cambio input
    const handleSearch = async (e) => {
        const val = e.target.value;
        setQuery(val);
        
        if (val.length < 2) {
            setResults([]);
            return;
        }
        
        setSearching(true);
        const data = await searchUsers(val);
        setResults(data);
        setSearching(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center p-4 pt-20 animate-fadeIn backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
                    <Search className="h-5 w-5 text-slate-400"/>
                    <input 
                        autoFocus
                        placeholder="Cerca per nome o matricola..." 
                        className="flex-1 bg-transparent outline-none text-slate-800 font-medium"
                        value={query}
                        onChange={handleSearch}
                    />
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X className="h-5 w-5 text-slate-500"/>
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-2">
                    {searching && <div className="p-4 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto"/></div>}
                    
                    {!searching && results.length === 0 && query.length >= 2 && (
                        <div className="p-4 text-center text-slate-400 text-sm">Nessun utente trovato.</div>
                    )}
                    
                    {results.map(user => (
                        <div 
                            key={user.id} 
                            onClick={() => { onOpenProfile(user); onClose(); }} 
                            className="flex items-center gap-3 p-3 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors group"
                        >
                            <Avatar name={user.full_name} src={user.avatar_url} size="md" />
                            <div>
                                <p className="font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{user.full_name}</p>
                                <p className="text-xs text-slate-500">{user.matricola || 'N/D'} • {user.role || 'N/D'}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Impostazioni ---

// Attivazione delle notifiche sul dispositivo (push a app chiusa).
const PushSettingsRow = ({ user }) => {
    const [status, setStatus] = useState('loading');
    const [busy, setBusy] = useState(false);

    useEffect(() => { getPushStatus().then(setStatus); }, []);

    const handleEnable = async () => {
        setBusy(true);
        const res = await enablePushNotifications(user.id);
        setBusy(false);
        if (res.ok) { setStatus('on'); return; }
        if (res.reason === 'denied') setStatus('denied');
        else alert("Non è stato possibile attivare le notifiche su questo dispositivo.");
    };

    const handleDisable = async () => {
        setBusy(true);
        await disablePushNotifications();
        setBusy(false);
        setStatus('off');
    };

    if (status === 'loading') {
        return <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin"/> Verifica in corso...</div>;
    }

    if (status === 'unsupported') {
        return (
            <p className="text-xs text-slate-500 leading-relaxed">
                Questo browser non supporta le notifiche push. Su iPhone funzionano
                solo se aggiungi RefPublic alla schermata Home e la apri da lì.
            </p>
        );
    }

    if (status === 'not_configured') {
        return <p className="text-xs text-slate-500 leading-relaxed">Le notifiche sul dispositivo non sono ancora attive su questo server.</p>;
    }

    if (status === 'denied') {
        return (
            <p className="text-xs text-red-600 leading-relaxed">
                Hai bloccato le notifiche per RefPublic. Per riattivarle devi
                consentirle dalle impostazioni del browser per questo sito.
            </p>
        );
    }

    const on = status === 'on';
    return (
        <div className="space-y-2">
            <p className="text-xs text-slate-500 leading-relaxed">
                {on
                    ? "Ricevi le notifiche anche con l'app chiusa su questo dispositivo."
                    : "Attivale per ricevere convocazioni, sfide e promemoria anche con l'app chiusa."}
            </p>
            <button
                type="button"
                onClick={on ? handleDisable : handleEnable}
                disabled={busy}
                className={`w-full py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${on ? 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200' : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'}`}
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <BellRing className="h-4 w-4"/>}
                {on ? 'Disattiva su questo dispositivo' : 'Attiva su questo dispositivo'}
            </button>
        </div>
    );
};

const SettingsModal = ({ user, onClose, onUpdateSettings, onOpenFeedback, onInstallApp }) => {
    const defaultSettings = {
        // Vecchie impostazioni
        threads_global: true, 
        threads_friends: true,
        my_thread_replies: true, 
        contrib_thread_replies: true,
        meetups_global: true, 
        meetups_friends: true,
        // Nuove impostazioni
        challenges_invite: true, 
        challenges_update: true, 
        daily_quiz_reminder: true,
        match_reminder: true
    };
    
    const [settings, setSettings] = useState({ ...defaultSettings, ...(user.notification_settings || {}) });
    const [saving, setSaving] = useState(false);

    const handleToggle = (key) => { setSettings(prev => ({ ...prev, [key]: !prev[key] })); };

    const handleSave = async () => {
        setSaving(true);
        const { error } = await supabase.from('profiles').update({ notification_settings: settings }).eq('id', user.id);
        setSaving(false);
        if (!error) { onUpdateSettings(settings); alert("Preferenze salvate!"); onClose(); } 
        else { alert("Errore: " + error.message); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4 animate-fadeIn backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
                
                {/* HEADER */}
                <div className="bg-slate-900 p-4 text-white flex justify-between items-center shrink-0">
                    <h3 className="font-bold flex items-center gap-2"><Settings className="h-5 w-5"/> Impostazioni & Notifiche</h3>
                    <button onClick={onClose}><X className="h-5 w-5 hover:text-slate-300"/></button>
                </div>

                {/* BODY SCROLLABILE */}
                <div className="p-5 space-y-6 overflow-y-auto flex-1">
                    
                    {/* 1. Sezione Quiz & Sfide (NUOVA) */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 border-b border-slate-100 pb-1 tracking-wider">Quiz & Competizione</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Inviti alle Sfide</span>
                                <input type="checkbox" checked={settings.challenges_invite} onChange={() => handleToggle('challenges_invite')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Aggiornamenti Sfide (Round/Esiti)</span>
                                <input type="checkbox" checked={settings.challenges_update} onChange={() => handleToggle('challenges_update')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Promemoria Allenamento Quotidiano</span>
                                <input type="checkbox" checked={settings.daily_quiz_reminder} onChange={() => handleToggle('daily_quiz_reminder')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                        </div>
                    </div>

                    {/* Gare & Diario */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 border-b border-slate-100 pb-1 tracking-wider">Gare</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center gap-3">
                                <span className="text-sm font-medium text-slate-700">Promemoria il giorno prima della gara</span>
                                <input type="checkbox" checked={settings.match_reminder} onChange={() => handleToggle('match_reminder')} className="accent-orange-600 h-4 w-4 cursor-pointer shrink-0"/>
                            </div>
                        </div>
                    </div>

                    {/* Notifiche push */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 border-b border-slate-100 pb-1 tracking-wider">Notifiche sul dispositivo</h4>
                        <PushSettingsRow user={user} />
                    </div>

                    {/* 2. Sezione Arena (VECCHIA RIPRISTINATA) */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 border-b border-slate-100 pb-1 tracking-wider">Notifiche Arena</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Nuove discussioni (Tutti)</span>
                                <input type="checkbox" checked={settings.threads_global} onChange={() => handleToggle('threads_global')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Nuove discussioni (Seguiti)</span>
                                <input type="checkbox" checked={settings.threads_friends} onChange={() => handleToggle('threads_friends')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Risposte alle mie discussioni</span>
                                <input type="checkbox" checked={settings.my_thread_replies} onChange={() => handleToggle('my_thread_replies')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Risposte dove ho partecipato</span>
                                <input type="checkbox" checked={settings.contrib_thread_replies} onChange={() => handleToggle('contrib_thread_replies')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                        </div>
                    </div>

                    {/* 3. Sezione Terzo Tempo */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 border-b border-slate-100 pb-1 tracking-wider">Notifiche Eventi</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Nuovi eventi (Tutti)</span>
                                <input type="checkbox" checked={settings.meetups_global} onChange={() => handleToggle('meetups_global')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">Nuovi eventi (Seguiti)</span>
                                <input type="checkbox" checked={settings.meetups_friends} onChange={() => handleToggle('meetups_friends')} className="accent-orange-600 h-4 w-4 cursor-pointer"/>
                            </div>
                        </div>
                    </div>

                    {/* 4. Azioni Extra (LAYOUT 4 PULSANTI RIPRISTINATO) */}
                    <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
                        
                        {/* Pulsante 1: Installazione */}
                        {onInstallApp && (
                            <button onClick={() => { onInstallApp(); onClose(); }} className="w-full py-2.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors">
                                <Download className="h-4 w-4"/> Installa App / Aggiungi a Home
                            </button>
                        )}

                        {/* Pulsante 2: Feedback */}
                        <button onClick={() => { onClose(); onOpenFeedback(); }} className="w-full py-2.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-yellow-100 transition-colors">
                            <Lightbulb className="h-4 w-4"/> Invia Feedback / Segnala Bug
                        </button>

                        {/* Pulsante 3: Changelog */}
                        <a href="/changelog.html" target="_blank" rel="noopener noreferrer" className="w-full py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-indigo-100 transition-colors decoration-0">
                            <Rocket className="h-4 w-4"/> Novità & Aggiornamenti
                        </a>
                        
                        {/* Pulsante 4: Privacy */}
                        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="w-full py-2.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors decoration-0">
                            <Shield className="h-4 w-4"/> Leggi Privacy Policy
                        </a>
                    </div>

                    {/* Tasto Salva */}
                    <button onClick={handleSave} disabled={saving} className="w-full py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 shadow-lg transition-transform transform active:scale-95">
                        {saving ? <Loader2 className="animate-spin h-5 w-5 mx-auto"/> : 'Salva Preferenze'}
                    </button>
                </div>
            </div>
        </div>
    );
};


const UserProfileModal = ({ 
    viewedUser, 
    currentUser, 
    onClose, 
    onUpdate, 
    onNavigateToDesignations, 
    onNavigateToMeetups, 
    onOpenThread, 
    followingList = [], 
    onToggleFollow, 
    avatarUrl, 
    onSwitchProfile,
    isHidden = false 
}) => {
    const committeesList = useCommittees();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...viewedUser });
    const [saving, setSaving] = useState(false);
    
    // Stati per credenziali (Solo per Me)

    // Stati per caricamento dati
    const [userThreads, setUserThreads] = useState([]);
    const [userDesignations, setUserDesignations] = useState([]);
    const [userCreatedMeetups, setUserCreatedMeetups] = useState([]); 
    const [stats, setStats] = useState({ followers: 0, following: 0 });
    
    const [showListType, setShowListType] = useState(null); 
    const [listData, setListData] = useState([]);
    const [loadingList, setLoadingList] = useState(false); 

    const isMe = currentUser && viewedUser.id === currentUser.id;
    const canEditAnything = currentUser && (currentUser.is_admin || currentUser.is_super_admin);

    const isFollowing = followingList.includes(viewedUser.id);
    const displayAvatarUrl = isMe ? avatarUrl : viewedUser.avatar_url;
    const containerStyle = isHidden ? { display: 'none' } : {};

    // 1. CARICAMENTO DATI
    useEffect(() => {
        if (viewedUser?.id) {
            // Fetch dati profilo pubblico
            supabase.from('profiles').select('*').eq('id', viewedUser.id).single()
                .then(({ data }) => {
                    if (data) {
                        setFormData(prev => ({ ...prev, ...data }));
                    }
                });


            Promise.all([
                fetchUserThreads(viewedUser.id),
                fetchUserDesignations(viewedUser.id),
                fetchUserCreatedMeetups(viewedUser.id),
                fetchUserStats(viewedUser.id)
            ]).then(([threads, designations, meetups, s]) => {
                setUserThreads(threads || []);
                setUserDesignations(designations || []);
                setUserCreatedMeetups(meetups || []);
                setStats(s);
            });
        }
    }, [viewedUser, isMe]); 

    useEffect(() => {
        if (!viewedUser?.id || !supabase) return;
        const channel = supabase.channel(`realtime_profile_${viewedUser.id}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'followers' },
                (payload) => {
                    const involvesUser = (payload.new?.follower_id === viewedUser.id) || (payload.new?.followed_id === viewedUser.id) || (payload.old?.follower_id === viewedUser.id) || (payload.old?.followed_id === viewedUser.id);
                    if (involvesUser) {
                        fetchUserStats(viewedUser.id).then(setStats);
                        if (showListType) fetchFollowDetails(viewedUser.id, showListType).then(setListData);
                    }
                }
            ).subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [viewedUser.id, showListType]);

    const handleShowList = async (type) => {
        if (showListType === type) { setShowListType(null); return; }
        if (!currentUser.is_admin && !isMe) return; 
        setShowListType(type); setListData([]); setLoadingList(true);
        const data = await fetchFollowDetails(viewedUser.id, type);
        setListData(data); setLoadingList(false);
    };

    const handleForceUnfollow = async (targetUserId) => {
        if (!currentUser.is_super_admin) return;
        if (!window.confirm("Rimuovere relazione?")) return;
        if (showListType === 'followers') await forceRemoveFollow(targetUserId, viewedUser.id);
        else await forceRemoveFollow(viewedUser.id, targetUserId);
    };

    const handleMatricolaInput = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val) && val.length <= 6) setFormData({...formData, matricola: val});
    };

    // LOGICA SALVATAGGIO AGGIORNATA
    const handleSave = async () => {
        setSaving(true);
        
        if (canEditAnything) {
            if (!formData.full_name.trim() || !formData.comitato || !formData.role) { alert("Campi obbligatori mancanti."); setSaving(false); return; }
            if (formData.matricola && (!/^\d+$/.test(formData.matricola) || formData.matricola.length > 6)) { alert("Matricola non valida."); setSaving(false); return; }
            const currentYear = new Date().getFullYear();
            if (!formData.anno_inizio || formData.anno_inizio < 1950 || formData.anno_inizio > currentYear) { alert("Anno non valido."); setSaving(false); return; }
        }

        let messages = [];
        let hasErrors = false;

        // 2. Update Profilo Pubblico (Solo Admin può cambiare dati sensibili, User può cambiare poco altro qui se abilitato)
        if (canEditAnything) {
             const errProfile = await updateProfile(viewedUser.id, formData);
             if (errProfile) { alert("Errore Profilo: " + errProfile.message); hasErrors = true; }
        }


        setSaving(false);
        
        if (!hasErrors) { 
            setIsEditing(false); 
            onUpdate(formData); 
            
            if (messages.length > 0) {
                alert(messages.join('\n'));
            } else if (canEditAnything) {
                alert("Profilo aggiornato!");
            }
        }
    };

    const goToGare = () => { onClose(); onNavigateToDesignations(viewedUser.matricola || viewedUser.full_name); };
    const goToThread = (threadId) => { onClose(); onOpenThread(threadId); };
    const goToMeetup = () => { onClose(); onNavigateToMeetups(); };

    if (!viewedUser) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex items-stretch sm:items-center justify-center p-0 sm:p-4 animate-fadeIn backdrop-blur-sm" style={containerStyle}>
            <div className="rp-glass-strong sm:rounded-2xl w-full max-w-lg overflow-hidden flex flex-col h-full sm:h-auto sm:max-h-[88vh]">
                
                {/* HEADER (Identico a prima) */}
                <div className="rp-nav px-6 pt-5 pb-4 text-white relative shrink-0 border-b">
                    <button onClick={onClose} className="absolute top-4 right-4 hover:bg-white/20 p-1 rounded-full transition-colors"><X className="h-5 w-5"/></button>
                    <div className="flex flex-col items-center">
                        <Avatar name={formData.full_name} src={displayAvatarUrl} size="lg" className="bg-white text-slate-900 border-4 border-orange-500 mb-2.5"/>
                        
                        {isEditing && canEditAnything ? (
                            <input className="text-center text-slate-900 rounded px-2 py-1 font-bold w-full text-lg mb-1" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                        ) : (
                            <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2 text-center leading-tight">
                                {formData.full_name} 
                                {formData.is_super_admin ? (<ShieldCheck className="h-6 w-6 text-yellow-400 fill-yellow-900/20" title="Super Admin" />) : formData.is_admin && (<ShieldCheck className="h-6 w-6 text-blue-400 fill-blue-900/20" title="Admin" />)}
                            </h2>
                        )}
                        
                        <span className="text-orange-400 font-medium text-sm mt-1">{formData.role || 'Nessun ruolo'}</span>
                        {formData.special_text && <span className="text-purple-300 font-bold text-xs mt-1.5 bg-white/10 px-3 py-1 rounded-full border border-white/20">{formData.special_text}</span>}
                        
                        <div className="flex gap-2.5 mt-3">
                            <StreakBadge profile={formData} className="py-1" />
                            <div className="flex items-center gap-1.5 bg-black/30 px-3 py-1 rounded-full border border-white/10 backdrop-blur-sm" title="Vittorie"><Swords className="h-4 w-4 text-indigo-300"/><span className="text-xs font-bold text-indigo-100">{formData.duels_won || 0}</span></div>
                        </div>

                        {!isMe && (
                            <button onClick={() => onToggleFollow(viewedUser.id)} className={`mt-3 px-6 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 transition-colors ${isFollowing ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-orange-500 text-white hover:bg-orange-600'}`}>
                                {isFollowing ? <><UserMinus className="h-3 w-3"/> Non seguire più</> : <><UserPlus className="h-3 w-3"/> Segui</>}
                            </button>
                        )}
                    </div>
                </div>

                <div className="rp-stack p-5 sm:p-6 flex-1 overflow-y-auto overscroll-contain custom-scrollbar">
                    {/* STATS & LISTE (Identici a prima, omessi per brevità se non modificati logicamente) */}
                    <div className="grid grid-cols-2 gap-3">
                        <div onClick={() => handleShowList('followers')} className={`text-center py-3 rounded-xl bg-slate-50 border border-slate-100 transition-colors ${(isMe || currentUser.is_admin) ? 'cursor-pointer hover:bg-slate-100' : ''}`}><span className="block font-bold text-xl text-slate-800">{stats.followers}</span><span className="text-[10px] uppercase text-slate-400 font-bold tracking-wider">Follower</span></div>
                        <div onClick={() => handleShowList('following')} className={`text-center py-3 rounded-xl bg-slate-50 border border-slate-100 transition-colors ${(isMe || currentUser.is_admin) ? 'cursor-pointer hover:bg-slate-100' : ''}`}><span className="block font-bold text-xl text-slate-800">{stats.following}</span><span className="text-[10px] uppercase text-slate-400 font-bold tracking-wider">Seguiti</span></div>
                    </div>
                    {showListType && (
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 animate-fadeIn shadow-inner">
                            <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200"><h4 className="text-xs font-bold uppercase text-slate-600 flex items-center gap-2"><Users className="h-3 w-3"/> Lista {showListType === 'followers' ? 'Follower' : 'Seguiti'}</h4><button onClick={() => setShowListType(null)} className="text-xs font-bold text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded">Chiudi</button></div>
                            <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                                {loadingList && <div className="text-center py-2"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400"/></div>}
                                {!loadingList && listData.length === 0 && <p className="text-xs text-slate-400 italic text-center py-2">Nessun utente in questa lista.</p>}
                                {!loadingList && listData.map(u => (<div key={u.id} className="flex items-center justify-between bg-white p-2 rounded-lg shadow-sm border border-slate-100 hover:border-orange-300 transition-colors cursor-pointer" onClick={() => onSwitchProfile(u)}><div className="flex items-center gap-2"><Avatar name={u.full_name} src={u.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><span className="text-xs font-bold text-slate-700 truncate max-w-[150px]">{u.full_name}</span></div>{currentUser.is_super_admin && <button onClick={(e) => { e.stopPropagation(); handleForceUnfollow(u.id); }} className="text-red-500 hover:bg-red-50 p-1.5 rounded transition-colors"><Trash2 className="h-3 w-3"/></button>}</div>))}
                            </div>
                        </div>
                    )}

                    {/* INFO FORM */}
                    <div className="space-y-4">
                         <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-3 rounded-lg"><label className="text-xs text-slate-500 uppercase font-bold flex items-center gap-1 mb-1"><Hash className="h-3 w-3"/> Matricola</label>{isEditing && canEditAnything ? <input type="text" inputMode="numeric" className="w-full border rounded px-2 py-1 font-mono text-sm bg-white" value={formData.matricola || ''} onChange={handleMatricolaInput} /> : <p className="font-mono font-semibold text-slate-700">{formData.matricola || '-'}</p>}</div>
                            <div className="bg-slate-50 p-3 rounded-lg"><label className="text-xs text-slate-500 uppercase font-bold flex items-center gap-1 mb-1"><Calendar className="h-3 w-3"/> Arbitro dal</label>{isEditing && canEditAnything ? <input type="number" min="1950" max={new Date().getFullYear()} className="w-full border rounded px-2 py-1 text-sm bg-white" value={formData.anno_inizio || ''} onChange={e => setFormData({...formData, anno_inizio: e.target.value})} /> : <p className="font-semibold text-slate-700">{formData.anno_inizio || 'N/D'}</p>}</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-lg"><label className="text-xs text-slate-500 uppercase font-bold flex items-center gap-1 mb-1"><Building2 className="h-3 w-3"/> Comitato</label>{isEditing && canEditAnything ? <select className="w-full border rounded p-1.5 text-sm bg-white" value={formData.comitato || ''} onChange={e => setFormData({...formData, comitato: e.target.value})}><option value="">Seleziona...</option>{committeesList.map(c => <option key={c} value={c}>{c}</option>)}</select> : <p className="font-semibold text-slate-700">{formData.comitato || '-'}</p>}</div>
                        {isEditing && canEditAnything && <div className="bg-slate-50 p-3 rounded-lg"><label className="text-xs text-slate-500 uppercase font-bold flex items-center gap-1 mb-1"><BadgeCheck className="h-3 w-3"/> Ruolo</label><select className="w-full border rounded p-1.5 text-sm bg-white" value={formData.role || ''} onChange={e => setFormData({...formData, role: e.target.value})}><option value="">Seleziona...</option>{RUOLI.map(r => <option key={r} value={r}>{r}</option>)}</select></div>}
                        {isEditing && currentUser.is_admin && canEditAnything && (<div className="bg-purple-50 p-3 rounded-lg border border-purple-100"><label className="text-xs text-purple-700 uppercase font-bold flex items-center gap-1 mb-1"><ShieldCheck className="h-3 w-3"/> Titolo Speciale (Admin)</label><input className="w-full border border-purple-200 rounded px-2 py-1 text-sm bg-white" value={formData.special_text || ''} onChange={e => setFormData({...formData, special_text: e.target.value})} /></div>)}
                        
                        {/* Email e password si gestiscono da profile.refpublic.it:
                            le credenziali stanno in un posto solo, dove vive
                            anche la verifica in due passaggi. */}
                        {isEditing && isMe && (
                            <div className="pt-1">
                                <a
                                    href="https://profile.refpublic.it"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-2.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
                                >
                                    <Lock className="h-4 w-4"/> Gestisci email e password
                                </a>
                                <p className="text-[10px] text-slate-400 mt-1.5 text-center">Si aprono nel tuo account RefPublic.</p>
                            </div>
                        )}
                    </div>

                    {/* VISTA SOLA LETTURA (Gare, Meetup, Thread - Identico a prima) */}
                    {!isEditing && (
                        <>
                            <div>
                                <div className="flex justify-between items-center mb-3"><h4 className="font-bold text-slate-800 flex items-center gap-2 text-sm"><Calendar className="h-4 w-4 text-orange-500"/> Prossime Gare</h4>{userDesignations.length > 0 && <button onClick={goToGare} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors">Vedi tutte</button>}</div>
                                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">{userDesignations.length === 0 && <p className="text-xs text-slate-400 italic">Nessuna gara futura.</p>}{userDesignations.slice(0, 3).map(d => (<div key={d.id} onClick={() => { onClose(); onNavigateToDesignations(d.match_date.split('T')[0]); }} className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-3 shadow-sm cursor-pointer hover:border-orange-300 transition-all group"><div className="flex flex-col items-center justify-center bg-slate-50 p-1.5 rounded border border-slate-100 min-w-[50px] group-hover:bg-orange-50"><span className="text-lg font-bold text-slate-700 leading-none group-hover:text-orange-700">{new Date(d.match_date).getDate()}</span><span className="text-[9px] font-bold uppercase text-slate-400">{new Date(d.match_date).toLocaleString('it-IT', { month: 'short' })}</span></div><div className="flex-1 min-w-0"><p className="text-xs font-bold text-slate-800 truncate">{d.home_team} vs {d.away_team}</p><p className="text-[10px] text-slate-500 flex items-center gap-1 truncate"><Clock className="h-3 w-3"/> {new Date(d.match_date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} • {d.location}</p></div></div>))}</div>
                            </div>
                            <div><h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 text-sm"><Beer className="h-4 w-4 text-orange-500"/> Eventi Organizzati</h4><div className="space-y-2 max-h-60 overflow-y-auto pr-1">{userCreatedMeetups.length === 0 && <p className="text-xs text-slate-400 italic">Nessun evento creato.</p>}{userCreatedMeetups.map(m => (<div key={m.id} onClick={goToMeetup} className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-3 shadow-sm cursor-pointer hover:border-orange-300 transition-all group"><div className="flex flex-col items-center justify-center bg-orange-50 p-1.5 rounded border border-orange-100 min-w-[50px]"><span className="text-lg font-bold text-orange-700 leading-none">{new Date(m.event_date).getDate()}</span><span className="text-[9px] font-bold uppercase text-orange-500">{new Date(m.event_date).toLocaleString('it-IT', { month: 'short' })}</span></div><div className="flex-1 min-w-0"><p className="text-xs font-bold text-slate-800 truncate">{m.title}</p><p className="text-[10px] text-slate-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3"/> {m.location}</p></div></div>))}</div></div>
                            <div><h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 text-sm"><MessageSquare className="h-4 w-4 text-blue-500"/> Ultime Discussioni</h4><div className="space-y-2 max-h-60 overflow-y-auto pr-1">{userThreads.length === 0 && <p className="text-xs text-slate-400 italic">Nessuna attività.</p>}{userThreads.map(t => (<div key={t.id} onClick={() => goToThread(t.id)} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100 cursor-pointer hover:bg-blue-50 transition-colors group"><div className="truncate pr-2"><p className="text-xs font-bold text-slate-700 truncate group-hover:text-blue-700">{t.titolo}</p><p className="text-[10px] text-slate-400">{new Date(t.created_at).toLocaleDateString()}</p></div><ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-blue-500"/></div>))}</div></div>
                        </>
                    )}
                </div>

                {(canEditAnything || isMe) && (
                    <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] border-t border-slate-200 bg-slate-50 shrink-0">
                        {canEditAnything ? (
                            isEditing ? (
                                <button onClick={handleSave} disabled={saving} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-green-500/20 transition-all transform active:scale-95">{saving ? <Loader2 className="animate-spin h-5 w-5"/> : <Save className="h-5 w-5"/>} Salva Modifiche</button>
                            ) : (
                                <button onClick={() => setIsEditing(true)} className="w-full py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all transform active:scale-95">
                                    <Edit className="h-5 w-5"/> {isMe ? 'Modifica Account' : 'Modifica Profilo'}
                                </button>
                            )
                        ) : (
                            <a
                                href="https://profile.refpublic.it"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all transform active:scale-95"
                            >
                                <ExternalLinkIcon className="h-5 w-5"/> RefPublic Profile
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
// Icona "link esterno" (Tabler): il profilo vive su un altro dominio.
const ExternalLinkIcon = ({ className = "h-5 w-5" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" />
        <path d="M11 13l9 -9" />
        <path d="M15 4h5v5" />
    </svg>
);

// --- SCHEDA SEGUITI ---

const FollowingFeed = ({ user, onOpenProfile, followingList }) => {
    const [followingUsers, setFollowingUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadFollowingDetails = async () => {
            if (followingList.length === 0) { setFollowingUsers([]); setLoading(false); return; }
            const { data } = await supabase.from('profiles').select('*').in('id', followingList);
            setFollowingUsers(data || []);
            setLoading(false);
        };
        loadFollowingDetails();
    }, [followingList]);

    return (
        <div className="flex flex-col h-[calc(100vh-140px)] gap-4">
            <div className="rp-card p-4 rounded-2xl">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><UserCheck className="h-6 w-6 text-orange-500"/> Seguiti</h2>
                <p className="text-sm text-slate-500">Le persone che segui.</p>
            </div>
            <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 overflow-y-auto p-4">
                {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500"/> : (
                    followingUsers.length === 0 ? (
                        <div className="text-center text-slate-400 mt-10">Non segui ancora nessuno. Cerca utenti per iniziare a seguirli.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {followingUsers.map(u => (
                                <div key={u.id} className="bg-white p-3 rounded-lg border border-slate-200 flex items-center justify-between cursor-pointer hover:border-orange-300 transition-all" onClick={() => onOpenProfile(u)}>
                                    <div className="flex items-center gap-3">
                                        {/* AGGIUNTO src={u.avatar_url} */}
                                        <Avatar name={u.full_name} src={u.avatar_url} />
                                        <div>
                                            <p className="font-bold text-slate-800">{u.full_name}</p>
                                            <p className="text-xs text-slate-500">{u.role}</p>
                                        </div>
                                    </div>
                                    <span className="text-xs text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded">Vedi Profilo</span>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

const MeetupsManager = ({ user, userLocation, onOpenProfile, followingList, viewMode }) => { 
    const committeesList = useCommittees();
    const [meetups, setMeetups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    const [isMapView, setIsMapView] = useState(false);
    const [radiusFilter, setRadiusFilter] = useState(50); 
    const [showFilters, setShowFilters] = useState(false);

    // Filtri
    const [filterSearch, setFilterSearch] = useState('');
    const [filterComitato, setFilterComitato] = useState('Tutti');
    const [filterRuolo, setFilterRuolo] = useState('Tutti');

    // Form
    const [form, setForm] = useState({ 
        title: '', description: '', 
        event_date: '', event_time: '', 
        city: '', address: '', 
        target_comitato: 'Tutti', target_ruolo: 'Tutti' 
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- LOGICA SMART FILTERS ---
    // Allineata a Gare e Arena: i super admin vedono tutti i comitati, gli
    // altri il proprio. L'elenco arriva dalla tabella `committees`.
    const getAvailableTargetCommittees = () => {
        if (user.is_super_admin || user.is_admin) return ["Tutti", ...committeesList];
        return user.comitato ? ["Tutti", user.comitato] : ["Tutti", ...committeesList];
    };

    const getAvailableTargetRoles = () => {
        const roles = ["Tutti"];
        const myRoleIdx = RUOLI.indexOf(user.role);
        if (myRoleIdx !== -1) {
            for (let i = myRoleIdx; i < RUOLI.length; i++) {
                roles.push(RUOLI[i]);
            }
        } else {
            return ["Tutti", ...RUOLI];
        }
        return roles;
    };

    const availableCommittees = getAvailableTargetCommittees();
    const availableRoles = getAvailableTargetRoles();

    useEffect(() => { loadMeetups(); }, [radiusFilter, viewMode, followingList]);

    const loadMeetups = async () => {
        setLoading(true);
        const data = await fetchMeetups();
        let enhanced = data.filter(m => isMeetupVisible(m, user)); 
        if (viewMode === 'following') enhanced = enhanced.filter(m => followingList.includes(m.created_by) || m.created_by === user.id);
        if (userLocation && enhanced.length > 0) {
            enhanced = enhanced.map(m => ({ ...m, distance: (m.lat && m.lng) ? calculateDistance(userLocation.lat, userLocation.lng, m.lat, m.lng) : null }))
            .filter(m => m.created_by === user.id || m.distance === null || m.distance <= radiusFilter)
            .sort((a, b) => (a.distance || 9999) - (b.distance || 9999));
        }
        setMeetups(enhanced);
        setLoading(false);
    };

    const notifyFollowers = async (title) => {
        const followersIds = await fetchFollowers(user.id);
        if (followersIds.length === 0) return;
        const notificationsPayload = followersIds.map(followerId => ({
            user_id: followerId, type: 'meetup', title: 'Nuovo Evento Terzo Tempo', text: `${user.full_name} ha organizzato: ${title}`, link_tab: 'meetups', read: false
        }));
        await supabase.from('notifications').insert(notificationsPayload);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.city || !form.event_date || !form.event_time) { alert("Compila almeno Città, Data e Ora."); return; }
        setIsSubmitting(true);
        
        const dateTimeString = `${form.event_date}T${form.event_time}:00`;
        const fullLocation = form.address ? `${form.address}, ${form.city}` : form.city;
        const coords = await geocodeAddress(form.city, form.address || ""); 
        
        const payload = {
            created_by: user.id, 
            title: form.title, 
            description: form.description, 
            location: fullLocation, 
            event_date: new Date(dateTimeString).toISOString(),
            target_comitato: form.target_comitato === 'Tutti' ? null : form.target_comitato,
            target_ruolo: form.target_ruolo === 'Tutti' ? null : form.target_ruolo,
            lat: coords ? coords.lat : null, 
            lng: coords ? coords.lng : null
        };
        
        let error;
        if (isEditing) {
            error = await updateMeetup(isEditing, payload);
        } else { 
            error = await createMeetup(payload); 
            if (!error) await notifyFollowers(form.title); 
        }
        
        setIsSubmitting(false);
        
        if (!error) { 
            setIsAdding(false); 
            setIsEditing(null); 
            setForm({ title: '', description: '', event_date: '', event_time: '', city: '', address: '', target_comitato: 'Tutti', target_ruolo: 'Tutti' }); 
            loadMeetups(); 
        } else { 
            alert("Errore: " + error.message); 
        }
    }; 

    const handleEditStart = (m) => {
        let existingCity = ''; let existingAddress = m.location || '';
        if (m.location && m.location.includes(',')) { const parts = m.location.split(','); existingCity = parts.pop().trim(); existingAddress = parts.join(',').trim(); } 
        else { existingCity = m.location; existingAddress = ''; }
        setForm({ 
            title: m.title, description: m.description || '', city: existingCity, address: existingAddress,
            event_date: new Date(m.event_date).toISOString().split('T')[0], event_time: new Date(m.event_date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}), 
            target_comitato: m.target_comitato || 'Tutti', target_ruolo: m.target_ruolo || 'Tutti' 
        });
        setIsEditing(m.id); setIsAdding(true);
    };

    const handleDelete = async (id) => { if(window.confirm("Eliminare evento?")) { setMeetups(prev => prev.filter(m => m.id !== id)); await deleteMeetup(id); } };
    const handleJoinToggle = async (m) => { const amParticipant = m.meetup_participants?.some(p => p.user_id === user.id); if (amParticipant) { if(window.confirm("Vuoi disdire la tua partecipazione?")) await leaveMeetup(m.id, user.id); } else { await joinMeetup(m.id, user.id); } loadMeetups(); };

    const filteredMeetups = meetups.filter(m => {
        const isMine = m.created_by === user.id;
        if (!isMine) {
            const comitatoOk = !m.target_comitato || m.target_comitato === 'Tutti' || m.target_comitato === user.comitato;
            const ruoloOk = !m.target_ruolo || m.target_ruolo === 'Tutti' || m.target_ruolo === user.role;
            if (!comitatoOk || !ruoloOk) return false;
        }
        if (viewMode === 'following') { if (!isMine && !followingList.includes(m.created_by)) return false; }
        if (filterSearch && !m.title.toLowerCase().includes(filterSearch.toLowerCase()) && !m.description?.toLowerCase().includes(filterSearch.toLowerCase())) return false;
        const targetC = m.target_comitato || 'Tutti'; const targetR = m.target_ruolo || 'Tutti';
        if (filterComitato !== 'Tutti' && targetC !== 'Tutti' && targetC !== filterComitato) return false;
        if (filterRuolo !== 'Tutti' && targetR !== 'Tutti' && targetR !== filterRuolo) return false;
        if (userLocation && m.distance !== null && !isMine) { if (m.distance > radiusFilter) return false; }
        return true;
    });

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn pb-20">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
                <div><h2 className="text-2xl font-black text-slate-800 flex items-center gap-3"><Beer className="h-7 w-7 text-orange-600" /> Terzo Tempo</h2><p className="text-sm text-slate-500 mt-1 font-medium">Ritrovi, cene e tempo libero per arbitri.</p></div>
                <div className="flex gap-2 w-full md:w-auto"><button onClick={() => setIsMapView(!isMapView)} className="flex-1 md:flex-none px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold flex items-center justify-center gap-2 text-sm transition-colors text-slate-700">{isMapView ? <><Users className="h-4 w-4"/> Lista</> : <><MapIcon className="h-4 w-4"/> Mappa</>}</button><button onClick={() => { setIsAdding(!isAdding); setIsEditing(null); setForm({ title: '', description: '', event_date: '', event_time: '', city: '', address: '', target_comitato: 'Tutti', target_ruolo: 'Tutti' }); }} className="w-full md:w-auto py-2.5 px-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all active:scale-95">{isAdding ? <X className="h-5 w-5"/> : <Plus className="h-5 w-5"/>} {isAdding ? 'Chiudi' : 'Organizza'}</button></div>
            </div>

            {isAdding && (
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-inner animate-slide-in">
                    <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2"><Calendar className="h-5 w-5 text-orange-600"/> {isEditing ? 'Modifica Evento' : 'Nuovo Evento'}</h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Titolo Evento *</label><input required placeholder="Es. Birra post-raduno..." className="border border-slate-300 p-3 rounded-xl w-full focus:ring-2 focus:ring-orange-500 outline-none bg-white font-medium" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Data *</label><div className="relative"><Calendar className="absolute left-3 top-3.5 h-5 w-5 text-slate-400 pointer-events-none"/><input required type="date" className="pl-10 w-full border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none bg-white text-slate-700 font-medium" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} /></div></div>
                            <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Ora *</label><div className="relative"><Clock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400 pointer-events-none"/><input required type="time" className="pl-10 w-full border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none bg-white text-slate-700 font-medium" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} /></div></div>
                            <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Città (Comune) *</label><input required placeholder="Es. Milano" className="border border-slate-300 p-3 rounded-xl w-full focus:ring-2 focus:ring-orange-500 outline-none bg-white font-medium" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                        </div>
                        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Indirizzo Specifico (Opzionale)</label><div className="relative"><MapPin className="absolute left-3 top-3.5 h-5 w-5 text-slate-400"/><input placeholder="Es. Pizzeria Da Mario, Via Roma 10" className="border border-slate-300 p-3 pl-10 rounded-xl w-full focus:ring-2 focus:ring-orange-500 outline-none bg-white font-medium" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div></div>
                        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Descrizione</label><textarea placeholder="Dettagli aggiuntivi..." className="border border-slate-300 p-3 rounded-xl w-full h-24 focus:ring-2 focus:ring-orange-500 outline-none resize-none bg-white" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>

                        <div className="bg-white p-4 rounded-xl border border-slate-200">
                             <label className="text-xs font-bold text-slate-500 uppercase mb-3 block flex items-center gap-2"><Users className="h-4 w-4"/> Chi può partecipare?</label>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Comitato Target</label>
                                    <select className="border border-slate-300 p-2.5 rounded-lg w-full bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none" value={form.target_comitato} onChange={e => setForm({ ...form, target_comitato: e.target.value })}>
                                        {/* LOGICA SMART: SOLO TUTTI O MIO */}
                                        {availableCommittees.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Ruolo Target</label>
                                    <select className="border border-slate-300 p-2.5 rounded-lg w-full bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none" value={form.target_ruolo} onChange={e => setForm({ ...form, target_ruolo: e.target.value })}>
                                        {/* LOGICA SMART: SOLO TUTTI O LIVELLI SOTTO */}
                                        {availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all shadow-md flex items-center justify-center gap-2">{isSubmitting ? <Loader2 className="animate-spin h-5 w-5"/> : <><Megaphone className="h-5 w-5"/> {isEditing ? 'Salva Modifiche' : 'Pubblica Evento'}</>}</button>
                    </form>
                </div>
            )}

            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200">
                 <div className="flex justify-between items-center mb-4 md:mb-0"><div className="flex items-center gap-3 bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-100 w-full md:w-auto"><span className="text-xs font-bold text-orange-700 uppercase flex items-center gap-1 whitespace-nowrap"><MapPin className="h-3 w-3"/> Raggio: {radiusFilter} km</span><input type="range" min="5" max="200" value={radiusFilter} onChange={(e) => setRadiusFilter(Number(e.target.value))} className="rp-range w-full md:w-32"/></div><div className="flex items-center md:hidden cursor-pointer ml-4" onClick={() => setShowFilters(!showFilters)}><Filter className="h-5 w-5 text-slate-500"/></div></div>
                <div className={`mt-4 md:mt-4 flex flex-col md:flex-row gap-3 ${showFilters ? 'block' : 'hidden md:flex'}`}>
                    <div className="flex-1 relative"><Filter className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><input placeholder="Cerca eventi..." className="w-full border border-slate-300 p-2.5 pl-9 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-orange-500 outline-none transition-all text-sm" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} /></div>
                    <select className="border border-slate-300 p-2.5 rounded-lg bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none" value={filterComitato} onChange={e => setFilterComitato(e.target.value)}>{availableCommittees.map(c => <option key={c} value={c}>{c === 'Tutti' ? 'Tutti i Comitati' : c}</option>)}</select>
                    <select className="border border-slate-300 p-2.5 rounded-lg bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none" value={filterRuolo} onChange={e => setFilterRuolo(e.target.value)}><option value="Tutti">Tutti i Ruoli</option>{RUOLI.map(r => <option key={r} value={r}>{r}</option>)}</select>
                </div>
            </div>

            {isMapView ? (<div className="h-[60vh] w-full rounded-2xl overflow-hidden shadow-sm border border-slate-200 relative z-0"><SimpleMap markers={filteredMeetups} center={userLocation} radius={radiusFilter} />{filteredMeetups.length === 0 && !loading && <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-4 py-2 rounded-full shadow-lg text-xs font-bold text-slate-500 z-[1000]">Nessun evento in zona</div>}</div>) : (<>{loading ? (<div className="flex justify-center p-12"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>) : filteredMeetups.length === 0 ? (<div className="text-center py-16 rp-card rounded-2xl flex flex-col items-center"><Megaphone className="h-16 w-16 text-slate-300 mb-4" /><p className="text-slate-500 font-medium text-lg">Nessun evento trovato.</p><p className="text-slate-400 text-sm mt-1">Prova ad aumentare il raggio o cambia filtri.</p>{!isAdding && <button onClick={() => setIsAdding(true)} className="mt-6 text-orange-600 font-bold hover:underline">Organizza ora</button>}</div>) : (<div className="grid grid-cols-1 md:grid-cols-2 gap-6">{filteredMeetups.map(m => { const dateObj = new Date(m.event_date); const isOwner = m.created_by === user.id; const canModify = isOwner || user.is_admin; const amParticipant = m.meetup_participants?.some(p => p.user_id === user.id); return (<div key={m.id} className="rp-card rounded-2xl overflow-hidden hover:shadow-md transition-all group relative flex flex-col"><div className="h-1.5 bg-gradient-to-r from-orange-400 to-orange-600 w-full"></div><div className="p-5 flex-1 flex flex-col"><div className="flex justify-between items-start mb-4"><div className="flex items-center gap-3 cursor-pointer" onClick={() => onOpenProfile(m.organizer)}><Avatar name={m.organizer?.full_name} src={m.organizer?.avatar_url} size="md" className="ring-2 ring-white shadow-sm" /><div><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-1">Organizzato da</p><p className="text-sm font-bold text-slate-800 leading-none hover:text-orange-600 transition-colors">{m.organizer?.full_name}</p></div></div>{canModify && (<div className="flex gap-1"><button onClick={(e) => { e.stopPropagation(); handleEditStart(m); }} className="text-slate-300 hover:text-yellow-500 transition-colors p-1"><Edit className="h-4 w-4" /></button><button onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} className="text-slate-300 hover:text-red-500 transition-colors p-1"><Trash2 className="h-4 w-4" /></button></div>)}</div><h3 className="text-xl font-black text-slate-900 mb-4 leading-tight group-hover:text-orange-700 transition-colors">{m.title}</h3><div className="bg-slate-50 rounded-xl p-4 grid grid-cols-[auto_1fr] gap-4 items-center border border-slate-100 mb-4"><div className="flex flex-col items-center justify-center bg-white p-2 rounded-lg border border-slate-200 shadow-sm min-w-[60px]"><span className="text-lg font-black text-orange-600 leading-none">{dateObj.getDate()}</span><span className="text-[10px] font-bold uppercase text-slate-500 leading-none mt-1">{dateObj.toLocaleString('it-IT', { month: 'short' })}</span></div><div className="space-y-2"><div className="flex items-center gap-2 text-sm font-bold text-slate-700"><Clock className="h-4 w-4 text-blue-500 flex-shrink-0"/>{dateObj.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div><div className="flex items-start gap-2 text-sm text-slate-600 leading-snug"><MapPin className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5"/><span className="font-medium break-words">{m.location}</span></div></div></div><div className="flex flex-wrap gap-2 mb-3">{(m.target_comitato && m.target_comitato !== 'Tutti') && <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-bold border border-blue-100 flex items-center gap-1"><Users className="h-3 w-3"/> {m.target_comitato}</span>}{(m.target_ruolo && m.target_ruolo !== 'Tutti') && <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-bold border border-purple-100 flex items-center gap-1"><Users className="h-3 w-3"/> {m.target_ruolo}</span>}{(!m.target_comitato || m.target_comitato === 'Tutti') && (!m.target_ruolo || m.target_ruolo === 'Tutti') && <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-md text-xs font-bold border border-green-100 flex items-center gap-1">Aperto a Tutti</span>}</div>{m.description && <p className="text-slate-600 text-sm line-clamp-3 leading-relaxed mb-4">{m.description}</p>}{(isOwner || user.is_admin) && (<div className="mt-auto mb-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg"><div className="text-[10px] font-bold text-yellow-800 uppercase mb-2 flex items-center gap-1"><Users className="h-3 w-3"/> Partecipanti ({m.meetup_participants?.length || 0})</div><div className="flex flex-wrap gap-1">{m.meetup_participants?.length > 0 ? (m.meetup_participants.map((p, idx) => (<span key={idx} onClick={(e) => {e.stopPropagation(); onOpenProfile(p.profiles)}} className="bg-white border border-yellow-200 px-2 py-1 rounded text-[10px] font-bold text-slate-700 cursor-pointer hover:bg-yellow-100 transition-colors">{p.profiles?.full_name}</span>))) : (<span className="text-[10px] text-slate-400 italic">Nessuno ancora.</span>)}</div></div>)}<div className="pt-3 border-t border-slate-100 flex justify-between items-center mt-auto gap-2"><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location)}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1"><MapPin className="h-4 w-4"/> Mappa</a><button onClick={() => addToGoogleCalendar(m)} className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1" title="Aggiungi al calendario"><CalendarPlus className="h-4 w-4"/> Calendario</button><button onClick={() => handleJoinToggle(m)} className={`px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-2 shadow-sm ${amParticipant ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-800 text-white hover:bg-slate-900'}`}>{amParticipant ? <><CheckCircle2 className="h-4 w-4"/> Ci sarò</> : "Partecipa"}</button></div></div></div>); })}</div>)}</>)}
        </div>
    );
};


const LikeCount = ({ messageId, userId, trigger }) => {
    const [likes, setLikes] = useState(0);
    const [likedByUser, setLikedByUser] = useState(false);

    useEffect(() => {
        countLikes(messageId).then(setLikes);
        userLikedMessage(messageId, userId).then(setLikedByUser);
    }, [messageId, userId, trigger]);

    return <span className={`${likedByUser ? 'text-orange-500 font-bold' : ''}`}>{likes > 0 ? likes : ''}</span>;
};

// --- GLOBALE / SEGUENTI TOGGLE ---

const ToggleViewMode = ({ viewMode, setViewMode }) => (
    <div className="flex bg-slate-100 p-1 rounded-lg gap-1 mb-4 w-full md:w-auto">
        <button 
            onClick={() => setViewMode('global')} 
            className={`flex-1 px-4 py-1.5 text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-all ${viewMode === 'global' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
        >
            <Users className="h-3 w-3"/> Globale
        </button>
        <button 
            onClick={() => setViewMode('following')} 
            className={`flex-1 px-4 py-1.5 text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-all ${viewMode === 'following' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
        >
            <Activity className="h-3 w-3"/> Seguiti
        </button>
    </div>
);

// --- Notifiche ---
const NotificationDropdown = ({ notifications, onClose, onMarkAllRead, onClear, onNotificationClick }) => {
    return (
        <div className="absolute top-16 right-4 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[1000] animate-fadeIn">
            <div className="bg-slate-50 p-3 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-bold text-slate-800 text-sm">Le tue Notifiche</h3>
                <div className="flex gap-2">
                    <button onClick={onMarkAllRead} className="text-[10px] text-blue-600 font-bold hover:underline">Leggi tutte</button>
                    <button onClick={onClear} className="text-[10px] text-slate-400 hover:text-red-500">Pulisci</button>
                </div>
            </div>
            <div className="max-h-[350px] overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="p-10 text-center text-slate-400 text-xs italic flex flex-col items-center gap-2">
                        <Bell className="h-8 w-8 opacity-20"/>
                        Nessuna nuova notifica.
                    </div>
                ) : (
                    notifications.map(n => (
                        <div 
                            key={n.id} 
                            // ⚠️ CLICK ATTIVO QUI
                            onClick={() => { onNotificationClick(n); onClose(); }}
                            className={`p-3 border-b border-slate-50 hover:bg-blue-50 transition-colors cursor-pointer relative ${!n.read ? 'bg-orange-50/60' : ''}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className={`text-xs ${!n.read ? 'font-bold text-orange-800' : 'font-semibold text-slate-700'}`}>{n.title || 'RefPublic'}</span>
                                <span className="text-[10px] text-slate-400">{n.time}</span>
                            </div>
                            <p className="text-xs text-slate-600 leading-snug line-clamp-2">{n.text}</p>
                            {!n.read && <div className="absolute top-4 right-2 w-2 h-2 bg-orange-500 rounded-full shadow-sm"></div>}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// ==================================================================================
// 📣 COMUNICAZIONI (solo super admin)
// ==================================================================================

// Modelli pronti: il super admin sceglie da qui e all'occorrenza ritocca il
// testo prima di inviare.
const BROADCAST_TEMPLATES = [
    {
        id: 'novita',
        label: 'Novità sull’app',
        icon: Rocket,
        title: 'Novità su RefPublic',
        text: 'Abbiamo aggiornato l’app. Dai un’occhiata a cosa è cambiato.',
        type: 'info'
    },
    {
        id: 'quiz',
        label: 'Nuove domande quiz',
        icon: GraduationCap,
        title: 'Nuove domande disponibili',
        text: 'Sono state caricate nuove domande nel quiz. Mettiti alla prova.',
        type: 'info',
        link_tab: 'quiz'
    },
    {
        id: 'arena',
        label: 'Discussione in Arena',
        icon: MessageSquare,
        title: 'Se ne parla in Arena',
        text: 'C’è una discussione aperta che vale la pena leggere. Passa a dire la tua.',
        type: 'info',
        link_tab: 'forum'
    },
    {
        id: 'terzotempo',
        label: 'Nuovo Terzo Tempo',
        icon: Beer,
        title: 'Nuovo ritrovo in zona',
        text: 'È stato organizzato un nuovo Terzo Tempo. Guarda se ti è comodo.',
        type: 'info',
        link_tab: 'meetups'
    },
    {
        id: 'diario',
        label: 'Promemoria diario',
        icon: NotebookPen,
        title: 'Hai diretto di recente?',
        text: 'Annota la gara nel diario finché hai i dettagli freschi: il recap si costruisce così.',
        type: 'info',
        link_tab: 'diary'
    },
    {
        id: 'manutenzione',
        label: 'Manutenzione',
        icon: AlertTriangle,
        title: 'Manutenzione programmata',
        text: 'RefPublic potrebbe non essere raggiungibile per un breve intervento di manutenzione.',
        type: 'warning'
    },
    {
        id: 'libero',
        label: 'Comunicazione libera',
        icon: Megaphone,
        title: 'Comunicazione',
        text: '',
        type: 'info'
    }
];

// Profili destinatari in base ai filtri scelti.
const fetchBroadcastTargets = async ({ comitato, ruolo }) => {
    if (!supabase) return [];
    let q = supabase.from('profiles').select('id');
    if (comitato && comitato !== 'Tutti') q = q.eq('comitato', comitato);
    if (ruolo && ruolo !== 'Tutti') q = q.eq('role', ruolo);
    const { data, error } = await q;
    if (error) { console.error("fetchBroadcastTargets error:", error); return []; }
    return (data || []).map(p => p.id);
};

// L'insert viene spezzato: una comunicazione a tutti puo' essere migliaia di righe.
const sendBroadcast = async (targetIds, payload) => {
    if (!supabase || targetIds.length === 0) return { error: null, sent: 0 };
    const CHUNK = 400;
    let sent = 0;
    for (let i = 0; i < targetIds.length; i += CHUNK) {
        const rows = targetIds.slice(i, i + CHUNK).map(id => ({
            user_id: id,
            title: payload.title,
            text: payload.text,
            type: payload.type || 'info',
            link_tab: payload.link_tab || null,
            read: false
        }));
        const { error } = await supabase.from('notifications').insert(rows);
        if (error) return { error, sent };
        sent += rows.length;
    }
    return { error: null, sent };
};

const BroadcastModal = ({ user, onClose }) => {
    const committeesList = useCommittees();
    const [templateId, setTemplateId] = useState(null);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [linkTab, setLinkTab] = useState(null);
    const [type, setType] = useState('info');

    const [comitato, setComitato] = useState('Tutti');
    const [ruolo, setRuolo] = useState('Tutti');

    const [targets, setTargets] = useState(null);
    const [counting, setCounting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);

    // Il numero di destinatari si aggiorna con i filtri: si sa sempre a quante
    // persone si sta per scrivere prima di premere invio.
    useEffect(() => {
        let alive = true;
        setCounting(true);
        fetchBroadcastTargets({ comitato, ruolo }).then(ids => {
            if (!alive) return;
            setTargets(ids);
            setCounting(false);
        });
        return () => { alive = false; };
    }, [comitato, ruolo]);

    const pickTemplate = (t) => {
        setTemplateId(t.id);
        setTitle(t.title);
        setText(t.text);
        setLinkTab(t.link_tab || null);
        setType(t.type || 'info');
    };

    const recipients = targets ? targets.filter(id => id !== user.id) : [];
    const canSend = !!templateId && title.trim() && text.trim() && recipients.length > 0;

    const handleSend = async () => {
        setSending(true);
        const { error, sent } = await sendBroadcast(recipients, {
            title: title.trim(), text: text.trim(), type, link_tab: linkTab
        });
        setSending(false);
        setConfirming(false);
        if (error) { alert("Invio non riuscito: " + error.message); return; }
        setDone(sent);
    };

    if (done !== null) {
        return (
            <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center animate-bounce-in">
                    <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <h3 className="font-black text-lg text-slate-800">Comunicazione inviata</h3>
                    <p className="text-sm text-slate-500 mt-1">
                        Recapitata a {done === 1 ? '1 arbitro' : `${done} arbitri`}.
                    </p>
                    <button onClick={onClose} className="mt-5 w-full py-2.5 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors">
                        Chiudi
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">

                <div className="bg-slate-900 p-4 text-white flex justify-between items-center shrink-0">
                    <h3 className="font-bold flex items-center gap-2"><Megaphone className="h-5 w-5" /> Invia comunicazione</h3>
                    <button onClick={onClose} aria-label="Chiudi"><X className="h-5 w-5 hover:text-slate-300" /></button>
                </div>

                <div className="p-5 space-y-5 overflow-y-auto flex-1 custom-scrollbar">

                    {/* 1. Modello */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Modello</label>
                        <div className="grid grid-cols-2 gap-2">
                            {BROADCAST_TEMPLATES.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => pickTemplate(t)}
                                    className={`p-3 rounded-xl border text-left transition-colors flex items-center gap-2.5 ${templateId === t.id ? 'border-orange-500 bg-orange-50 text-orange-900' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                                >
                                    <t.icon className={`h-4 w-4 shrink-0 ${templateId === t.id ? 'text-orange-600' : 'text-slate-400'}`} />
                                    <span className="text-xs font-bold leading-tight">{t.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 2. Testo */}
                    {templateId && (
                        <div className="space-y-3 animate-slide-in">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Titolo</label>
                                <input
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    maxLength={80}
                                    className="w-full border border-slate-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Messaggio</label>
                                <textarea
                                    rows="3"
                                    value={text}
                                    onChange={e => setText(e.target.value)}
                                    maxLength={400}
                                    placeholder="Scrivi il testo della comunicazione..."
                                    className="w-full border border-slate-300 p-2.5 rounded-lg text-sm resize-none focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 text-right">{text.length}/400</p>
                            </div>
                        </div>
                    )}

                    {/* 3. Destinatari */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Destinatari</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Comitato</span>
                                <select value={comitato} onChange={e => setComitato(e.target.value)} className="w-full border border-slate-300 p-2.5 rounded-lg text-sm bg-white">
                                    <option value="Tutti">Tutti i comitati</option>
                                    {committeesList.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Ruolo</span>
                                <select value={ruolo} onChange={e => setRuolo(e.target.value)} className="w-full border border-slate-300 p-2.5 rounded-lg text-sm bg-white">
                                    <option value="Tutti">Tutti i ruoli</option>
                                    {RUOLI.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2 text-sm">
                            {counting ? (
                                <><Loader2 className="h-4 w-4 animate-spin text-slate-400" /><span className="text-slate-500">Conteggio in corso...</span></>
                            ) : (
                                <><Users className="h-4 w-4 text-slate-400" />
                                <span className="text-slate-700">
                                    <strong className="font-black">{recipients.length}</strong> {recipients.length === 1 ? 'arbitro riceverà' : 'arbitri riceveranno'} questa notifica
                                </span></>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2">Tu sei escluso dall&apos;invio.</p>
                    </div>
                </div>

                <div className="p-4 border-t border-slate-100 shrink-0 bg-white">
                    <button
                        onClick={() => setConfirming(true)}
                        disabled={!canSend}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        <Send className="h-4 w-4" /> Invia a {recipients.length} {recipients.length === 1 ? 'arbitro' : 'arbitri'}
                    </button>
                </div>
            </div>

            {/* Conferma: l'invio non è annullabile, quindi si passa di qui */}
            {confirming && (
                <div className="fixed inset-0 bg-black/60 z-[1300] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <h3 className="font-black text-lg text-slate-800 mb-2">Confermi l&apos;invio?</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Stai per inviare <strong>&ldquo;{title}&rdquo;</strong> a <strong>{recipients.length}</strong> {recipients.length === 1 ? 'arbitro' : 'arbitri'}.
                            La notifica non può essere ritirata.
                        </p>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setConfirming(false)} disabled={sending} className="flex-1 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors">
                                Annulla
                            </button>
                            <button onClick={handleSend} disabled={sending} className="flex-1 py-2.5 bg-orange-600 text-white rounded-xl font-bold text-sm hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invia ora'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Dashboard = ({ 
    user, 
    isNewUser, 
    onOpenThread, 
    userLocation, 
    setActiveTab, 
    viewMode, 
    followingList, 
    onOpenQuiz, 
    onOpenChallenges,
    onOpenBroadcast
}) => {
  const [recentThreads, setRecentThreads] = useState([]);
  const [myDesignations, setMyDesignations] = useState([]);
  const [nearbyMeetups, setNearbyMeetups] = useState([]);
  const [joinedMeetups, setJoinedMeetups] = useState([]); 
  const [nearbyRadius, setNearbyRadius] = useState(50); 
  
  const [motd, setMotd] = useState(null);
  const [isEditingMotd, setIsEditingMotd] = useState(false);
  const [newMotdText, setNewMotdText] = useState('');

  // Stato per le sfide in arrivo
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const quizDoneToday = hasPlayedToday(user);

  useEffect(() => {
    // Carica Thread
    fetchRecentThreads().then(data => {
        let visible = data.filter(t => isThreadVisible(t, user));
        if (viewMode === 'following') {
            visible = visible.filter(t => followingList.includes(t.created_by) || t.created_by === user.id);
        }
        setRecentThreads(visible.slice(0, 5)); 
    });

    fetchMyDesignations(user.id).then(setMyDesignations);
    fetchJoinedMeetups(user.id).then(setJoinedMeetups); 

    // Carica Meetup
    const loadNearby = async () => {
        const data = await fetchMeetups();
        if (userLocation && data.length > 0) {
            let visible = data.filter(m => isMeetupVisible(m, user));
            if (viewMode === 'following') {
                visible = visible.filter(m => followingList.includes(m.created_by) || m.created_by === user.id);
            }
            const withDist = visible.map(m => ({
                ...m,
                distance: (m.lat && m.lng) ? calculateDistance(userLocation.lat, userLocation.lng, m.lat, m.lng) : 9999
            })).filter(m => m.distance <= nearbyRadius).sort((a, b) => a.distance - b.distance);
            setNearbyMeetups(withDist.slice(0, 3));
        }
    };
    loadNearby();
    loadMotd();

    // Carica Sfide In Attesa (Inviti)
    if (user.id) {
        fetchMyChallenges(user.id).then(data => {
            // Filtra solo quelle PENDING dove IO sono l'OPPONENT (cioè sono stato sfidato)
            // Nota: La pulizia delle sfide scadute avviene quando si apre la DailyQuizView
            const invites = data.filter(c => c.status === 'pending' && c.opponent_id === user.id);
            setPendingChallenges(invites);
        });
    }
    
  }, [user, userLocation, nearbyRadius, viewMode, followingList]);

  const loadMotd = async () => {
      const data = await fetchActiveMOTD();
      setMotd(data);
      if(data) setNewMotdText(data.message);
  };

  const handleSaveMotd = async () => {
      await saveMOTD(newMotdText, user.id);
      if (!newMotdText || newMotdText.trim() === '') setMotd(null);
      setIsEditingMotd(false);
      loadMotd(); 
  };

  const handleDeleteMotd = async () => {
      if (!window.confirm("Vuoi davvero rimuovere il messaggio globale?")) return;
      await saveMOTD('', user.id);
      setMotd(null);
      setNewMotdText('');
      setIsEditingMotd(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header Utente */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 rounded-3xl p-6 text-white shadow-xl shadow-orange-500/25 ring-1 ring-white/25 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold">{isNewUser ? 'Benvenuto' : 'Bentornato'}, {user.full_name}!</h1>
                {user.is_admin && <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider border border-red-400">ADMIN</span>}
                {user.is_super_admin && <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider border border-yellow-200">SUPER ADMIN</span>}
                
                {/* --- BADGE STREAK & DUELLI --- */}
                <div className="flex gap-2 mt-1 md:mt-0">
                    <StreakBadge profile={user} />
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-0.5 rounded-full border border-white/15" title="Duelli Vinti">
                        <Swords className="h-4 w-4 text-indigo-300"/>
                        <span className="text-xs font-bold text-indigo-100">{user.duels_won || 0}</span>
                    </div>
                </div>
            </div>
            
            <p className="text-slate-300 text-sm">Matricola: <span className="font-mono bg-white/10 px-2 py-0.5 rounded">{user.matricola || "N/A"}</span> • {user.comitato || 'N/D'}</p>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
                <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm border border-orange-400">{user.role || 'Arbitro'}</span>
                {user.anno_inizio && <span className="bg-white/10 text-slate-200 text-xs px-3 py-1 rounded-full border border-white/10 flex items-center gap-1"><Award className="h-3 w-3" /> Arbitro dal {user.anno_inizio}</span>}
            </div>
        </div>
        <Award className="h-16 w-16 text-white/25 hidden md:block"/>
      </div>

      {/* ALERT INVITI SFIDA (Se presenti) */}
      {pendingChallenges.length > 0 && (
        <div className="bg-gradient-to-r from-sky-500 to-indigo-600 rounded-2xl p-4 text-white shadow-lg shadow-sky-500/25 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 animate-bounce-in">
            <div className="flex items-center gap-3 min-w-0">
                <div className="bg-white/20 p-2 rounded-full shrink-0"><Swords className="h-6 w-6 text-yellow-300"/></div>
                <div className="min-w-0">
                    <p className="font-bold text-lg">
                        {pendingChallenges.length === 1 ? 'Hai 1 sfida in attesa!' : `Hai ${pendingChallenges.length} sfide in attesa!`}
                    </p>
                    <p className="text-xs opacity-90">Qualcuno vuole duellare con te al quiz.</p>
                </div>
            </div>
            <button onClick={onOpenChallenges} className="w-full sm:w-auto shrink-0 whitespace-nowrap bg-white text-indigo-600 px-5 py-2 rounded-lg font-bold text-sm shadow-sm hover:bg-indigo-50 transition-colors">
                Vai alle Sfide
            </button>
        </div>
      )}

      {/* CARD QUIZ GENERICA (Sempre visibile) */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-4 text-white shadow-lg shadow-indigo-500/25 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="min-w-0">
                <h3 className="font-bold text-lg flex items-center gap-2"><GraduationCap className="h-5 w-5 text-indigo-200 shrink-0"/> Allenamento Quotidiano</h3>
                <p className="text-sm opacity-90">
                    {quizDoneToday
                        ? "Quiz di oggi completato: la tua streak è al sicuro."
                        : "Mettiti alla prova e mantieni la tua streak attiva!"}
                </p>
            </div>
            <button onClick={onOpenQuiz} className="rp-gloss w-full sm:w-auto shrink-0 whitespace-nowrap px-4 py-2 bg-white text-indigo-700 rounded-lg font-bold text-sm hover:bg-indigo-50 transition-colors">
                {quizDoneToday ? 'Allenati ancora' : 'Inizia Quiz'}
            </button>
      </div>

      {/* Sezione MOTD */}
      {(motd || user.is_super_admin) && (
        <div className={`rounded-2xl border shadow-sm p-4 ${motd ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200 border-dashed'}`}>
            <div className="flex justify-between items-center gap-3">
                <div className="flex gap-3 items-center w-full">
                    <div className={`p-2 rounded-full flex-shrink-0 ${motd ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'}`}><Megaphone className="h-5 w-5" /></div>
                    <div className="flex-1">
                        {!isEditingMotd && motd && <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide mb-1">AVVISO</h3>}
                        {isEditingMotd ? (
                            <div className="mt-2 w-full">
                                <textarea className="w-full p-2 border rounded text-sm mb-2" rows="3" placeholder="Scrivi qui il messaggio globale..." value={newMotdText} onChange={(e) => setNewMotdText(e.target.value)}/>
                                <div className="flex gap-2 flex-wrap">
                                    <button onClick={handleSaveMotd} className="px-3 py-1 bg-green-600 text-white text-xs rounded font-bold hover:bg-green-700">Salva e Pubblica</button>
                                    <button onClick={() => { setIsEditingMotd(false); setNewMotdText(motd ? motd.message : ''); }} className="px-3 py-1 bg-slate-200 text-slate-700 text-xs rounded font-bold hover:bg-slate-300">Annulla</button>
                                    <button onClick={handleDeleteMotd} className="px-3 py-1 bg-red-100 text-red-600 text-xs rounded font-bold hover:bg-red-200 ml-auto">Rimuovi Messaggio</button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-slate-700 text-sm whitespace-pre-wrap">{motd ? motd.message : <span className="italic text-slate-400">Nessun avviso attivo. Premi modifica per crearne uno.</span>}</div>
                        )}
                    </div>
                </div>
                {user.is_super_admin && !isEditingMotd && (
                    <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => setIsEditingMotd(true)} className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-slate-900 flex items-center gap-1 transition-colors"><Edit className="h-3 w-3" /> Gestisci</button>
                        <button onClick={onOpenBroadcast} className="text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-orange-700 flex items-center gap-1 transition-colors" title="Invia una notifica agli arbitri"><Megaphone className="h-3 w-3" /> Notifica</button>
                    </div>
                )}
            </div>
        </div>
      )}

      {/* Griglia Vicini e Adesioni */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rp-card p-5 rounded-2xl">
              <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-sm shadow-orange-500/30"><MapIcon className="h-4 w-4"/></span> Vicini a te</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500 font-bold"><input type="range" min="10" max="200" value={nearbyRadius} onChange={(e) => setNearbyRadius(Number(e.target.value))} className="rp-range w-20"/>{nearbyRadius}km</div>
              </div>
              {nearbyMeetups.length === 0 ? <p className="text-xs text-slate-400 italic">Nessun evento nel raggio selezionato.</p> : (
                  <div className="space-y-2">{nearbyMeetups.map(m => (<div key={m.id} onClick={() => setActiveTab('meetups')} className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 flex justify-between items-center cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors"><div className="truncate flex-1 mr-2"><p className="font-bold text-slate-800 text-xs truncate">{m.title}</p><p className="text-[10px] text-slate-500 truncate">{m.location}</p></div><span className="text-xs font-bold text-blue-600 whitespace-nowrap flex items-center gap-1"><NavIcon className="h-3 w-3"/> {m.distance.toFixed(0)} km</span></div>))}</div>
              )}
          </div>

          <div className="rp-card p-5 rounded-2xl">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3"><span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-sm shadow-emerald-500/30"><CheckCircle2 className="h-4 w-4"/></span> Le tue Adesioni</h3>
              {joinedMeetups.length === 0 ? <p className="text-xs text-slate-400 italic">Non hai ancora aderito a nessun evento.</p> : (
                  <div className="space-y-2">{joinedMeetups.map(m => (<div key={m.id} onClick={() => setActiveTab('meetups')} className="bg-green-50 p-2.5 rounded-lg border border-green-100 cursor-pointer hover:bg-green-100 transition-colors"><div className="flex justify-between"><p className="font-bold text-green-900 text-xs">{m.title}</p><p className="text-[10px] font-bold text-green-700">{new Date(m.event_date).toLocaleDateString()}</p></div><p className="text-[10px] text-green-600 truncate">{m.location}</p></div>))}</div>
              )}
          </div>
      </div>

      {/* Griglia Arena e Gare */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-800 flex items-center gap-2"><span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 text-white shadow-sm shadow-blue-500/30"><MessageSquare className="h-4 w-4"/></span> Ultime dall'Arena</h3><button onClick={() => onOpenThread(null)} className="text-xs text-blue-600 hover:underline font-medium">Vedi tutte</button></div>
            <div className="space-y-2">{recentThreads.length === 0 && <p className="text-slate-400 text-sm italic">Nessuna discussione recente.</p>}{recentThreads.map(thread => (<div key={thread.id} onClick={() => onOpenThread(thread.id)} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-blue-50 cursor-pointer transition-colors group"><div><p className="font-bold text-slate-700 text-sm group-hover:text-blue-700">{thread.titolo}</p><p className="text-xs text-slate-400 mt-0.5">di {thread.author?.full_name || 'Utente'}</p></div><ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-blue-500" /></div>))}</div>
        </div>
        
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-800 flex items-center gap-2"><span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-sm shadow-amber-500/30"><Calendar className="h-4 w-4"/></span> Le tue prossime gare</h3></div>
            <div className="space-y-2">
                 {myDesignations.length === 0 && (<div className="flex flex-col items-center justify-center h-40 text-slate-400"><Calendar className="h-8 w-8 mb-2 opacity-30"/><p className="text-sm">Nessuna designazione inserita</p></div>)}
                 {myDesignations.map(match => (<div key={match.id} className="p-3 rounded-lg bg-slate-50 border-l-4 border-orange-400"><p className="font-bold text-slate-800 text-sm">{match.home_team} vs {match.away_team}</p><div className="flex gap-3 mt-1 text-xs text-slate-500"><span className="flex items-center gap-1"><Calendar className="h-3 w-3"/> {new Date(match.match_date).toLocaleDateString()}</span><span className="flex items-center gap-1"><Clock className="h-3 w-3"/> {new Date(match.match_date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span></div><div className="flex items-center justify-between mt-1"><span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-600">{match.category}</span><p className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="h-3 w-3"/> {match.location}</p></div></div>))}
            </div>
        </div>
      </div>
    </div>
  );
};

const DesignationsManager = ({ user, onOpenProfile, onOpenSearchModal, initialSearch, followingList, viewMode }) => {
    const committeesList = useCommittees();
    const [designations, setDesignations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(null); 
    
    const [filterLevel, setFilterLevel] = useState(''); 
    const [filterComitato, setFilterComitato] = useState(''); 
    const [filterSearch, setFilterSearch] = useState(initialSearch || ''); 
    const [filterDate, setFilterDate] = useState('');
    const [filterDoubleRef, setFilterDoubleRef] = useState(false);
    
    const [newMatch, setNewMatch] = useState({
        home_team: '', away_team: '', 
        level: 'Regionale', gender: 'Maschile', series: '',
        match_date: '', match_time: '', 
        city: '', address: '',
        my_role: '1° Arbitro', is_friendly: false,
        is_private: false, // <--- NUOVO CAMPO

        partner: null, partner_role: '2° Arbitro', 
        second_referee: null, 
        scorekeeper: null,
        videocheck: null,
        
        selectingFor: null,
        original_user_id: null, // Per preservare creatore
        original_invites: []
    });

    const ROLES_HIGH = ["Arbitro Nazionale", "Arbitro Regionale"];
    const ROLES_NAT = ["Arbitro Nazionale"];

    const getAvailableTargetCommittees = () => {
        if (user.is_super_admin) return ["Tutti", ...committeesList];
        return user.comitato ? ["Tutti", user.comitato] : ["Tutti", ...committeesList];
    };

    const getAvailableTargetRoles = () => {
        if (user.is_super_admin) return ["Tutti", ...RUOLI];
        const myRoleIdx = RUOLI.indexOf(user.role);
        if (myRoleIdx === -1) return ["Tutti", ...RUOLI];
        return ["Tutti", ...RUOLI.slice(myRoleIdx)];
    };

    useEffect(() => { if (initialSearch) setFilterSearch(initialSearch); }, [initialSearch]);
    useEffect(() => { loadDesignations(); }, [followingList, viewMode]);

    const loadDesignations = async () => {
        setLoading(true);
        const data = await fetchDesignations();
        setLoading(false);
        setDesignations(data);
    };

    const openSearchFor = (roleType) => {
        setNewMatch(prev => ({ ...prev, selectingFor: roleType }));
        onOpenSearchModal(handleUserSelect);
    };

    const handleUserSelect = (selectedUser) => {
        if (selectedUser.id === user.id) { alert("Non puoi invitare te stesso."); return; }
        const type = newMatch.selectingFor;
        if (!newMatch.is_friendly) {
            if (newMatch.level === 'Nazionale') {
                if (type === 'partner' || type === 'second_referee') { if (!ROLES_NAT.includes(selectedUser.role)) { alert("Richiesto Arbitro Nazionale."); return; } }
                if (type === 'scorekeeper') { if (!ROLES_HIGH.includes(selectedUser.role)) { alert("Richiesto almeno Regionale."); return; } }
                if (type === 'videocheck') { if (!ROLES_NAT.includes(selectedUser.role)) { alert("Richiesto Arbitro Nazionale."); return; } }
            }
        }
        if (type === 'partner') setNewMatch(prev => ({ ...prev, partner: selectedUser }));
        else if (type === 'second_referee') setNewMatch(prev => ({ ...prev, second_referee: selectedUser }));
        else if (type === 'scorekeeper') setNewMatch(prev => ({ ...prev, scorekeeper: selectedUser }));
        else if (type === 'videocheck') setNewMatch(prev => ({ ...prev, videocheck: selectedUser }));
    };

    const handleAddOrUpdate = async (e) => {
        e.preventDefault();
        try {
            const dateTimeString = `${newMatch.match_date}T${newMatch.match_time}:00`;
            const categoryString = `${newMatch.series} ${newMatch.gender} (${newMatch.level})`;
            const fullLocation = `${newMatch.address}, ${newMatch.city}`;
            let partnerRole = '2° Arbitro';
            if (newMatch.my_role === '2° Arbitro') partnerRole = '1° Arbitro';
            if (newMatch.my_role === 'Segnapunti' || newMatch.my_role === 'Addetto al VideoCheck') partnerRole = '1° Arbitro';

            // In modifica notifichiamo solo i nuovi invitati, non chi era già
            // sulla gara: altrimenti ogni salvataggio avrebbe rinotificato tutti.
            const alreadyInvited = newMatch.original_invites || [];

            const payload = {
                // FIX: Se modifico, uso l'ID originale, altrimenti il mio
                user_id: isEditing ? newMatch.original_user_id : user.id,
                home_team: newMatch.home_team.trim(),
                away_team: newMatch.away_team.trim(),
                category: categoryString, 
                level: newMatch.level,
                gender: newMatch.gender, 
                series: newMatch.series.trim(),
                location: fullLocation, 
                match_date: new Date(dateTimeString).toISOString(),
                my_role: newMatch.my_role,
                is_friendly: newMatch.is_friendly,
                is_private: newMatch.is_private, 

                partner_id: newMatch.partner?.id || null,
                partner_role: partnerRole,
                partner_status: newMatch.partner ? 'pending' : 'none',
                second_referee_id: newMatch.second_referee?.id || null,
                second_referee_status: newMatch.second_referee ? 'pending' : 'none',
                scorekeeper_id: newMatch.scorekeeper?.id || null,
                scorekeeper_status: newMatch.scorekeeper ? 'pending' : 'none',
                videocheck_id: newMatch.videocheck?.id || null,
                videocheck_status: newMatch.videocheck ? 'pending' : 'none'
            };

            let error;
            if (isEditing) error = await updateDesignation(isEditing, payload);
            else error = await createDesignation(payload);
            
            if (!error) {
                // Chi viene aggiunto come 2° arbitro, segnapunti o addetto al
                // videocheck non riceveva nulla: l'invito compariva in elenco
                // solo se apriva la sezione Gare per caso.
                const invited = [
                    { id: payload.partner_id, roleType: 'partner', label: payload.partner_role || 'collega di gara' },
                    { id: payload.second_referee_id, roleType: 'second_referee', label: '2° Arbitro' },
                    { id: payload.scorekeeper_id, roleType: 'scorekeeper', label: 'Segnapunti' },
                    { id: payload.videocheck_id, roleType: 'videocheck', label: 'Addetto al VideoCheck' }
                ].filter(i => i.id && i.id !== user.id && !alreadyInvited.includes(i.id));

                const matchLabel = `${payload.home_team} - ${payload.away_team}`;
                const matchWhen = new Date(payload.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long' });
                await pushNotifications(invited.map(i => ({
                    user_id: i.id,
                    type: 'designation',
                    title: 'Ti hanno aggiunto a una gara',
                    text: `${user.full_name} ti ha indicato come ${i.label} per ${matchLabel} del ${matchWhen}.`,
                    link_tab: 'designations'
                })));

                setIsAdding(false); setIsEditing(null);
                setNewMatch({ 
                    home_team: '', away_team: '', level: 'Regionale', gender: 'Maschile', series: '', 
                    match_date: '', match_time: '', city: '', address: '', is_friendly: false, is_private: false,
                    my_role: '1° Arbitro', partner: null, second_referee: null, scorekeeper: null, videocheck: null, original_user_id: null, original_invites: [] 
                });
                loadDesignations(); 
                alert("Gara salvata!");
            } else { alert("Errore: " + error.message); }
        } catch (err) { console.error(err); alert("Errore imprevisto."); }
    };

    const handleEditStart = (d) => {
        const dateObj = new Date(d.match_date);
        let existingCity = ''; let existingAddress = d.location || '';
        if (d.location && d.location.includes(',')) { const parts = d.location.split(','); existingCity = parts.pop().trim(); existingAddress = parts.join(',').trim(); }
        
        setNewMatch({
            home_team: d.home_team, away_team: d.away_team, level: d.level, gender: d.gender, series: d.series,
            city: existingCity, address: existingAddress,
            match_date: dateObj.toISOString().split('T')[0],
            match_time: dateObj.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            my_role: d.my_role || '1° Arbitro',
            is_friendly: d.is_friendly || false,
            is_private: d.is_private || false,
            partner: d.partner || null, second_referee: d.second_referee || null,
            scorekeeper: d.scorekeeper || null, videocheck: d.videocheck || null,
            original_user_id: d.user_id, // SALVO ID ORIGINALE
            // Chi era già invitato: serve a non rinotificarlo a ogni salvataggio.
            original_invites: [d.partner_id, d.second_referee_id, d.scorekeeper_id, d.videocheck_id].filter(Boolean)
        });
        setIsEditing(d.id); setIsAdding(true);
    };

    const handleInviteResponse = async (designationId, roleType, status) => { await respondToInvite(designationId, roleType, status); loadDesignations(); };
    const handleDelete = async (id) => { if(window.confirm("Eliminare?")) { setDesignations(prev => prev.filter(d => d.id !== id)); await deleteDesignation(id); } };

    const myPendingInvites = designations.filter(d => 
        (d.partner_id === user.id && d.partner_status === 'pending') ||
        (d.second_referee_id === user.id && d.second_referee_status === 'pending') ||
        (d.scorekeeper_id === user.id && d.scorekeeper_status === 'pending') ||
        (d.videocheck_id === user.id && d.videocheck_status === 'pending')
    );

    const filteredDesignations = designations.filter(d => {
        const isMine = d.user_id === user.id;
        
        // Se sono coinvolto (invitato E accettato)
        const isPartnerAccepted = 
            (d.partner_id === user.id && d.partner_status === 'accepted') ||
            (d.second_referee_id === user.id && d.second_referee_status === 'accepted') ||
            (d.scorekeeper_id === user.id && d.scorekeeper_status === 'accepted') ||
            (d.videocheck_id === user.id && d.videocheck_status === 'accepted');

        // LOGICA PRIVACY
        // Se privata: Visibile solo a Me, Admin o Chi ha accettato
        if (d.is_private) {
            if (!isMine && !user.is_admin && !isPartnerAccepted) return false;
        }

        if (viewMode === 'following') { if (!isMine && !isPartnerAccepted && !followingList.includes(d.user_id)) return false; }
        if (filterDoubleRef && !d.partner_id) return false;
        
        const matchDate = new Date(d.match_date).toISOString().split('T')[0];
        const matchesSearch = !filterSearch || JSON.stringify(d).toLowerCase().includes(filterSearch.toLowerCase());

        return (!filterLevel || d.level === filterLevel) &&
               (!(filterLevel === 'Territoriale' && filterComitato) || d.profile?.comitato === filterComitato) &&
               (!filterDate || matchDate === filterDate) &&
               matchesSearch;
    });

    // ... (Resto dei ruoli e return identico a prima) ...
    const isAuxiliary = newMatch.my_role === 'Segnapunti' || newMatch.my_role === 'Addetto al VideoCheck';
    const showScorekeeperSlot = (newMatch.level === 'Nazionale' || newMatch.is_friendly) && newMatch.my_role !== 'Segnapunti';
    const showVideoCheckSlot = (newMatch.level === 'Nazionale' || newMatch.is_friendly) && newMatch.my_role !== 'Addetto al VideoCheck';

    const availableLevels = getAvailableTargetRoles().length > 0 ? LIVELLI_GARA : LIVELLI_GARA; 
    const availableMyRoles = getAvailableTargetRoles(); 
    const getMyRolesForCreation = () => {
        const baseRoles = ['1° Arbitro', '2° Arbitro'];
        if (newMatch.is_friendly) return [...baseRoles, 'Segnapunti', 'Addetto al VideoCheck'];
        if (newMatch.level === 'Nazionale') {
            if (ROLES_NAT.includes(user.role)) return [...baseRoles, 'Segnapunti', 'Addetto al VideoCheck'];
            if (user.role === 'Arbitro Regionale') return ['Segnapunti'];
            return [];
        }
        return baseRoles; 
    };
    const myCreationRoles = getMyRolesForCreation();

    return (
        // ... (Stesso JSX di prima, con checkbox privacy già incluso) ...
        <div className="flex flex-col gap-4 h-[calc(100vh-140px)]">
            <button onClick={() => setShowMobileFilters(!showMobileFilters)} className="md:hidden w-full bg-slate-800 text-white py-2 px-4 rounded-lg font-bold flex items-center justify-between shadow-md shrink-0"><span className="flex items-center gap-2"><Filter className="h-4 w-4"/> Filtri & Opzioni</span>{showMobileFilters ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}</button>

            <div className="flex flex-col md:flex-row gap-6 flex-1 overflow-hidden">
                <div className={`w-full md:w-1/3 lg:w-1/4 space-y-4 overflow-y-auto pb-4 ${showMobileFilters ? 'block' : 'hidden md:block'}`}>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                        <button onClick={() => { setIsAdding(!isAdding); setIsEditing(null); }} className="w-full py-2 bg-orange-600 text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-orange-700 transition-colors">{isAdding ? <X className="h-4 w-4"/> : <CalendarPlus className="h-4 w-4"/>} {isAdding ? 'Chiudi' : 'Nuova Designazione'}</button>
                        
                        {isAdding && (
                            <form onSubmit={handleAddOrUpdate} className="mt-4 space-y-3 animate-fadeIn">
                                <h4 className="text-xs font-bold text-orange-700 uppercase border-b pb-1 mb-2">{isEditing ? 'Modifica Gara' : 'Inserisci Gara'}</h4>
                                
                                <div className="flex flex-col gap-2 mb-2 bg-slate-50 p-2 rounded border border-slate-200">
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="isFriendly" className="w-4 h-4 accent-orange-600" checked={newMatch.is_friendly} onChange={e => setNewMatch({...newMatch, is_friendly: e.target.checked})} />
                                        <label htmlFor="isFriendly" className="text-xs font-bold text-slate-700">Gara Amichevole</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="isPrivate" className="w-4 h-4 accent-orange-600" checked={newMatch.is_private} onChange={e => setNewMatch({...newMatch, is_private: e.target.checked})} />
                                        <label htmlFor="isPrivate" className="text-xs font-bold text-slate-700 flex items-center gap-1"><EyeOff className="h-3 w-3"/> Visibile solo a me (e invitati)</label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <select className="border p-2 rounded text-sm w-full bg-slate-50" value={newMatch.level} onChange={e => setNewMatch({...newMatch, level: e.target.value, my_role: '1° Arbitro', scorekeeper: null, videocheck: null, partner: null, second_referee: null})}>{availableLevels.map(l => <option key={l} value={l}>{l}</option>)}</select>
                                    <select className="border p-2 rounded text-sm w-full bg-slate-50" value={newMatch.gender} onChange={e=>setNewMatch({...newMatch, gender: e.target.value})}>{GENERI_GARA.map(g => <option key={g} value={g}>{g}</option>)}</select>
                                </div>

                                <input required placeholder="Es. Serie C" className="border p-2 rounded text-sm w-full" value={newMatch.series} onChange={e=>setNewMatch({...newMatch, series: e.target.value})} />
                                <div className="grid grid-cols-2 gap-2"><input required placeholder="Casa" className="border p-2 rounded text-sm w-full" value={newMatch.home_team} onChange={e=>setNewMatch({...newMatch, home_team: e.target.value})} /><input required placeholder="Ospite" className="border p-2 rounded text-sm w-full" value={newMatch.away_team} onChange={e=>setNewMatch({...newMatch, away_team: e.target.value})} /></div>
                               <div className="grid grid-cols-2 gap-2">
                                    <div className="relative"><Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none"/><input required type="date" className="pl-9 border p-2 rounded text-sm w-full outline-none focus:ring-1 focus:ring-orange-500" value={newMatch.match_date} onChange={e => setNewMatch({...newMatch, match_date: e.target.value})} /></div>
                                    <div className="relative"><Clock className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none"/><input required type="time" className="pl-9 border p-2 rounded text-sm w-full outline-none focus:ring-1 focus:ring-orange-500" value={newMatch.match_time} onChange={e => setNewMatch({...newMatch, match_time: e.target.value})} /></div>
                                </div>
                                <div className="grid grid-cols-1 gap-2"><input required placeholder="Città" className="border p-2 rounded text-sm w-full" value={newMatch.city} onChange={e=>setNewMatch({...newMatch, city: e.target.value})} /><input required placeholder="Indirizzo" className="border p-2 rounded text-sm w-full" value={newMatch.address} onChange={e=>setNewMatch({...newMatch, address: e.target.value})} /></div>

                                <div className="bg-orange-50 p-2 rounded border border-orange-100 mt-2 space-y-2">
                                    {myCreationRoles.length > 0 ? (<><label className="text-[10px] font-bold text-orange-800 uppercase mb-1 block">Il tuo ruolo</label><div className="flex flex-wrap gap-2 mb-2">{myCreationRoles.map(r => (<button key={r} type="button" onClick={() => setNewMatch({...newMatch, my_role: r})} className={`px-2 py-1 text-[10px] rounded border ${newMatch.my_role === r ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-600 border-slate-300'}`}>{r}</button>))}</div></>) : (<p className="text-xs text-red-500 mb-2">Ruolo non disponibile.</p>)}
                                    
                                    <div className="mb-2"><label className="text-[10px] font-bold text-orange-800 uppercase mb-1 block">{isAuxiliary ? '1° Arbitro' : (newMatch.my_role === '1° Arbitro' ? '2° Arbitro' : '1° Arbitro')}</label>{newMatch.partner ? (<div className="flex items-center justify-between bg-white p-1.5 rounded border border-orange-200"><span className="text-xs font-bold truncate ml-1">{newMatch.partner.full_name}</span><button type="button" onClick={() => setNewMatch({...newMatch, partner: null})}><X className="h-4 w-4 text-red-500"/></button></div>) : (<button type="button" onClick={() => openSearchFor('partner')} className="w-full py-1 bg-white border border-dashed border-slate-400 text-slate-500 text-xs rounded flex items-center justify-center gap-1 hover:bg-slate-50"><UserPlus className="h-3 w-3"/> Cerca</button>)}</div>
                                    {isAuxiliary && (<div className="mb-2"><label className="text-[10px] font-bold text-orange-800 uppercase mb-1 block">2° Arbitro</label>{newMatch.second_referee ? (<div className="flex items-center justify-between bg-white p-1.5 rounded border border-orange-200"><span className="text-xs font-bold truncate ml-1">{newMatch.second_referee.full_name}</span><button type="button" onClick={() => setNewMatch({...newMatch, second_referee: null})}><X className="h-4 w-4 text-red-500"/></button></div>) : (<button type="button" onClick={() => openSearchFor('second_referee')} className="w-full py-1 bg-white border border-dashed border-slate-400 text-slate-500 text-xs rounded flex items-center justify-center gap-1 hover:bg-slate-50"><UserPlus className="h-3 w-3"/> Cerca</button>)}</div>)}
                                    {showScorekeeperSlot && (<div className="mb-2"><label className="text-[10px] font-bold text-blue-800 uppercase mb-1 block">Segnapunti</label>{newMatch.scorekeeper ? (<div className="flex items-center justify-between bg-white p-1.5 rounded border border-blue-200"><span className="text-xs font-bold truncate ml-1">{newMatch.scorekeeper.full_name}</span><button type="button" onClick={() => setNewMatch({...newMatch, scorekeeper: null})}><X className="h-4 w-4 text-red-500"/></button></div>) : (<button type="button" onClick={() => openSearchFor('scorekeeper')} className="w-full py-1 bg-white border border-dashed border-blue-400 text-blue-500 text-xs rounded flex items-center justify-center gap-1 hover:bg-blue-50"><UserPlus className="h-3 w-3"/> Cerca</button>)}</div>)}
                                    {showVideoCheckSlot && (<div><label className="text-[10px] font-bold text-purple-800 uppercase mb-1 block">VideoCheck</label>{newMatch.videocheck ? (<div className="flex items-center justify-between bg-white p-1.5 rounded border border-purple-200"><span className="text-xs font-bold truncate ml-1">{newMatch.videocheck.full_name}</span><button type="button" onClick={() => setNewMatch({...newMatch, videocheck: null})}><X className="h-4 w-4 text-red-500"/></button></div>) : (<button type="button" onClick={() => openSearchFor('videocheck')} className="w-full py-1 bg-white border border-dashed border-purple-400 text-purple-500 text-xs rounded flex items-center justify-center gap-1 hover:bg-purple-50"><UserPlus className="h-3 w-3"/> Cerca</button>)}</div>)}
                                </div>
                                <button type="submit" className="w-full bg-slate-800 text-white py-2 rounded text-sm font-medium hover:bg-slate-900">{isEditing ? 'Aggiorna' : 'Pubblica'}</button>
                            </form>
                        )}
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 space-y-3">
                        <div className="flex justify-between items-center"><h3 className="font-bold text-slate-700 flex items-center gap-2"><Filter className="h-4 w-4"/> Filtri</h3>{(filterComitato || filterLevel || filterDate || filterSearch || filterDoubleRef) && (<button onClick={() => {setFilterComitato(''); setFilterLevel(''); setFilterDate(''); setFilterSearch(''); setFilterDoubleRef(false);}} className="text-[10px] text-red-500 hover:underline">Reset</button>)}</div>
                        <input placeholder="Cerca..." className="w-full border p-2 rounded text-sm bg-slate-50" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
                        <div><label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Livello</label><select className="w-full border p-2 rounded text-sm bg-white" value={filterLevel} onChange={e=>setFilterLevel(e.target.value)}><option value="">Tutti</option>{LIVELLI_GARA.map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                        {filterLevel === 'Territoriale' && (<div><label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Comitato</label><select className="w-full border p-2 rounded text-sm bg-white" value={filterComitato} onChange={e=>setFilterComitato(e.target.value)}><option value="">Tutti i Comitati</option>{getAvailableTargetCommittees().map(c => <option key={c} value={c}>{c}</option>)}</select></div>)}
                        <div><label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Data</label><input type="date" className="w-full border p-2 rounded text-sm bg-white" value={filterDate} onChange={e => setFilterDate(e.target.value)} /></div>
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100"><input type="checkbox" id="doubleRef" className="w-4 h-4 accent-orange-600" checked={filterDoubleRef} onChange={e => setFilterDoubleRef(e.target.checked)} /><label htmlFor="doubleRef" className="text-xs font-bold text-slate-600">Solo Gare con 2 Arbitri</label></div>
                    </div>
                </div>

                <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 overflow-hidden flex flex-col">
                    <div className="p-4 bg-white border-b border-slate-200 flex justify-between items-center"><h2 className="font-bold text-lg flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-600"/> Calendario Gare</h2><span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">{filteredDesignations.length}</span></div>
                    
                    <div className="overflow-y-auto p-4 space-y-3 flex-1">
                        {myPendingInvites.length > 0 && (
                            <div className="mb-4 space-y-2">
                                <div className="text-xs font-bold text-orange-600 uppercase tracking-wider flex items-center gap-1"><Bell className="h-3 w-3"/> Inviti in attesa</div>
                                {myPendingInvites.map(invite => {
                                    let roleOffered = ''; let roleType = '';
                                    if (invite.partner_id === user.id) { roleOffered = invite.my_role === '1° Arbitro' ? '2° Arbitro' : '1° Arbitro'; roleType = 'partner'; }
                                    else if (invite.second_referee_id === user.id) { roleOffered = '2° Arbitro'; roleType = 'second_referee'; }
                                    else if (invite.scorekeeper_id === user.id) { roleOffered = 'Segnapunti'; roleType = 'scorekeeper'; }
                                    else if (invite.videocheck_id === user.id) { roleOffered = 'VideoCheck'; roleType = 'videocheck'; }
                                    return (
                                        <div key={invite.id} className="bg-white border-l-4 border-orange-500 rounded-lg p-3 shadow-sm animate-pulse-slow">
                                            <div className="flex justify-between items-start">
                                                <div><p className="text-sm font-bold text-slate-800">{invite.profile?.full_name} ti ha invitato</p><p className="text-xs text-slate-600 mt-1">{new Date(invite.match_date).toLocaleDateString()} • {invite.home_team} vs {invite.away_team}</p><p className="text-[10px] text-slate-500 mt-1">Ruolo proposto: <strong>{roleOffered}</strong></p></div>
                                                <div className="flex gap-2"><button onClick={() => handleInviteResponse(invite.id, roleType, 'accepted')} className="bg-green-100 text-green-700 p-1.5 rounded hover:bg-green-200 font-bold text-xs">Accetta</button><button onClick={() => handleInviteResponse(invite.id, roleType, 'rejected')} className="bg-red-100 text-red-700 p-1.5 rounded hover:bg-red-200 font-bold text-xs">Rifiuta</button></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {loading && <div className="flex justify-center p-10"><Loader2 className="animate-spin text-slate-400"/></div>}
                        {!loading && filteredDesignations.length === 0 && myPendingInvites.length === 0 && (<div className="flex flex-col items-center justify-center h-full text-slate-400"><Calendar className="h-12 w-12 mb-2 opacity-20"/><p>Nessuna gara trovata.</p></div>)}
                        
                        {filteredDesignations.map(d => {
                            const canModify = d.user_id === user.id || user.is_admin;
                            const isPartner = d.partner_id === user.id || d.second_referee_id === user.id || d.scorekeeper_id === user.id || d.videocheck_id === user.id;
                            return (
                                <div key={d.id} className={`bg-white p-4 rounded-lg shadow-sm border ${isPartner ? 'border-l-4 border-l-blue-500' : 'border-slate-200'} flex flex-col sm:flex-row gap-4 items-start sm:items-center group hover:border-blue-400 transition-colors`}>
                                    <div className="flex flex-col items-center justify-center bg-slate-100 p-2 rounded-lg min-w-[70px] border border-slate-200 w-full sm:w-auto"><span className="text-xl font-black text-slate-700">{new Date(d.match_date).getDate()}</span><span className="text-[10px] font-bold uppercase text-slate-500">{new Date(d.match_date).toLocaleString('it-IT', { month: 'short' })}</span><span className="text-xs font-mono font-bold text-blue-600 mt-1">{new Date(d.match_date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span></div>
                                    <div className="flex-1 min-w-0 w-full">
                                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                            {d.is_friendly && <span className="text-[9px] bg-yellow-100 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded font-bold uppercase">Amichevole</span>}
                                            {d.is_private && <span className="text-[9px] bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded font-bold uppercase flex items-center gap-1"><EyeOff className="h-3 w-3"/> Privata</span>}
                                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${d.level === 'Nazionale' ? 'bg-red-100 text-red-700 border border-red-200' : d.level === 'Regionale' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-orange-100 text-orange-700 border border-orange-200'}`}>{d.level}</span>
                                            {d.partner_status === 'accepted' && <span className="text-[9px] bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded font-bold">Doppia</span>}
                                        </div>
                                        <h3 className="font-bold text-slate-800 text-lg leading-tight mb-2 flex flex-wrap items-center gap-1">{d.home_team} <span className="text-slate-300 font-light text-sm">vs</span> {d.away_team}</h3>
                                        <div className="flex flex-wrap items-center gap-4 w-full mt-2">
                                            <div className="inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 pr-2 rounded-full transition-colors -ml-1" onClick={() => onOpenProfile({ ...d.profile, id: d.user_id })}><Avatar name={d.profile?.full_name} src={d.profile?.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><div className="flex flex-col"><span className="text-xs font-bold text-slate-700 leading-none">{d.profile?.full_name}</span><span className="text-[9px] text-slate-400 leading-none">{d.my_role || '1° Arbitro'}</span></div></div>
                                            {d.partner_id && d.partner_status === 'accepted' && (<div className="inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 pr-2 rounded-full transition-colors" onClick={() => onOpenProfile({ ...d.partner, id: d.partner_id })}><Avatar name={d.partner?.full_name} src={d.partner?.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><div className="flex flex-col"><span className="text-xs font-bold text-slate-700 leading-none">{d.partner?.full_name}</span><span className="text-[9px] text-slate-400 leading-none">{d.my_role === '1° Arbitro' ? '2° Arbitro' : '1° Arbitro'}</span></div></div>)}
                                            {d.second_referee_id && d.second_referee_status === 'accepted' && (<div className="inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 pr-2 rounded-full transition-colors" onClick={() => onOpenProfile({ ...d.second_referee, id: d.second_referee_id })}><Avatar name={d.second_referee?.full_name} src={d.second_referee?.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><div className="flex flex-col"><span className="text-xs font-bold text-slate-700 leading-none">{d.second_referee?.full_name}</span><span className="text-[9px] text-slate-400 leading-none">2° Arbitro</span></div></div>)}
                                            {d.scorekeeper_id && d.scorekeeper_status === 'accepted' && (<div className="inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 pr-2 rounded-full transition-colors" onClick={() => onOpenProfile({ ...d.scorekeeper, id: d.scorekeeper_id })}><Avatar name={d.scorekeeper?.full_name} src={d.scorekeeper?.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><div className="flex flex-col"><span className="text-xs font-bold text-slate-700 leading-none">{d.scorekeeper?.full_name}</span><span className="text-[9px] text-slate-400 leading-none">Segnapunti</span></div></div>)}
                                            {d.videocheck_id && d.videocheck_status === 'accepted' && (<div className="inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 pr-2 rounded-full transition-colors" onClick={() => onOpenProfile({ ...d.videocheck, id: d.videocheck_id })}><Avatar name={d.videocheck?.full_name} src={d.videocheck?.avatar_url} size="sm" className="w-6 h-6 text-[10px]"/><div className="flex flex-col"><span className="text-xs font-bold text-slate-700 leading-none">{d.videocheck?.full_name}</span><span className="text-[9px] text-slate-400 leading-none">VideoCheck</span></div></div>)}
                                            {canModify && (d.partner_status === 'pending' || d.second_referee_status === 'pending' || d.scorekeeper_status === 'pending' || d.videocheck_status === 'pending') && (<span className="text-[10px] text-orange-500 italic flex items-center gap-1"><Clock className="h-3 w-3"/> Inviti in attesa</span>)}
                                        </div>
                                        <div className="mt-2 text-xs text-slate-400 flex items-center gap-1 truncate max-w-full"><MapPin className="h-3 w-3 flex-shrink-0"/> {d.location}</div>
                                    </div>
                                    <div className="flex flex-row sm:flex-col gap-2 border-t sm:border-t-0 sm:border-l border-slate-100 pt-3 sm:pt-0 sm:pl-3 w-full sm:w-auto justify-end">
                                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.location)}`} target="_blank" rel="noreferrer" className="flex-1 sm:flex-none p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex justify-center" title="Vedi su Mappa"><MapPin className="h-5 w-5"/></a>
                                        <button onClick={() => addToGoogleCalendar(d)} className="flex-1 sm:flex-none p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors flex justify-center" title="Aggiungi al Calendario"><CalendarPlus className="h-5 w-5"/></button>
                                        {canModify && (<><button onClick={() => handleEditStart(d)} className="flex-1 sm:flex-none p-2 text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded transition-colors flex justify-center" title="Modifica"><Edit className="h-5 w-5"/></button><button onClick={() => handleDelete(d.id)} className="flex-1 sm:flex-none p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors flex justify-center" title="Elimina"><Trash2 className="h-5 w-5"/></button></>)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================================================================================
// ✍️ TESTO FORMATTATO E MEDIA (Arena)
// ==================================================================================
//
// Il testo dei messaggi resta testo: la formattazione e' una sintassi leggera
// interpretata al momento del rendering e trasformata in elementi React.
// Non si usa mai dangerouslySetInnerHTML: i messaggi sono scritti da altri
// utenti, e inserire il loro HTML nella pagina sarebbe una falla.

// --- Riconoscimento dei media -------------------------------------------
// Tutte le piattaforme qui sotto si incorporano senza chiavi API.

const YT_RE = /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/i;
const VIMEO_RE = /^https?:\/\/(?:www\.)?vimeo\.com\/(\d{6,12})/i;
const TIKTOK_RE = /^https?:\/\/(?:www\.)?tiktok\.com\/@[\w.-]+\/video\/(\d{10,25})/i;
const IG_RE = /^https?:\/\/(?:www\.)?instagram\.com\/(?:reel|reels|p|tv)\/([A-Za-z0-9_-]{5,30})/i;
const FB_RE = /^https?:\/\/(?:www\.|web\.|m\.)?(?:facebook\.com\/[^\s]+|fb\.watch\/[\w-]+)/i;
const GIPHY_PAGE_RE = /^https?:\/\/(?:www\.)?giphy\.com\/(?:gifs|clips)\/(?:[\w-]*-)?([A-Za-z0-9]{10,})/i;
// I link diretti di Giphy oggi hanno un segmento con i punti dentro
// (media0.giphy.com/media/v1.Y2lkPTc.../giphy.gif): il vecchio [\w-]+ non
// li prendeva e le GIF finivano etichettate come immagini generiche.
const GIPHY_MEDIA_RE = /^https?:\/\/(?:media|i)\d*\.giphy\.com\/(?:media|avatars)\//i;
const TENOR_PAGE_RE = /^https?:\/\/(?:www\.)?tenor\.com\/(?:[a-z-]+\/)?view\/[\w-]*?(\d{6,})(?:$|[/?#])/i;
const TENOR_MEDIA_RE = /^https?:\/\/media\d*\.tenor\.com\/[\w-]+\/[^\s]+\.(?:gif|webp|mp4)/i;
const IMG_RE = /^https?:\/\/[^\s]+\.(?:gif|png|jpe?g|webp)(?:\?[^\s]*)?$/i;

const detectMedia = (raw) => {
    const url = (raw || '').trim();
    if (!/^https?:\/\//i.test(url)) return null;

    let m;
    if ((m = url.match(YT_RE))) {
        // Dominio senza cookie: incorporare YouTube non deve profilare chi legge.
        return { kind: 'youtube', label: 'YouTube', embed: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0`,
                 thumb: `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg`, url, ratio: '56.25%' };
    }
    if ((m = url.match(VIMEO_RE))) {
        return { kind: 'vimeo', label: 'Vimeo', embed: `https://player.vimeo.com/video/${m[1]}`, url, ratio: '56.25%' };
    }
    if ((m = url.match(TIKTOK_RE))) {
        return { kind: 'tiktok', label: 'TikTok', embed: `https://www.tiktok.com/embed/v2/${m[1]}`, url, ratio: '150%' };
    }
    if ((m = url.match(IG_RE))) {
        return { kind: 'instagram', label: 'Instagram', embed: `https://www.instagram.com/reel/${m[1]}/embed`, url, ratio: '125%' };
    }
    if (GIPHY_MEDIA_RE.test(url)) {
        return { kind: 'gif', label: 'GIF', src: url, url };
    }
    if ((m = url.match(GIPHY_PAGE_RE))) {
        // Dalla pagina di condivisione si ricava il file, senza passare dall'API.
        return { kind: 'gif', label: 'GIF', src: `https://media.giphy.com/media/${m[1]}/giphy.gif`, url };
    }
    if (TENOR_MEDIA_RE.test(url)) {
        return { kind: 'gif', label: 'GIF', src: url, url };
    }
    if ((m = url.match(TENOR_PAGE_RE))) {
        return { kind: 'tenor', label: 'GIF', embed: `https://tenor.com/embed/${m[1]}`, url, ratio: '100%' };
    }
    if (IMG_RE.test(url)) {
        return { kind: 'gif', label: 'Immagine', src: url, url };
    }
    if (FB_RE.test(url) && /\/(videos?|watch|reel)\//i.test(url + '/')) {
        return { kind: 'facebook', label: 'Facebook',
                 embed: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`,
                 url, ratio: '75%' };
    }
    return null;
};

const MEDIA_TONE = {
    youtube:   'from-red-500 to-red-600',
    vimeo:     'from-sky-500 to-cyan-600',
    tiktok:    'from-slate-800 to-slate-900',
    instagram: 'from-fuchsia-500 via-pink-500 to-orange-400',
    facebook:  'from-blue-600 to-blue-700',
    tenor:     'from-violet-500 to-indigo-600'
};

// I video non partono da soli: finche' non si tocca la scheda, la
// piattaforma non riceve alcuna richiesta e non sa chi sta leggendo.
const MediaEmbed = ({ media }) => {
    const [loaded, setLoaded] = useState(false);

    if (media.kind === 'gif') {
        return (
            <a href={media.url} target="_blank" rel="noopener noreferrer" className="block mt-2 rounded-xl overflow-hidden border border-slate-200 max-w-[280px]">
                <img src={media.src} alt="GIF" loading="lazy" className="w-full h-auto block bg-slate-100" />
            </a>
        );
    }

    if (loaded) {
        return (
            <div className="mt-2 rounded-xl overflow-hidden border border-slate-200 bg-black max-w-[420px]">
                <div className="relative w-full" style={{ paddingBottom: media.ratio }}>
                    <iframe
                        src={media.embed}
                        title={media.label}
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                    />
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setLoaded(true)}
            className="mt-2 w-full max-w-[420px] text-left rounded-xl overflow-hidden border border-slate-200 bg-slate-900 relative group/media"
        >
            {media.thumb ? (
                <img src={media.thumb} alt="" loading="lazy" className="w-full h-auto block opacity-80 group-hover/media:opacity-100 transition-opacity" />
            ) : (
                <div className={`w-full h-28 bg-gradient-to-br ${MEDIA_TONE[media.kind] || 'from-slate-700 to-slate-900'}`} />
            )}
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="h-12 w-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                    <ArrowLeft className="h-5 w-5 text-slate-900 rotate-180" />
                </span>
                <span className="text-[11px] font-bold text-white uppercase tracking-wider drop-shadow">
                    Guarda su {media.label}
                </span>
            </span>
        </button>
    );
};

// --- Formattazione ------------------------------------------------------

const INLINE_RE = /(\*\*[^*\n]+\*\*|~~[^~\n]+~~|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\((?:https?:\/\/)[^)\s]+\)|https?:\/\/[^\s]+)/g;

const SafeLink = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline decoration-current/40 underline-offset-2 hover:decoration-current break-all"
    >
        {children}
    </a>
);

// Trasforma una riga in nodi React. Nessun HTML costruito a mano.
const renderInline = (text, keyPrefix) => {
    const out = [];
    let last = 0;
    let i = 0;
    text.replace(INLINE_RE, (match, _g, offset) => {
        if (offset > last) out.push(text.slice(last, offset));
        const k = `${keyPrefix}-i${i++}`;

        if (match.startsWith('**') && match.endsWith('**')) {
            out.push(<strong key={k} className="font-bold">{match.slice(2, -2)}</strong>);
        } else if (match.startsWith('~~') && match.endsWith('~~')) {
            out.push(<s key={k} className="opacity-70">{match.slice(2, -2)}</s>);
        } else if (match.startsWith('`') && match.endsWith('`')) {
            out.push(<code key={k} className="px-1 py-0.5 rounded bg-black/10 font-mono text-[0.9em]">{match.slice(1, -1)}</code>);
        } else if (match.startsWith('[')) {
            const cut = match.indexOf('](');
            out.push(<SafeLink key={k} href={match.slice(cut + 2, -1)}>{match.slice(1, cut)}</SafeLink>);
        } else if (/^https?:\/\//i.test(match)) {
            const short = match.replace(/^https?:\/\/(www\.)?/i, '').slice(0, 48);
            out.push(<SafeLink key={k} href={match}>{short}{match.length > 55 ? '…' : ''}</SafeLink>);
        } else {
            // Corsivo: * oppure _
            out.push(<em key={k} className="italic">{match.slice(1, -1)}</em>);
        }
        last = offset + match.length;
        return match;
    });
    if (last < text.length) out.push(text.slice(last));
    return out;
};

const RichText = ({ text, className = "" }) => {
    const blocks = useMemo(() => {
        const lines = (text || '').split('\n');
        const result = [];
        let list = null;

        const flush = () => { if (list) { result.push(list); list = null; } };

        lines.forEach((line, idx) => {
            const trimmed = line.trim();

            // Una riga fatta solo da un link riconosciuto diventa il media.
            const media = trimmed && !trimmed.includes(' ') ? detectMedia(trimmed) : null;
            if (media) { flush(); result.push({ type: 'media', media, key: idx }); return; }

            let m;
            if ((m = trimmed.match(/^[-*]\s+(.*)$/))) {
                if (!list || list.type !== 'ul') { flush(); list = { type: 'ul', items: [], key: idx }; }
                list.items.push(m[1]);
                return;
            }
            if ((m = trimmed.match(/^(\d{1,2})[.)]\s+(.*)$/))) {
                if (!list || list.type !== 'ol') { flush(); list = { type: 'ol', items: [], key: idx }; }
                list.items.push(m[2]);
                return;
            }
            if ((m = trimmed.match(/^>\s?(.*)$/))) {
                flush();
                result.push({ type: 'quote', text: m[1], key: idx });
                return;
            }

            flush();
            result.push({ type: 'p', text: line, key: idx });
        });
        flush();
        return result;
    }, [text]);

    return (
        <div className={`whitespace-pre-wrap break-words ${className}`}>
            {blocks.map((b, i) => {
                if (b.type === 'media') return <MediaEmbed key={`m${b.key}`} media={b.media} />;
                if (b.type === 'ul') return (
                    <ul key={`u${b.key}`} className="list-disc pl-5 my-1 space-y-0.5">
                        {b.items.map((it, j) => <li key={j}>{renderInline(it, `u${b.key}-${j}`)}</li>)}
                    </ul>
                );
                if (b.type === 'ol') return (
                    <ol key={`o${b.key}`} className="list-decimal pl-5 my-1 space-y-0.5">
                        {b.items.map((it, j) => <li key={j}>{renderInline(it, `o${b.key}-${j}`)}</li>)}
                    </ol>
                );
                if (b.type === 'quote') return (
                    <blockquote key={`q${b.key}`} className="border-l-2 border-current/30 pl-3 my-1 opacity-80 italic">
                        {renderInline(b.text, `q${b.key}`)}
                    </blockquote>
                );
                if (!b.text.trim()) return <span key={`e${b.key}`}>{i === blocks.length - 1 ? null : '\n'}</span>;
                return <p key={`p${b.key}`} className="my-0">{renderInline(b.text, `p${b.key}`)}</p>;
            })}
        </div>
    );
};

// --- Barra di formattazione ---------------------------------------------

const RichTextToolbar = ({ textareaRef, value, onChange, onOpenGif, onOpenLink }) => {
    // Avvolge la selezione, o inserisce un segnaposto se non c'e' selezione.
    const wrap = (before, after = before, placeholder = 'testo') => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const selected = value.slice(start, end) || placeholder;
        const next = value.slice(0, start) + before + selected + after + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + before.length, start + before.length + selected.length);
        });
    };

    // I prefissi di riga vanno messi all'inizio della riga corrente.
    const prefixLine = (prefix) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
        onChange(next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + prefix.length, start + prefix.length);
        });
    };

    const Btn = ({ onClick, title, children }) => (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={title}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors shrink-0"
        >
            {children}
        </button>
    );

    return (
        <div className="flex items-center gap-0.5 px-1 pb-1.5 overflow-x-auto custom-scrollbar">
            <Btn onClick={() => wrap('**')} title="Grassetto"><span className="font-black text-sm">B</span></Btn>
            <Btn onClick={() => wrap('_')} title="Corsivo"><span className="italic font-serif text-sm">I</span></Btn>
            <Btn onClick={() => wrap('~~')} title="Barrato"><span className="line-through text-sm">S</span></Btn>
            <Btn onClick={() => wrap('`')} title="Codice"><span className="font-mono text-xs">{'</>'}</span></Btn>
            <span className="w-px h-5 bg-slate-200 mx-1 shrink-0" />
            <Btn onClick={() => prefixLine('- ')} title="Elenco puntato"><List className="h-4 w-4" /></Btn>
            <Btn onClick={() => prefixLine('1. ')} title="Elenco numerato"><span className="text-xs font-bold">1.</span></Btn>
            <Btn onClick={() => prefixLine('> ')} title="Citazione"><span className="text-sm font-bold">&ldquo;</span></Btn>
            <span className="w-px h-5 bg-slate-200 mx-1 shrink-0" />
            <Btn
                onClick={() => {
                    // Quello che e' selezionato diventa il testo del link:
                    // e' quasi sempre la parola su cui si vuole agganciarlo.
                    const el = textareaRef.current;
                    const sel = el ? value.slice(el.selectionStart, el.selectionEnd) : '';
                    onOpenLink(sel);
                }}
                title="Link"><ExternalLink className="h-4 w-4" /></Btn>
            <Btn onClick={onOpenGif} title="GIF o video"><Film className="h-4 w-4" /></Btn>
        </div>
    );
};

// --- Inserimento di GIF e video -----------------------------------------

// La chiave Giphy e' destinata all'uso da browser, ma resta visibile nel
// bundle: va limitata per dominio dal pannello Giphy. Senza chiave la
// scheda di ricerca sparisce e resta l'inserimento per link.
const GIPHY_KEY = process.env.REACT_APP_GIPHY_KEY || '';

const searchGiphy = async (query, signal) => {
    if (!GIPHY_KEY) return [];
    const base = query
        ? `https://api.giphy.com/v1/gifs/search?q=${encodeURIComponent(query)}&lang=it&`
        : 'https://api.giphy.com/v1/gifs/trending?';
    const url = `${base}api_key=${GIPHY_KEY}&limit=24&rating=pg-13&bundle=messaging_non_clips`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error('Giphy ha risposto ' + res.status);
    const json = await res.json();
    return (json.data || []).map(g => ({
        id: g.id,
        title: g.title || 'GIF',
        preview: g.images?.fixed_width_downsampled?.url || g.images?.fixed_width?.url,
        full: g.images?.downsized_medium?.url || g.images?.original?.url
    })).filter(g => g.preview && g.full);
};

// Elenco delle piattaforme riconosciute, mostrato nella scheda Link.
const LINK_SOURCES = [
    { label: 'YouTube', hint: 'anche Shorts' },
    { label: 'TikTok', hint: null },
    { label: 'Instagram', hint: 'Reels e post' },
    { label: 'Facebook', hint: 'video' },
    { label: 'Vimeo', hint: null },
    { label: 'Tenor', hint: 'GIF' }
];

// Inserire un link scrivendo [testo](https://) a mano e' una sintassi che
// nessuno ha voglia di ricordare, e il segnaposto restava li' quando ci si
// dimenticava di sostituirlo. Qui si compilano due campi.
const LinkModal = ({ initialText = '', onInsert, onClose }) => {
    const [text, setText] = useState(initialText);
    const [href, setHref] = useState('');
    const [pasteErr, setPasteErr] = useState('');
    const canPaste = typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.readText;

    const url = href.trim();
    // Chi incolla un dominio senza schema non ha sbagliato: glielo mettiamo noi.
    const normalized = url && !/^https?:\/\//i.test(url) ? 'https://' + url : url;
    const valid = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(normalized);
    const label = text.trim() || normalized.replace(/^https?:\/\//i, '').replace(/\/$/, '');

    const confirm = () => {
        if (!valid) return;
        onInsert(label, normalized);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div className="rp-glass-strong rounded-2xl w-full max-w-md overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="rp-nav p-4 text-white flex justify-between items-center shrink-0">
                    <h3 className="font-bold flex items-center gap-2"><ExternalLink className="h-5 w-5" /> Inserisci un link</h3>
                    <button onClick={onClose} aria-label="Chiudi"><X className="h-5 w-5 hover:text-slate-300" /></button>
                </div>

                <div className="p-4 space-y-4">
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Indirizzo</label>
                        <div className="flex gap-2">
                            <input
                                autoFocus
                                value={href}
                                onChange={e => { setHref(e.target.value); setPasteErr(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
                                placeholder="refpublic.it oppure https://…"
                                className="flex-1 min-w-0 border border-slate-300 p-2.5 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-500 outline-none"
                            />
                            {canPaste && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            const t = await navigator.clipboard.readText();
                                            if (t && t.trim()) { setHref(t.trim()); setPasteErr(''); }
                                            else setPasteErr('Gli appunti sono vuoti.');
                                        } catch (err) {
                                            setPasteErr('Il browser non ci lascia leggere gli appunti: incolla a mano.');
                                        }
                                    }}
                                    className="shrink-0 px-3.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
                                >
                                    Incolla
                                </button>
                            )}
                        </div>
                        {pasteErr && <p className="text-[11px] text-amber-700 mt-1.5">{pasteErr}</p>}
                        {!pasteErr && url && !valid && <p className="text-[11px] text-amber-700 mt-1.5">Non sembra un indirizzo valido.</p>}
                    </div>

                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Testo da mostrare</label>
                        <input
                            value={text}
                            onChange={e => setText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
                            placeholder="Lascia vuoto per usare l&rsquo;indirizzo"
                            className="w-full border border-slate-300 p-2.5 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-500 outline-none"
                        />
                    </div>

                    {/* Si vede subito come verra': la sintassi non la scrive nessuno. */}
                    {valid && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Anteprima</p>
                            <p className="text-sm text-blue-600 underline break-all">{label}</p>
                        </div>
                    )}
                </div>

                <div className="p-4 pt-0 flex gap-2 shrink-0">
                    <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors">Annulla</button>
                    <button
                        onClick={confirm}
                        disabled={!valid}
                        className="flex-[1.6] py-2.5 rp-gloss bg-orange-500 text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:hover:bg-orange-500"
                    >
                        Inserisci link
                    </button>
                </div>
            </div>
        </div>
    );
};

const MediaLinkModal = ({ onInsert, onClose }) => {
    const [tab, setTab] = useState('gif');
    // Incolla dagli appunti: su mobile copiare un link e poi doverlo
    // incollare a mano dentro un campo e' il passaggio che si sbaglia.
    const [pasteErr, setPasteErr] = useState('');
    const canPaste = typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.readText;

    // --- GIF ---
    const [query, setQuery] = useState('');
    const [gifs, setGifs] = useState([]);
    const [gifLoading, setGifLoading] = useState(false);
    const [gifError, setGifError] = useState('');

    useEffect(() => {
        if (tab !== 'gif' || !GIPHY_KEY) return;
        const ctrl = new AbortController();
        // Si aspetta che l'utente smetta di scrivere prima di chiamare.
        const t = setTimeout(async () => {
            setGifLoading(true);
            setGifError('');
            try {
                setGifs(await searchGiphy(query.trim(), ctrl.signal));
            } catch (err) {
                if (err.name !== 'AbortError') setGifError('Ricerca non riuscita. Riprova fra poco.');
            } finally {
                setGifLoading(false);
            }
        }, query ? 380 : 0);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [query, tab]);

    // --- Link ---
    const [url, setUrl] = useState('');
    const trimmed = url.trim();
    const media = detectMedia(trimmed);
    const looksLikeUrl = /^https?:\/\//i.test(trimmed);

    const Tab = ({ id, icon: Icon, children }) => (
        <button
            type="button"
            onClick={() => setTab(id)}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${tab === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
            <Icon className="h-4 w-4" /> {children}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div className="rp-glass-strong rounded-2xl w-full max-w-lg overflow-hidden max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>

                <div className="bg-slate-900 p-4 text-white flex justify-between items-center shrink-0">
                    <h3 className="font-bold flex items-center gap-2"><Film className="h-5 w-5" /> Aggiungi al messaggio</h3>
                    <button onClick={onClose} aria-label="Chiudi"><X className="h-5 w-5 hover:text-slate-300" /></button>
                </div>

                <div className="p-3 pb-0 shrink-0">
                    <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                        <Tab id="gif" icon={Film}>GIF</Tab>
                        <Tab id="link" icon={ExternalLink}>Link</Tab>
                    </div>
                </div>

                {/* --- Ricerca GIF --- */}
                {tab === 'gif' && !GIPHY_KEY && (
                    <div className="p-6 text-center flex-1 flex flex-col items-center justify-center gap-3">
                        <Film className="h-10 w-10 text-slate-300" />
                        <p className="text-sm text-slate-500 max-w-xs">
                            La ricerca GIF non &egrave; attiva su questa versione.
                            Puoi comunque incollare il link di una GIF da Giphy o Tenor.
                        </p>
                        <button onClick={() => setTab('link')} className="text-sm font-bold text-orange-600 hover:underline">
                            Passa a Link
                        </button>
                    </div>
                )}

                {tab === 'gif' && GIPHY_KEY && (
                    <>
                        <div className="p-4 pb-3 shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                                <input
                                    autoFocus
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Cerca una GIF…"
                                    className="w-full border border-slate-300 p-2.5 pl-9 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                        </div>

                        <div className="px-4 pb-4 overflow-y-auto flex-1 custom-scrollbar">
                            {gifLoading && gifs.length === 0 ? (
                                <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
                            ) : gifError ? (
                                <p className="text-center text-sm text-red-600 py-10">{gifError}</p>
                            ) : gifs.length === 0 ? (
                                <p className="text-center text-sm text-slate-400 italic py-10">Nessun risultato.</p>
                            ) : (
                                <div className="grid grid-cols-3 gap-2">
                                    {gifs.map(g => (
                                        <button
                                            key={g.id}
                                            type="button"
                                            onClick={() => { onInsert(g.full); onClose(); }}
                                            title={g.title}
                                            className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-orange-500 transition-shadow"
                                        >
                                            <img src={g.preview} alt={g.title} loading="lazy" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className="text-[10px] text-slate-400 text-center mt-3">Risultati forniti da GIPHY</p>
                        </div>
                    </>
                )}

                {/* --- Inserimento link --- */}
                {tab === 'link' && (
                    <div className="p-4 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Incolla il link</label>
                            <div className="flex gap-2">
                                <input
                                    autoFocus
                                    value={url}
                                    onChange={e => { setUrl(e.target.value); setPasteErr(''); }}
                                    placeholder="https://…"
                                    className="flex-1 min-w-0 border border-slate-300 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none bg-white"
                                />
                                {canPaste && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                const t = await navigator.clipboard.readText();
                                                if (t && t.trim()) { setUrl(t.trim()); setPasteErr(''); }
                                                else setPasteErr('Gli appunti sono vuoti.');
                                            } catch (err) {
                                                // Alcuni browser chiedono un permesso e possono negarlo.
                                                setPasteErr('Il browser non ci lascia leggere gli appunti: incolla a mano.');
                                            }
                                        }}
                                        className="shrink-0 px-3.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
                                    >
                                        Incolla
                                    </button>
                                )}
                                {url && (
                                    <button type="button" onClick={() => { setUrl(''); setPasteErr(''); }}
                                        title="Svuota" aria-label="Svuota"
                                        className="shrink-0 px-3 rounded-xl border border-slate-300 text-slate-500 hover:bg-slate-50 transition-colors">
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            {pasteErr && <p className="text-[11px] text-amber-700 mt-1.5">{pasteErr}</p>}
                            {!url && <p className="text-[11px] text-slate-400 mt-1.5">Copia il link dall&apos;app o dal browser, poi tocca Incolla.</p>}
                        </div>

                        {/* Anteprima immediata: si vede subito cosa verra' inserito. */}
                        {looksLikeUrl && media && (
                            <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                                <p className="flex items-center gap-2 text-sm text-green-800 font-bold mb-2">
                                    <CheckCircle2 className="h-4 w-4 shrink-0" /> {media.label}
                                </p>
                                <div className="pointer-events-none opacity-90">
                                    <MediaEmbed media={media} />
                                </div>
                            </div>
                        )}

                        {looksLikeUrl && !media && (
                            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                <span>Piattaforma non riconosciuta: verrà inserito come link normale, cliccabile.</span>
                            </div>
                        )}

                        <div>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Da dove puoi incollare</p>
                            <div className="flex flex-wrap gap-1.5">
                                {LINK_SOURCES.map(sr => (
                                    <span key={sr.label} className="px-2.5 py-1 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-600">
                                        <strong className="text-slate-700">{sr.label}</strong>
                                        {sr.hint && <span className="text-slate-400"> · {sr.hint}</span>}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">
                                Va bene anche un link non riconosciuto: diventa un collegamento normale.
                                I video non partono da soli, chi legge vede un&apos;anteprima e decide se aprirli.
                            </p>
                        </div>
                    </div>
                )}

                {tab === 'link' && (
                    <div className="p-4 border-t border-slate-200 flex gap-2 shrink-0">
                        <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors">Annulla</button>
                        <button
                            onClick={() => { onInsert(trimmed); onClose(); }}
                            disabled={!looksLikeUrl}
                            className="flex-[1.6] py-2.5 rp-gloss bg-orange-500 text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:hover:bg-orange-500"
                        >
                            {media ? `Inserisci ${media.label}` : 'Inserisci'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const Arena = ({ currentUser, onOpenProfile, currentThreadId, setCurrentThreadId, followingList, viewMode }) => {
    const committeesList = useCommittees();
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [newThreadTitle, setNewThreadTitle] = useState('');
    const [targetComitato, setTargetComitato] = useState('Tutti');
    const [targetRuolo, setTargetRuolo] = useState('Tutti');
    const [newMessageText, setNewMessageText] = useState('');
    const [gifModalOpen, setGifModalOpen] = useState(false);
    // null = chiusa; una stringa (anche vuota) = aperta con quel testo.
    const [linkModalText, setLinkModalText] = useState(null);
    const composerRef = useRef(null);
    const [refresh, setRefresh] = useState(0);
    const messagesEndRef = useRef(null);

    // --- LOGICA SMART FILTERS (Stessa dei Meetup) ---
    
    const getAvailableTargetCommittees = () => {
        // Gli Admin/Super Admin possono postare per qualsiasi comitato
        if (currentUser.is_super_admin || currentUser.is_admin) return ["Tutti", ...committeesList];
        
        // Se l'utente non ha un comitato definito (fallback), vede tutto
        if (!currentUser.comitato) return ["Tutti", ...committeesList];
        
        // Utente standard: può selezionare solo "Tutti" o il PROPRIO comitato
        return ["Tutti", currentUser.comitato];
    };

    const getAvailableTargetRoles = () => {
        // Gli Admin vedono tutto
        if (currentUser.is_super_admin || currentUser.is_admin) return ["Tutti", ...RUOLI];

        const roles = ["Tutti"];
        const myRoleIdx = RUOLI.indexOf(currentUser.role);
        
        if (myRoleIdx !== -1) {
            // Logica gerarchica: Posso indirizzare a "Tutti", al mio livello, e ai livelli inferiori
            // (Es. Se sono Regionale, vedo: Regionale, Territoriale, Osservatore)
            for (let i = myRoleIdx; i < RUOLI.length; i++) {
                roles.push(RUOLI[i]);
            }
        } else {
            // Fallback se il ruolo non è nella lista standard
            return ["Tutti", ...RUOLI];
        }
        return roles;
    };

    // Calcolo le liste disponibili in base all'utente corrente
    const availableCommittees = getAvailableTargetCommittees();
    const availableRoles = getAvailableTargetRoles();

    useEffect(() => {
        fetchThreads().then(data => {
            let visible = data.filter(t => isThreadVisible(t, currentUser));
            if (viewMode === 'following') {
                visible = visible.filter(t => followingList.includes(t.created_by) || t.created_by === currentUser.id);
            }
            setThreads(visible);
        });
    }, [refresh, currentUser, viewMode, followingList]); 

    useEffect(() => { if (currentThreadId) { fetchMessages(currentThreadId).then(setMessages); } }, [currentThreadId, refresh]);

    useEffect(() => {
        if (!supabase) return;
        const channel = supabase.channel('arena_live')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'likes' }, () => setRefresh(p => p + 1))
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => { if (payload.new.thread_id === currentThreadId) setRefresh(p => p + 1); })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages' }, () => setRefresh(p => p + 1))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'threads' }, () => setRefresh(p => p + 1))
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [currentThreadId]);

    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const handleCreateThread = async () => { 
        if (!newThreadTitle.trim()) return; 
        const newThreads = await createThread(newThreadTitle.trim(), currentUser.id, targetComitato, targetRuolo); 
        if(newThreads && newThreads.length > 0) {
            setCurrentThreadId(newThreads[0].id);
            notifyNewThread(newThreads[0], currentUser);
        } 
        setNewThreadTitle(''); 
        setTargetComitato('Tutti'); 
        setTargetRuolo('Tutti'); 
        setRefresh(prev => prev + 1); 
    };

    const handleDeleteThread = async (e, tId) => { 
        e.stopPropagation(); 
        if(window.confirm("Eliminare discussione?")) { 
            setThreads(prev => prev.filter(t => t.id !== tId)); 
            if(currentThreadId === tId) setCurrentThreadId(null); 
            await deleteThread(tId); 
        } 
    };

    // Il media va su una riga sua: e' cosi' che viene riconosciuto in lettura.
    // Il link va dove sta il cursore, non in fondo: e' un pezzo di frase.
    const insertLink = (label, href) => {
        const el = composerRef.current;
        // La sintassi in lettura non ammette parentesi quadre nell'etichetta
        // ne' tonde nell'indirizzo: se ci finiscono, il link non viene
        // riconosciuto e resta a schermo il markup grezzo.
        const safeLabel = label.replace(/[[\]\n]/g, ' ').trim() || 'link';
        const safeHref = href.replace(/[()]/g, c => encodeURIComponent(c));
        const md = `[${safeLabel}](${safeHref})`;
        const start = el ? el.selectionStart : newMessageText.length;
        const end = el ? el.selectionEnd : newMessageText.length;
        setNewMessageText(prev => prev.slice(0, start) + md + prev.slice(end));
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + md.length, start + md.length);
        });
    };

    const insertMedia = (url) => {
        setNewMessageText(prev => {
            const sep = !prev || prev.endsWith('\n') ? '' : '\n';
            return prev + sep + url + '\n';
        });
        requestAnimationFrame(() => composerRef.current?.focus());
    };

    const handleComposerKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSendMessage = async (e) => { 
        e?.preventDefault(); 
        if (!currentThreadId || !newMessageText.trim()) return; 
        await sendMessage({ threadId: currentThreadId, testo: newMessageText }); 
        notifyThreadReply(currentThreadId, currentUser);
        setNewMessageText(''); 
        setRefresh(prev => prev + 1); 
    };

    const handleDeleteMessage = async (msgId) => { 
        if(window.confirm("Eliminare messaggio?")) { 
            setMessages(prev => prev.filter(m => m.id !== msgId)); 
            await deleteMessage(msgId); 
        } 
    };

    const handleLike = async (msgId) => { 
        const liked = await userLikedMessage(msgId, currentUser.id); 
        await toggleLike(msgId, currentUser.id, liked); 
        setRefresh(prev => prev + 1); 
    };

    return (
        <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-140px)]">
            <div className={`${currentThreadId ? 'hidden md:flex' : 'flex'} w-full md:w-1/3 lg:w-1/4 flex-col gap-4`}>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-3"><MessageSquare className="h-5 w-5 text-orange-500"/> Nuova Discussione</h2>
                    <div className="flex flex-col gap-2">
                        <input type="text" placeholder="Argomento..." className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-orange-500 outline-none" value={newThreadTitle} onChange={(e) => setNewThreadTitle(e.target.value)} />
                        <div className="flex gap-2">
                            {/* SELECT COMITATI INTELLIGENTE */}
                            <select className="w-1/2 text-sm border border-slate-200 rounded-lg p-2 bg-slate-50 text-slate-600 outline-none focus:border-orange-500" value={targetComitato} onChange={e => setTargetComitato(e.target.value)}>
                                {availableCommittees.map(c => (
                                    <option key={c} value={c}>
                                        {c === 'Tutti' ? 'Tutti i Comitati' : c}
                                    </option>
                                ))}
                            </select>
                            
                            {/* SELECT RUOLI INTELLIGENTE */}
                            <select className="w-1/2 text-sm border border-slate-200 rounded-lg p-2 bg-slate-50 text-slate-600 outline-none focus:border-orange-500" value={targetRuolo} onChange={e => setTargetRuolo(e.target.value)}>
                                {availableRoles.map(r => (
                                    <option key={r} value={r}>
                                        {r === 'Tutti' ? 'Tutti i Ruoli' : r}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button onClick={handleCreateThread} className="bg-slate-800 text-white px-3 py-1.5 rounded hover:bg-slate-700 flex items-center justify-center gap-2 text-sm"><Users className="h-3 w-3"/> Crea</button>
                    </div>
                </div>
                <div className="rp-card rounded-2xl flex-1 overflow-hidden flex flex-col">
                    <div className="p-3 border-b bg-slate-50 text-xs font-bold text-slate-500 uppercase">Discussioni Recenti</div>
                    <div className="overflow-y-auto flex-1 p-2 space-y-1">
                        {threads.map((thread) => (
                            <button key={thread.id} onClick={() => setCurrentThreadId(thread.id)} className={`w-full text-left p-3 rounded-lg transition-all border ${currentThreadId === thread.id ? 'bg-orange-50 border-orange-400' : 'bg-white border-transparent hover:bg-slate-50'}`}>
                                <div className="font-bold text-sm text-slate-800 truncate">{thread.titolo}</div>
                                <div className="text-xs text-slate-400 mt-1 flex justify-between"><span>{new Date(thread.created_at).toLocaleDateString()}</span><span>{thread.author ? getInitials(thread.author.full_name) : ''}</span> </div>
                                {(thread.created_by === currentUser.id || currentUser.is_admin) && <div onClick={(e) => handleDeleteThread(e, thread.id)} className="text-[10px] text-red-400 hover:underline mt-1 text-right">Elimina</div>}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className={`${!currentThreadId ? 'hidden md:flex' : 'flex'} flex-1 rp-card rounded-2xl overflow-hidden flex flex-col`}>
                {currentThreadId ? (
                    <>
                        <div className="p-4 bg-white border-b border-slate-200 shadow-sm flex items-center gap-3"><button onClick={() => setCurrentThreadId(null)} className="md:hidden p-2 -ml-2 text-slate-500 rounded-full hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button><div><h3 className="font-bold text-slate-800 truncate">{threads.find(t => t.id === currentThreadId)?.titolo}</h3><p className="text-xs text-slate-400">{threads.find(t => t.id === currentThreadId)?.target_comitato || 'Pubblico'} • {threads.find(t => t.id === currentThreadId)?.target_ruolo || 'Tutti'}</p></div></div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                            {messages.map((msg) => {
                                const isMe = msg.inviato_da === currentUser.id; const profile = msg.profile || { full_name: 'Utente' };
                                return (
                                    <div key={msg.id} className={`flex gap-3 group ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                        <Avatar name={profile.full_name} src={profile.avatar_url} onClick={() => onOpenProfile(profile)} className="mt-1 shadow-sm hover:ring-2 hover:ring-orange-300 cursor-pointer"/>
                                        <div className={`flex flex-col max-w-[80%] ${isMe ? 'items-end' : 'items-start'}`}>
                                            <div className="flex items-baseline gap-2 mb-1"><span onClick={() => onOpenProfile(profile)} className={`text-xs font-bold cursor-pointer hover:underline ${isMe ? 'text-slate-600' : 'text-slate-800'}`}>{profile.full_name}</span><span className="text-[10px] text-slate-400">{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span></div>
                                            <div className={`p-3 rounded-2xl text-sm shadow-sm relative group-message ${isMe ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white text-slate-800 rounded-tl-none border border-slate-100'}`}><RichText text={msg.testo} />{(isMe || currentUser.is_admin) && <button onClick={() => handleDeleteMessage(msg.id)} className="absolute -top-2 -left-2 bg-white text-red-500 rounded-full p-1 shadow border opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3 w-3"/></button>}</div>
                                            <div className="mt-1"><button onClick={() => handleLike(msg.id)} className="flex items-center gap-1 text-slate-400 hover:text-orange-500 text-xs"><ThumbsUp className="h-3 w-3"/><LikeCount messageId={msg.id} userId={currentUser.id} trigger={refresh}/></button></div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="p-3 bg-white border-t border-slate-200">
                            <form onSubmit={handleSendMessage}>
                                <div className="bg-slate-100 rounded-2xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500 transition-shadow">
                                    <RichTextToolbar
                                        textareaRef={composerRef}
                                        value={newMessageText}
                                        onChange={setNewMessageText}
                                        onOpenGif={() => setGifModalOpen(true)}
                                        onOpenLink={(sel) => setLinkModalText(sel || '')}
                                    />
                                    <div className="flex gap-2 items-end px-2 pb-2">
                                        <textarea
                                            ref={composerRef}
                                            rows={1}
                                            className="flex-1 bg-transparent border-0 resize-none outline-none text-sm px-2 py-2 max-h-40 custom-scrollbar"
                                            placeholder="Scrivi un messaggio…"
                                            value={newMessageText}
                                            onChange={(e) => setNewMessageText(e.target.value)}
                                            onKeyDown={handleComposerKeyDown}
                                            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'; }}
                                        />
                                        <button type="submit" disabled={!newMessageText.trim()} className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white p-3 rounded-full shadow-md shrink-0 transition-colors">
                                            <Send className="h-5 w-5 ml-0.5"/>
                                        </button>
                                    </div>
                                </div>
                            </form>
                            {gifModalOpen && <MediaLinkModal onInsert={insertMedia} onClose={() => setGifModalOpen(false)} />}
                            {linkModalText !== null && <LinkModal initialText={linkModalText} onInsert={insertLink} onClose={() => setLinkModalText(null)} />}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400"><div className="bg-slate-100 p-6 rounded-full mb-4"><MessageCircle className="h-12 w-12 text-slate-300"/></div><p className="font-medium text-slate-500">Seleziona una discussione per iniziare</p></div>
                )}
            </div>
        </div>
    );
};

const DailyQuizView = ({ user, onUpdateUserStreak, onOpenSearch, initialMode = 'training' }) => {
    const [mode, setMode] = useState(initialMode); 
    
    const [setupMode, setSetupMode] = useState(true);
    const [selectedSources, setSelectedSources] = useState(['Tutti']);
    const [selectedDifficulty, setSelectedDifficulty] = useState('Tutte');
    const [questionCount, setQuestionCount] = useState(5);
    
    const [challenges, setChallenges] = useState([]);
    const [showPastChallenges, setShowPastChallenges] = useState(false);
    const [activeChallenge, setActiveChallenge] = useState(null); 
    const [challengeRoundData, setChallengeRoundData] = useState(null); 
    const [showRulesModal, setShowRulesModal] = useState(false); 
    
    const [questions, setQuestions] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // TIMER
    const [timeLeft, setTimeLeft] = useState(0);
    const [totalTime, setTotalTime] = useState(0);
    const [gameOutcome, setGameOutcome] = useState(null); 
    const [streakInfo, setStreakInfo] = useState(null);
    const [celebration, setCelebration] = useState(null);

    // Ripasso degli errori
    const [reviewDue, setReviewDue] = useState(0);
    const [isReviewSession, setIsReviewSession] = useState(false);
    // Gli esiti si accumulano qui e partono in un'unica chiamata a fine sessione.
    const answersRef = useRef([]);

    const DOCUMENTS = ['Regole di Gioco', 'Casistica Ufficiale', 'Normative Federali', 'Indicazioni Tecniche', 'Regolamento Video-Check', 'Manuale Segnapunti'];
    const DIFFICULTIES = ['Tutte', 'Facile', 'Medio', 'Difficile'];
    const COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30, 50];

    useEffect(() => {
        setMode(initialMode);
        if (initialMode === 'training') reset();
    }, [initialMode]);

    useEffect(() => { if (mode === 'challenges') loadChallenges(); }, [mode]);

    useEffect(() => { loadReviewCount(); /* eslint-disable-next-line */ }, [user.id]);

    const loadReviewCount = async () => {
        const { rows } = await fetchReviewQueue(user.id);
        setReviewDue(rows.length);
    };

    // --- TIMER LOGIC ---
    useEffect(() => {
        let timer;
        if (mode === 'challenges' && !setupMode && !showResult && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (mode === 'challenges' && timeLeft === 0 && !setupMode && !showResult) {
            finishSession(true); // Force finish
        }
        return () => clearInterval(timer);
    }, [timeLeft, setupMode, showResult, mode]);

    // --- REALTIME SYNC ---
    useEffect(() => {
        if (mode === 'challenges') {
            const channel = supabase.channel('challenges_realtime')
                .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_challenges' }, (payload) => {
                    const isMine = payload.new?.challenger_id === user.id || payload.new?.opponent_id === user.id || payload.old?.challenger_id === user.id || payload.old?.opponent_id === user.id;
                    if (isMine) loadChallenges();
                })
                .subscribe();
            return () => { supabase.removeChannel(channel); };
        }
    }, [mode, user.id]);

    // --- UTILS ---
    const cleanExpiredChallenges = async (data) => {
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        const toDelete = data.filter(c => {
            const lastUpdate = c.updated_at ? new Date(c.updated_at) : new Date(c.created_at);
            return (c.status === 'pending' || c.status === 'active') && lastUpdate < oneHourAgo;
        });
        if (toDelete.length > 0) {
            const ids = toDelete.map(c => c.id);
            await supabase.from('quiz_challenges').delete().in('id', ids);
            return data.filter(c => !ids.includes(c.id));
        }
        return data;
    };

    const loadChallenges = async () => {
        let data = await fetchMyChallenges(user.id);
        data = await cleanExpiredChallenges(data);
        setChallenges(data);
    };

    const sendChallengeNotification = async (targetUserId, title, text) => {
        await supabase.from('notifications').insert([{
            user_id: targetUserId, type: 'challenge', title: title, text: text, link_tab: 'quiz', link_id: 'challenges', read: false
        }]);
    };

    const shuffleQuestionsData = (rawQuestions) => {
        return rawQuestions.map(q => {
            const optionsWithIndex = q.options.map((opt, i) => ({ text: opt, originalIndex: i }));
            for (let i = optionsWithIndex.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [optionsWithIndex[i], optionsWithIndex[j]] = [optionsWithIndex[j], optionsWithIndex[i]];
            }
            const newCorrectIndex = optionsWithIndex.findIndex(o => o.originalIndex === q.correct_index);
            return { ...q, options: optionsWithIndex.map(o => o.text), correct_index: newCorrectIndex };
        });
    };

    const fetchRandomQuestions = async (count, filters = {}, excludeIds = []) => {
        let query = supabase.from('quiz_questions').select('id'); // select('id') is lightweight
        if (filters.sources && !filters.sources.includes('Tutti')) query = query.in('source_doc', filters.sources);
        if (filters.difficulty && filters.difficulty !== 'Tutte') query = query.eq('difficulty', filters.difficulty);
        
        const { data: idsData, error } = await query;
        if (error || !idsData || idsData.length === 0) return [];

        const availableIds = idsData.map(x => x.id).filter(id => !excludeIds.includes(id));
        const pool = availableIds.length >= count ? availableIds : idsData.map(x => x.id);
        const shuffledIds = pool.sort(() => 0.5 - Math.random());
        const selectedIds = shuffledIds.slice(0, count);
        const { data: questionsData } = await supabase.from('quiz_questions').select('*').in('id', selectedIds);
        return questionsData || [];
    };

    const toggleSource = (doc) => {
        if (doc === 'Tutti') { setSelectedSources(['Tutti']); return; }
        let newS = [...selectedSources];
        if (newS.includes('Tutti')) newS = [];
        if (newS.includes(doc)) newS = newS.filter(s => s !== doc); else newS.push(doc);
        if (newS.length === 0) newS = ['Tutti'];
        setSelectedSources(newS);
    };

    // --- AZIONI ---
    const startTraining = async () => {
        setLoading(true);
        const rawQuestions = await fetchRandomQuestions(questionCount, { sources: selectedSources, difficulty: selectedDifficulty }, []);
        if (rawQuestions.length === 0) { alert("Nessuna domanda trovata!"); setLoading(false); return; }
        answersRef.current = [];
        setIsReviewSession(false);
        const processed = shuffleQuestionsData(rawQuestions);
        setQuestions(processed);
        setSetupMode(false);
        setLoading(false);
    };

    // Sessione costruita sulle domande sbagliate e tornate in scadenza.
    const startReview = async () => {
        setLoading(true);
        const { rows, notReady } = await fetchReviewQueue(user.id);
        if (notReady) {
            alert("Il ripasso non è ancora attivo su questo server.");
            setLoading(false); return;
        }
        if (rows.length === 0) {
            alert("Non hai domande da ripassare adesso. Torna quando ne avrai sbagliata qualcuna.");
            setLoading(false); return;
        }
        const raw = await fetchQuestionsByIds(rows.slice(0, 20).map(r => r.question_id));
        if (raw.length === 0) {
            alert("Le domande da ripassare non sono più disponibili.");
            setLoading(false); return;
        }
        answersRef.current = [];
        setIsReviewSession(true);
        setQuestions(shuffleQuestionsData(raw));
        setSetupMode(false);
        setLoading(false);
    };

    const handleOpenCreateFlow = () => { setShowRulesModal(true); };

    const handleProceedToSearch = () => {
        setShowRulesModal(false);
        onOpenSearch(async (opponent) => {
            if (opponent.id === user.id) return alert("Non puoi sfidare te stesso.");
            await createChallenge(user.id, opponent.id);
            await sendChallengeNotification(opponent.id, "⚔️ Nuova Sfida PvP!", `${user.full_name} ti ha sfidato a duello! Accetta per giocare.`);
            loadChallenges();
            alert(`Sfida inviata a ${opponent.full_name}!`);
        });
    };

    const handlePlayRound = async (c) => {
        setLoading(true);
        const { data: freshChallenge } = await supabase.from('quiz_challenges').select('*').eq('id', c.id).single();
        if (!freshChallenge) { alert("Errore caricamento sfida."); setLoading(false); return; }

        setActiveChallenge(freshChallenge); 
        const roundNum = freshChallenge.current_round;
        const roundsData = freshChallenge.rounds_data || {};
        const currentRoundData = roundsData[roundNum] || {};
        const isP1 = user.id === freshChallenge.challenger_id;
        const myScoreKey = isP1 ? 'p1_score' : 'p2_score';
        
        if (currentRoundData[myScoreKey] !== undefined) {
            alert("Hai già giocato questo turno. Attendi l'avversario.");
            setLoading(false); setActiveChallenge(null); loadChallenges(); return;
        }

        let roundQuestions = [];
        if (currentRoundData.questions) {
            const { data } = await supabase.from('quiz_questions').select('*').in('id', currentRoundData.questions);
            const orderedQuestions = currentRoundData.questions.map(id => data.find(q => q.id === id));
            roundQuestions = shuffleQuestionsData(orderedQuestions);
        } else {
            const count = roundNum === 5 ? 3 : 5;
            const usedIds = Object.values(roundsData).flatMap(r => r.questions || []);
            const rawQuestions = await fetchRandomQuestions(count, {}, usedIds);
            if(rawQuestions.length < count) { alert("Database domande insufficiente."); setLoading(false); return; }
            const qIds = rawQuestions.map(q => q.id);
            roundsData[roundNum] = { ...roundsData[roundNum], questions: qIds };
            await updateChallengeRound(freshChallenge.id, roundsData);
            roundQuestions = shuffleQuestionsData(rawQuestions);
        }
        
        const t = roundNum === 5 ? 20 : 30; 
        setTimeLeft(t);
        setTotalTime(t);

        setQuestions(roundQuestions);
        setChallengeRoundData({ ...roundsData });
        setSetupMode(false);
        setLoading(false);
    };

    const handleAcceptChallenge = async (c) => { await supabase.from('quiz_challenges').update({ status: 'active' }).eq('id', c.id); loadChallenges(); };
    const handleRejectChallenge = async (c) => { if(!window.confirm("Rifiutare la sfida?")) return; await supabase.from('quiz_challenges').update({ status: 'rejected' }).eq('id', c.id); loadChallenges(); };
    const handleAnswer = (index) => {
        if (isAnswered) return;
        setSelectedOption(index);
        setIsAnswered(true);
        const q = questions[currentStep];
        const correct = index !== -1 && index === q.correct_index;
        if (correct) setScore(s => s + 1);
        // Ogni risposta data in allenamento o ripasso alimenta la coda.
        if (mode === 'training' && q?.id) {
            answersRef.current.push({ question_id: q.id, correct });
        }
    };
    
    const reportQuestion = async () => {
        if (!window.confirm("Vuoi segnalare questa domanda?")) return;
        const qId = questions[currentStep].id;
        await supabase.from('quiz_reports').insert([{ question_id: qId, user_id: user.id, reason: "Segnalata in-app", status: 'pending' }]);
        alert("Segnalata! Grazie per il contributo.");
    };
    
    const nextQuestion = () => { if (currentStep < questions.length - 1) { setCurrentStep(s => s + 1); setSelectedOption(null); setIsAnswered(false); } else { finishSession(); } };
    
    const finishSession = async (forceFinish = false) => {
        setShowResult(true);
        // `score` è già aggiornato da handleAnswer, ultima risposta compresa.
        // Prima veniva sommato +1 anche qui: il punteggio salvato a DB (e usato
        // per decidere il vincitore dei round PvP) risultava gonfiato di 1
        // ogni volta che l'ultima risposta era corretta.
        const finalTotalScore = score;

        if (mode === 'training') {
            // Prima la coda di ripasso: un errore appena fatto deve tornare,
            // una risposta giusta su una domanda in coda la fa avanzare.
            await recordReviewResults(answersRef.current);
            answersRef.current = [];
            loadReviewCount();

            await supabase.from('quiz_attempts').insert([{ user_id: user.id, score: finalTotalScore, total_questions: questions.length }]);
            // Un'unica fonte di verità per la streak. Prima subito dopo veniva
            // chiamata anche la RPC `recalc_quiz_streak`, che riscriveva il
            // valore appena salvato mentre la UI mostrava quello calcolato qui:
            // il numero a schermo e quello a DB potevano divergere.
            if (!isReviewSession) {
                const streakRes = await checkAndIncrementStreak(user.id, user.streak_count, user.last_quiz_date);
                if (streakRes) {
                    setStreakInfo(streakRes);
                    // Solo quando il contatore avanza davvero: rivedere
                    // l'animazione al secondo quiz del giorno sarebbe rumore.
                    if (!streakRes.alreadyPlayed) setCelebration(streakRes);
                    if (onUpdateUserStreak) onUpdateUserStreak(streakRes);
                }
            }
        
        } else if (mode === 'challenges' && activeChallenge) {
            const { data: freshData } = await supabase.from('quiz_challenges').select('rounds_data, p1_sets_won, p2_sets_won').eq('id', activeChallenge.id).single();
            if (!freshData) return;

            const roundNum = activeChallenge.current_round;
            const isP1 = user.id === activeChallenge.challenger_id;
            const updatedRounds = freshData.rounds_data || {};
            if (!updatedRounds[roundNum]) updatedRounds[roundNum] = {};
            updatedRounds[roundNum][isP1 ? 'p1_score' : 'p2_score'] = finalTotalScore;
            
            const p1S = updatedRounds[roundNum].p1_score;
            const p2S = updatedRounds[roundNum].p2_score;
            let p1WinsTotal = freshData.p1_sets_won;
            let p2WinsTotal = freshData.p2_sets_won;
            let nextRound = activeChallenge.current_round;
            let matchWinner = null;

            if (p1S !== undefined && p2S !== undefined) {
                if (p1S > p2S) p1WinsTotal++; else if (p2S > p1S) p2WinsTotal++;
                if (p1WinsTotal >= 3) matchWinner = activeChallenge.challenger_id; else if (p2WinsTotal >= 3) matchWinner = activeChallenge.opponent_id; else if (roundNum >= 5) { if (p1S > p2S) matchWinner = activeChallenge.challenger_id; else matchWinner = activeChallenge.opponent_id; } else nextRound++;
                await updateScores(activeChallenge.id, p1WinsTotal, p2WinsTotal, nextRound, matchWinner);

                if (matchWinner) {
                    const isMeWinner = matchWinner === user.id;
                    setGameOutcome(isMeWinner ? 'win' : 'loss');
                    const loserId = matchWinner === activeChallenge.challenger_id ? activeChallenge.opponent_id : activeChallenge.challenger_id;
                    await sendChallengeNotification(loserId, "🏁 Sfida Terminata", `${user.full_name} ha vinto la sfida ${p1WinsTotal}-${p2WinsTotal}.`);
                    if (!isMeWinner) await sendChallengeNotification(matchWinner, "🏆 Vittoria!", `Hai battuto ${user.full_name}!`);
                } else {
                    setGameOutcome('round');
                    const opponentId = user.id === activeChallenge.challenger_id ? activeChallenge.opponent_id : activeChallenge.challenger_id;
                    await sendChallengeNotification(opponentId, "🔔 Round Completato", `${user.full_name} ha completato il round ${roundNum}. Controlla il risultato!`);
                }
            } else {
                setGameOutcome('waiting');
                const opponentId = user.id === activeChallenge.challenger_id ? activeChallenge.opponent_id : activeChallenge.challenger_id;
                await sendChallengeNotification(opponentId, "⏳ Tocca a te!", `${user.full_name} ha giocato il round ${roundNum}. Ora sta a te!`);
            }
            await updateChallengeRound(activeChallenge.id, updatedRounds);
            loadChallenges();
        }
    };

    const reset = () => {
        setSetupMode(true); setShowResult(false); setScore(0); setCurrentStep(0); setQuestions([]); setSelectedOption(null); setIsAnswered(false);
        setActiveChallenge(null); setChallengeRoundData(null); setGameOutcome(null); setStreakInfo(null);
        setIsReviewSession(false);
        if (mode === 'challenges') loadChallenges();
    };

    const activeChallengesList = challenges.filter(c => c.status === 'pending' || c.status === 'active');
    const pastChallengesList = challenges.filter(c => c.status === 'finished' || c.status === 'rejected');

    return (
        <div className="h-full flex flex-col gap-4 animate-fadeIn relative">
            {celebration && <StreakCelebration result={celebration} onClose={() => setCelebration(null)} />}
            {showRulesModal && (
                <div className="fixed inset-0 bg-black/60 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="bg-indigo-600 p-4 text-white flex justify-between items-center">
                            <h3 className="font-bold flex items-center gap-2"><BookOpen className="h-5 w-5"/> Regolamento Sfida</h3>
                            <button onClick={() => setShowRulesModal(false)}><X className="h-5 w-5 hover:text-indigo-200"/></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100"><Trophy className="h-6 w-6 text-indigo-600 shrink-0 mt-1"/><div><h4 className="font-bold text-indigo-900 text-sm">Al Meglio dei 5 Set</h4><p className="text-xs text-indigo-700">Vince chi si aggiudica per primo 3 round.</p></div></div>
                            <ul className="space-y-3 text-sm text-slate-600"><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500"/> <strong>Round 1-4:</strong> 5 domande a testa.</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500"/> <strong>Round 5 (TieBreak):</strong> 3 domande secche.</li><li className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-500"/> <strong>Tempo:</strong> 30s (20s TieBreak) a domanda.</li></ul>
                            <div className="pt-2 border-t border-slate-100"><button onClick={handleProceedToSearch} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2"><Search className="h-4 w-4"/> Cerca Avversario</button></div>
                        </div>
                    </div>
                </div>
            )}

            <div className="rp-card p-3 rounded-2xl flex justify-between items-center shrink-0">
                {setupMode || showResult ? (
                    <div className="flex gap-2 w-full">
                        <button onClick={() => { setMode('training'); reset(); }} className={`flex-1 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${mode === 'training' ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' : 'text-slate-500 hover:bg-slate-50'}`}><GraduationCap className="h-5 w-5"/> Allenamento</button>
                        <button onClick={() => { setMode('challenges'); reset(); }} className={`flex-1 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${mode === 'challenges' ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'text-slate-500 hover:bg-slate-50'}`}><Swords className="h-5 w-5"/> PvP</button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-lg font-bold text-slate-800 leading-none">{mode === 'challenges' ? `Round ${activeChallenge.current_round}` : isReviewSession ? 'Ripasso errori' : 'Allenamento'}</h2>
                                {mode === 'challenges' && (
                                    <span className={`text-xs font-black px-2 py-0.5 rounded flex items-center gap-1 ${timeLeft <= 5 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-slate-100 text-slate-600'}`}>
                                        <Clock className="h-3 w-3"/> {timeLeft}s
                                    </span>
                                )}
                            </div>
                        </div>
                        <button onClick={reportQuestion} className="text-slate-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors" title="Segnala domanda"><Flag className="h-5 w-5"/></button>
                    </>
                )}
            </div>

            <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative flex flex-col">
                {!setupMode && !showResult && mode === 'challenges' && (
                    <div className="h-1.5 w-full bg-slate-100"><div className={`h-full transition-all duration-1000 ease-linear ${timeLeft <= 10 ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${(timeLeft / totalTime) * 100}%` }}></div></div>
                )}

                <div className="flex-1 overflow-y-auto p-6 pb-24 scroll-smooth">
                    {loading ? <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin h-10 w-10 text-slate-400"/></div> : setupMode ? (
                        mode === 'training' ? (
                            <div className="max-w-lg mx-auto space-y-6">
                                {reviewDue > 0 && (
                                    <button
                                        onClick={startReview}
                                        className="w-full text-left p-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-amber-300 transition-colors flex items-center gap-4"
                                    >
                                        <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl shrink-0"><Repeat className="h-6 w-6"/></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-amber-900">Ripassa i tuoi errori</p>
                                            <p className="text-xs text-amber-700 mt-0.5">
                                                {reviewDue === 1
                                                    ? "1 domanda che avevi sbagliato è pronta per tornare."
                                                    : `${reviewDue} domande che avevi sbagliato sono pronte per tornare.`}
                                            </p>
                                        </div>
                                        <ChevronRight className="h-5 w-5 text-amber-400 shrink-0"/>
                                    </button>
                                )}
                                <div className="grid grid-cols-1 gap-2"><button onClick={() => toggleSource('Tutti')} className={`p-3 rounded-xl border text-left text-sm font-medium flex justify-between ${selectedSources.includes('Tutti') ? 'border-orange-500 bg-orange-50 text-orange-900' : 'border-slate-200'}`}><span>Tutte</span>{selectedSources.includes('Tutti') && <CheckSquare className="h-4 w-4"/>}</button>{DOCUMENTS.map(doc => (<button key={doc} onClick={() => toggleSource(doc)} className={`p-3 rounded-xl border text-left text-sm font-medium flex justify-between ${selectedSources.includes(doc) ? 'border-orange-500 bg-orange-50 text-orange-900' : 'border-slate-200'}`}><span>{doc}</span>{selectedSources.includes(doc) && <CheckSquare className="h-4 w-4"/>}</button>))}</div>
                                <div className="grid grid-cols-2 gap-4"><div><label className="text-xs font-bold text-slate-500 uppercase">Difficoltà</label><select className="w-full border p-2 rounded-lg bg-slate-50" value={selectedDifficulty} onChange={e => setSelectedDifficulty(e.target.value)}>{DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}</select></div><div><label className="text-xs font-bold text-slate-500 uppercase">Domande</label><select className="w-full border p-2 rounded-lg bg-slate-50" value={questionCount} onChange={e => setQuestionCount(parseInt(e.target.value))}>{COUNTS.map(c => <option key={c} value={c}>{c}</option>)}</select></div></div>
                                <button onClick={startTraining} className="w-full py-4 bg-orange-600 text-white rounded-xl font-bold shadow-md">Inizia</button>
                            </div>
                        ) : (
                            <div className="max-w-xl mx-auto space-y-4">
                                <div className="text-center pb-4 border-b border-slate-100"><button onClick={handleOpenCreateFlow} className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-indigo-700 flex items-center gap-2 mx-auto"><Target className="h-5 w-5"/> Nuova Sfida</button></div>
                                {activeChallengesList.length === 0 && <p className="text-center text-slate-400 italic py-10">Nessuna sfida attiva.</p>}
                                {activeChallengesList.map(c => {
                                    const isMeChallenger = c.challenger_id === user.id; const opponent = isMeChallenger ? c.opponent : c.challenger; const myScore = isMeChallenger ? c.p1_sets_won : c.p2_sets_won; const oppScore = isMeChallenger ? c.p2_sets_won : c.p1_sets_won;
                                    const rData = c.rounds_data ? c.rounds_data[c.current_round] : {}; const haveIPlayed = rData ? (isMeChallenger ? rData.p1_score !== undefined : rData.p2_score !== undefined) : false; const waitingOpponent = rData && haveIPlayed && (isMeChallenger ? rData.p2_score === undefined : rData.p1_score === undefined);
                                    return (
                                        <div key={c.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 relative overflow-hidden">
                                            <div className="flex items-center justify-between mb-3"><div className="flex items-center gap-3"><div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white ${c.status === 'finished' && c.winner_id === user.id ? 'bg-green-500' : 'bg-indigo-500'}`}>{myScore}</div><div className="text-xs font-bold text-slate-400 uppercase">VS</div><div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white ${c.status === 'finished' && c.winner_id === opponent.id ? 'bg-green-500' : 'bg-slate-400'}`}>{oppScore}</div></div><div className="text-right"><p className="font-bold text-slate-800">{opponent?.full_name}</p><p className="text-xs text-slate-500">{c.status === 'pending' ? 'In attesa' : c.status === 'active' ? `Round ${c.current_round}/5` : 'Conclusa'}</p></div></div>
                                            {c.status === 'pending' && !isMeChallenger && (<div className="flex gap-2"><button onClick={() => handleAcceptChallenge(c)} className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold text-xs">Accetta</button><button onClick={() => handleRejectChallenge(c)} className="flex-1 bg-red-100 text-red-600 py-2 rounded-lg font-bold text-xs">Rifiuta</button></div>)}
                                            {c.status === 'pending' && isMeChallenger && <p className="text-xs text-center text-slate-400 bg-white p-2 rounded border border-slate-100">In attesa che {opponent?.full_name} accetti...</p>}
                                            {c.status === 'active' && (waitingOpponent ? (<div className="bg-yellow-50 text-yellow-800 p-2 rounded text-xs font-bold text-center border border-yellow-100">Hai giocato! In attesa dell'avversario...</div>) : haveIPlayed ? (<div className="bg-slate-200 text-slate-600 p-2 rounded text-xs font-bold text-center">Round Completato</div>) : (<button onClick={() => handlePlayRound(c)} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold shadow-md hover:bg-indigo-700 animate-pulse">Gioca Round {c.current_round} {c.current_round === 5 && '(TieBreak!)'}</button>))}
                                        </div>
                                    );
                                })}

                                {pastChallengesList.length > 0 && (
                                    <div className="mt-8 pt-6 border-t border-slate-100">
                                        <button onClick={() => setShowPastChallenges(!showPastChallenges)} className="w-full flex items-center justify-between text-slate-500 font-bold text-sm mb-4 hover:text-slate-700"><span>Archivio Sfide</span>{showPastChallenges ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}</button>
                                        {showPastChallenges && pastChallengesList.map(c => {
                                            const isMeChallenger = c.challenger_id === user.id; const opponent = isMeChallenger ? c.opponent : c.challenger; const myScore = isMeChallenger ? c.p1_sets_won : c.p2_sets_won; const oppScore = isMeChallenger ? c.p2_sets_won : c.p1_sets_won; const isWinner = c.winner_id === user.id;
                                            return (
                                                <div key={c.id} className="bg-slate-50 border border-slate-100 rounded-xl p-3 mb-2 opacity-75 hover:opacity-100 transition-opacity">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-2"><div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white ${isWinner ? 'bg-green-500' : 'bg-red-400'}`}>{myScore}</div><span className="text-xs text-slate-400">vs</span><div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white ${c.winner_id === opponent.id ? 'bg-green-500' : 'bg-slate-400'}`}>{oppScore}</div><span className="text-sm font-bold text-slate-700 ml-2">{opponent?.full_name}</span></div>
                                                        <div className={`text-[10px] font-black px-2 py-1 rounded ${isWinner ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{isWinner ? 'VITTORIA' : c.status === 'rejected' ? 'RIFIUTATA' : 'SCONFITTA'}</div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    ) : !showResult ? (
                        <div className="max-w-2xl mx-auto">
                            {mode === 'training' && (
                                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-6"><div className="bg-orange-500 h-full transition-all duration-500" style={{ width: `${((currentStep) / questions.length) * 100}%` }}></div></div>
                            )}
                            <div className="mb-4">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-1 rounded uppercase tracking-wider border border-slate-200">{questions[currentStep]?.source_doc}</span>
                                    <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider border ${questions[currentStep]?.difficulty === 'Difficile' ? 'bg-red-50 text-red-700 border-red-100' : questions[currentStep]?.difficulty === 'Medio' ? 'bg-orange-50 text-orange-700 border-orange-100' : 'bg-green-50 text-green-700 border-green-100'}`}>{questions[currentStep]?.difficulty}</span>
                                    {questions[currentStep]?.report_count > 5 && (<span className="text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider border bg-amber-100 text-amber-700 border-amber-200 flex items-center gap-1"><AlertTriangle className="h-3 w-3"/> Segnalata</span>)}
                                </div>
                                <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wide mb-1">{questions[currentStep]?.category || "Tecnica"}</h2>
                                <h3 className="text-xl md:text-2xl font-black text-slate-800 leading-snug">{questions[currentStep]?.question}</h3>
                            </div>
                            <div className="space-y-3 mb-6">{questions[currentStep]?.options.map((opt, idx) => { let cls = "w-full p-4 text-left rounded-xl border-2 font-medium transition-all transform active:scale-[0.99] "; if(isAnswered) { if(idx === questions[currentStep].correct_index) cls += "bg-green-50 border-green-500 text-green-800 shadow-sm"; else if(idx === selectedOption) cls += "bg-red-50 border-red-500 text-red-800 shadow-sm"; else cls += "opacity-40 border-slate-100 grayscale"; } else cls += "bg-white border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 hover:shadow-md text-slate-700"; return <button key={idx} disabled={isAnswered} onClick={() => handleAnswer(idx)} className={cls}>{opt}</button> })}</div>
                            {isAnswered && (<div className="animate-slide-in mt-6"><div className={`p-5 rounded-xl border-l-4 shadow-sm mb-4 text-sm leading-relaxed ${questions[currentStep].correct_index === selectedOption ? 'bg-green-50 border-green-500 border-l-green-500 text-green-900' : 'bg-red-50 border-red-500 border-l-red-500 text-red-900'}`}><strong className="block mb-1 flex items-center gap-2 uppercase text-xs tracking-wider opacity-80"><Info className="h-4 w-4"/> Spiegazione Tecnica</strong>{questions[currentStep]?.explanation || "Nessuna spiegazione disponibile."}</div><button onClick={nextQuestion} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold shadow-lg hover:bg-slate-800 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2">{currentStep < questions.length - 1 ? "Prossima Domanda" : "Vedi Risultati"} <ArrowLeft className="h-5 w-5 rotate-180"/></button></div>)}
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center p-4 animate-fadeIn">
                             {mode === 'challenges' && gameOutcome === 'win' && (<div className="mb-6 animate-bounce-in"><Trophy className="h-24 w-24 text-yellow-400 drop-shadow-lg mx-auto mb-2"/><h2 className="text-4xl font-black text-slate-800">VITTORIA!</h2><p className="text-slate-500 font-medium">Hai battuto il tuo avversario!</p></div>)}
                             {mode === 'challenges' && gameOutcome === 'loss' && (<div className="mb-6 animate-shake"><XCircle className="h-20 w-20 text-red-400 mx-auto mb-2"/><h2 className="text-4xl font-black text-slate-800">SCONFITTA</h2><p className="text-slate-500 font-medium">Sarà per la prossima volta...</p></div>)}
                             {mode === 'challenges' && gameOutcome === 'round' && (<div className="mb-6"><CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-2"/><h2 className="text-3xl font-bold text-slate-800">Round Completato!</h2></div>)}
                             {mode === 'challenges' && gameOutcome === 'waiting' && (<div className="mb-6 animate-pulse"><Clock className="h-16 w-16 text-yellow-500 mx-auto mb-2"/><h2 className="text-2xl font-bold text-slate-800">In attesa...</h2><p className="text-xs text-slate-400">Ti arriverà una notifica quando avrà finito.</p></div>)}

                             {mode === 'training' && (<div className="w-24 h-24 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"><Award className="h-12 w-12 text-orange-500"/></div>)}
                             {mode === 'training' && <h2 className="text-3xl font-black text-slate-800 mb-2">{isReviewSession ? 'Ripasso Finito!' : 'Allenamento Finito!'}</h2>}
                             {mode === 'training' && isReviewSession && (
                                <p className="text-sm text-slate-500 -mt-1 mb-2 max-w-sm">
                                    Le domande sbagliate torneranno fra qualche giorno. Tre risposte giuste di fila e escono dalla coda.
                                </p>
                             )}

                             <div className="my-6 py-6 px-12 bg-white rounded-3xl border border-slate-200 shadow-xl"><span className="text-6xl font-black text-indigo-600">{score}</span><span className="text-2xl text-slate-400 font-bold"> / {questions.length}</span></div>

                             {/* Esito streak: prima il quiz aggiornava il contatore in
                                 silenzio e non si capiva se la streak fosse salita. */}
                             {mode === 'training' && streakInfo && (
                                <div className={`mb-6 w-full max-w-xs mx-auto px-4 py-3 rounded-2xl border flex items-center justify-center gap-2.5 animate-bounce-in ${streakInfo.alreadyPlayed ? 'bg-slate-50 border-slate-200' : 'bg-orange-50 border-orange-200'}`}>
                                    <Flame className={`h-6 w-6 ${streakInfo.alreadyPlayed ? 'text-slate-300' : 'text-orange-500 fill-orange-400'}`}/>
                                    <div className="text-left">
                                        <p className={`font-black leading-none ${streakInfo.alreadyPlayed ? 'text-slate-600' : 'text-orange-700'}`}>
                                            {streakInfo.streak} {streakInfo.streak === 1 ? 'giorno' : 'giorni'} di streak
                                        </p>
                                        <p className="text-[11px] text-slate-500 mt-1 leading-tight">
                                            {streakInfo.alreadyPlayed
                                                ? 'Avevi già completato il quiz di oggi.'
                                                : streakInfo.streak === 1
                                                    ? 'Hai iniziato una nuova striscia. Torna domani!'
                                                    : 'Striscia allungata. Torna domani per continuare!'}
                                        </p>
                                    </div>
                                </div>
                             )}
                            <button onClick={reset} className="w-full max-w-xs py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-transform transform hover:scale-105 shadow-lg">Torna al Menu</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// --- FEEDBACK INVIO ---
const FeedbackSubmitModal = ({ user, onClose }) => {
    const [type, setType] = useState('bug'); // 'bug' o 'feature'
    const [form, setForm] = useState({ title: '', description: '' });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setLoading(true);
        
        const { error } = await supabase.from('feedback').insert([{
            user_id: user.id,
            type,
            title: form.title,
            description: form.description
        }]);

        setLoading(false);
        if (error) {
            alert("Errore invio: " + error.message);
        } else {
            alert("Grazie! Il tuo feedback è stato inviato.");
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4 animate-fadeIn backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="bg-slate-900 p-4 text-white flex justify-between items-center">
                    <h3 className="font-bold flex items-center gap-2">
                        {type === 'bug' ? <Bug className="h-5 w-5 text-red-400"/> : <Lightbulb className="h-5 w-5 text-yellow-400"/>}
                        Invia Feedback
                    </h3>
                    <button onClick={onClose}><X className="h-5 w-5 hover:text-slate-300"/></button>
                </div>
                <div className="p-4">
                    <div className="flex bg-slate-100 p-1 rounded-lg mb-4">
                        <button onClick={() => setType('bug')} className={`flex-1 py-1.5 text-xs font-bold rounded flex items-center justify-center gap-2 ${type === 'bug' ? 'bg-white shadow text-red-600' : 'text-slate-500'}`}>
                            <Bug className="h-3 w-3"/> Segnala Bug
                        </button>
                        <button onClick={() => setType('feature')} className={`flex-1 py-1.5 text-xs font-bold rounded flex items-center justify-center gap-2 ${type === 'feature' ? 'bg-white shadow text-yellow-600' : 'text-slate-500'}`}>
                            <Lightbulb className="h-3 w-3"/> Suggerisci Feature
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase">Titolo</label>
                            <input required className="w-full border p-2 rounded text-sm" placeholder={type === 'bug' ? "Es. Errore nel login" : "Es. Modalità scura"} value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase">Descrizione</label>
                            <textarea className="w-full border p-2 rounded text-sm h-24" placeholder="Descrivi meglio..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                        </div>
                        <button type="submit" disabled={loading} className="w-full py-2 bg-slate-800 hover:bg-slate-900 text-white rounded font-bold text-sm flex justify-center items-center gap-2">
                            {loading ? <Loader2 className="animate-spin h-4 w-4"/> : <Send className="h-4 w-4"/>} Invia
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// --- FEEDBACK SUPERADMIN VIEW ---

const FeedbackAdminView = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, bug, feature

    useEffect(() => {
        loadFeedback();
    }, []);

    const loadFeedback = async () => {
        setLoading(true);
        // Scarica feedback e chi lo ha mandato
        const { data, error } = await supabase
            .from('feedback')
            .select('*, profiles(full_name)')
            .order('created_at', { ascending: false });
        
        if (!error) setFeedbacks(data);
        setLoading(false);
    };

    const filtered = feedbacks.filter(f => filter === 'all' || f.type === filter);

    return (
        <div className="h-[calc(100vh-140px)] flex flex-col gap-4">
            <div className="rp-card p-4 rounded-2xl flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><ClipboardList className="h-6 w-6 text-orange-500"/> Feedback Hub</h2>
                    <p className="text-sm text-slate-500">Gestione segnalazioni e idee.</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                    <button onClick={() => setFilter('all')} className={`px-3 py-1 text-xs font-bold rounded ${filter === 'all' ? 'bg-white shadow' : 'text-slate-500'}`}>Tutti</button>
                    <button onClick={() => setFilter('bug')} className={`px-3 py-1 text-xs font-bold rounded ${filter === 'bug' ? 'bg-white shadow text-red-600' : 'text-slate-500'}`}>Bug</button>
                    <button onClick={() => setFilter('feature')} className={`px-3 py-1 text-xs font-bold rounded ${filter === 'feature' ? 'bg-white shadow text-yellow-600' : 'text-slate-500'}`}>Feature</button>
                </div>
            </div>

            <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 overflow-y-auto p-4 space-y-3">
                {loading && <div className="text-center p-4"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400"/></div>}
                {!loading && filtered.length === 0 && <p className="text-center text-slate-400 italic mt-10">Nessun feedback trovato.</p>}
                
                {filtered.map(item => (
                    <div key={item.id} className={`bg-white p-3 rounded-lg border border-l-4 shadow-sm ${item.type === 'bug' ? 'border-l-red-500' : 'border-l-yellow-500'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded mr-2 ${item.type === 'bug' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {item.type === 'bug' ? 'BUG' : 'FEAT'} #{item.id}
                                </span>
                                <span className="text-xs text-slate-400">{new Date(item.created_at).toLocaleDateString()} • {item.profiles?.full_name || 'Utente'}</span>
                                <h4 className="font-bold text-slate-800 mt-1">{item.title}</h4>
                                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{item.description}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- NAVIGATION ---
const Navigation = ({ 
  activeTab, 
  setActiveTab, 
  user, 
  onLogout, 
  onOpenProfile, 
  onOpenSearch, 
  notifications, 
  avatarUrl, 
  onMarkAllRead, 
  onClearNotifications, 
  onOpenSettings, 
  onNotificationClick, 
}) => {
  const [showNotif, setShowNotif] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Prima aprire la campanella marcava subito tutto come letto: l'evidenza
  // "non letto" spariva ancora prima che l'elenco fosse visibile. Ora le
  // notifiche restano evidenziate finché il pannello è aperto e vengono
  // marcate lette alla chiusura.
  const closeNotif = () => {
      setShowNotif(false);
      if (unreadCount > 0) onMarkAllRead();
  };
  const toggleNotif = () => { if (showNotif) closeNotif(); else setShowNotif(true); };

  const navItems = [
    { id: 'dashboard', label: 'Home', icon: Award },
    { id: 'quiz', label: 'Quiz', icon: GraduationCap }, 
    { id: 'forum', label: 'Arena', icon: MessageSquare },
    { id: 'designations', label: 'Gare', icon: Calendar },
    { id: 'diary', label: 'Diario', icon: NotebookPen },
    { id: 'meetups', label: 'Terzo Tempo', icon: Beer }, 
  ];

  if (user?.is_super_admin) { 
    navItems.push({ id: 'feedback_hub', label: 'Feedback', icon: ClipboardList }); 
  }

  // Se sono Quiz Manager O Super Admin -> Vedo gestione quiz
  if (user?.is_quiz_manager || user?.is_super_admin) {
      navItems.push({ id: 'quiz_admin', label: 'Gestione Quiz', icon: BookOpen });
  }

  return (
    <>
      {/* DESKTOP / TOP NAVBAR */}
      <nav className="rp-nav text-white fixed inset-x-0 top-0 z-[950] border-b md:top-3 md:left-4 md:right-4 md:mx-auto md:max-w-7xl md:border md:rounded-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* LOGO */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveTab('dashboard')}>
              <div className="w-10 h-10 shadow-lg shadow-orange-500/20 rounded-xl overflow-hidden">
                 <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 64 64" fill="none">
                  <defs>
                    <linearGradient id="orangeGradientNav" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fb923c" stopOpacity="1" />
                        <stop offset="100%" stopColor="#c2410c" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <rect width="64" height="64" rx="16" fill="url(#orangeGradientNav)"/>
                  <g transform="translate(12, 12) scale(1.66)" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <g transform="translate(-2, 2)">
                        <path d="M14 14H18.5a1.5 1.5 0 0 0 1.5-1.5v-1a1.5 1.5 0 0 0-1.5-1.5H15"/>
                        <path d="M15 10a5 5 0 1 0-6 5"/>
                        <circle cx="9" cy="10" r="1.5"/>
                        <path d="M11 5V3a1.5 1.5 0 0 0-3 0v2"/>
                    </g>
                    <path d="M21 8l2-2"/>
                    <path d="M21 12h2"/>
                    <path d="M21 16l2 2"/>
                  </g>
                </svg>
              </div>
              <span className="font-bold text-xl tracking-tight hidden xs:block">RefPublic</span>
            </div>

            {/* DESKTOP ITEMS */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navItems.map((item) => (
                  <button 
                    key={item.id} 
                    onClick={() => setActiveTab(item.id)} 
                    className={`rp-tab px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 
                      ${activeTab === item.id 
                        ? 'rp-tab-active text-white' 
                        : 'text-blue-50/80 hover:text-white'}`}
                  >
                    <item.icon className="h-4 w-4" /> {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ACTIONS RIGHT */}
            <div className="flex items-center gap-4 relative">
              
              <button onClick={onOpenSettings} className="text-blue-50/80 hover:text-white transition-colors p-1" title="Impostazioni"><Settings className="h-6 w-6"/></button>

              <button onClick={toggleNotif} className="text-blue-50/80 hover:text-white transition-colors relative p-1" aria-label={unreadCount > 0 ? `Notifiche, ${unreadCount} non lette` : "Notifiche"}>
                 <Bell className="h-6 w-6"/>
                 {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold leading-[18px] text-center rounded-full ring-2 ring-white/60 shadow-sm">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                 )}
              </button>

              {showNotif && (<><div className="fixed inset-0 z-[999]" onClick={closeNotif}></div><NotificationDropdown notifications={notifications} onClose={closeNotif} onMarkAllRead={onMarkAllRead} onClear={onClearNotifications} onNotificationClick={onNotificationClick}/></>)}

              <button onClick={onOpenSearch} className="text-blue-50/80 hover:text-white transition-colors p-1" title="Cerca Utenti"><Search className="h-6 w-6"/></button>
              
              <button className="flex items-center gap-3 cursor-pointer group outline-none" onClick={() => onOpenProfile(user)}>
                <Avatar name={user.full_name} src={avatarUrl} size="sm" className="bg-slate-700 text-white border-2 border-slate-700 group-hover:border-slate-500 transition-colors" />
              </button>

              <button onClick={onLogout} className="text-blue-50/70 hover:text-red-300 transition-colors ml-1 p-1" title="Esci"><LogOut className="h-5 w-5" /></button>
            </div>
          </div>
        </div>
      </nav>

      {/* MOBILE BOTTOM NAV */}
      <div className="md:hidden fixed bottom-4 left-4 right-4 z-[900]">
        <div className="rp-nav border rounded-2xl flex justify-between items-center h-16 px-3 safe-pb overflow-x-auto custom-scrollbar">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button 
                key={item.id} 
                onClick={() => setActiveTab(item.id)} 
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                title={item.label}
                className={`relative flex-shrink-0 flex items-center justify-center h-full transition-all duration-300 group px-2 min-w-[50px]`}
              >
                {isActive && (
                  <div className="absolute top-1 w-8 h-1 bg-orange-400 rounded-full shadow-[0_0_10px_rgba(249,115,22,0.9)]"></div>
                )}
                <div className={`p-1.5 rounded-xl transition-all duration-300 ${isActive ? 'text-white -translate-y-0.5 drop-shadow' : 'text-blue-50/70 group-hover:text-white'}`}>
                  <item.icon className={`h-6 w-6 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

const LoginScreen = () => {
  const [authMode, setAuthMode] = useState('password'); // 'password' | 'magic'
  const [authAction, setAuthAction] = useState('login'); // 'login' | 'register'
  
  // Stati Email/Password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // Stati Magic Link
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  // --- HANDLERS ---

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setAuthError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) setAuthError("Errore accesso: " + error.message);
};

const handleRegister = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    setAuthError('');

    if (!email || !password || !password2) {
        setAuthError("Compila tutti i campi.");
        return;
    }
    const pwdProblem = checkPasswordPolicy(password);
    if (pwdProblem) {
        setAuthError(pwdProblem);
        return;
    }
    if (password !== password2) {
        setAuthError("Le password non coincidono.");
        return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin }
    });
    setLoading(false);

    if (error) {
        const msg = (error?.message || "").toLowerCase();
        if (
            msg.includes('already registered') ||
            msg.includes('user already registered') ||
            msg.includes('already exists') ||
            msg.includes('email already') ||
            msg.includes('email address already') ||
            msg.includes('duplicate key value')
        ) {
            setAuthError("Questa email è già registrata. Accedi oppure usa “Password dimenticata”.");
        } else {
            setAuthError("Errore registrazione: " + error.message);
        }
    } else {
        setAuthError("Se non eri ancora registrato, riceverai una email per completare la registrazione. Controlla la tua casella!");
    }
};

  const handleMagicLogin = async (e) => {
      e.preventDefault();
      if (!magicEmail) return;
      setMagicLoading(true);
      const error = await signInWithMagicLink(magicEmail);
      setMagicLoading(false);
      
      if (error) {
          alert("Errore invio link: " + error.message);
      } else {
          setMagicSent(true);
      }
  };

  const Logo = () => (
    <div className="w-16 h-16 shadow-lg shadow-orange-500/20 rounded-xl overflow-hidden mb-6 mx-auto">
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
            <linearGradient id="orangeGradientLogin" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fb923c" stopOpacity="1" />
                <stop offset="100%" stopColor="#c2410c" stopOpacity="1" />
            </linearGradient>
        </defs>
        <rect width="64" height="64" rx="16" fill="url(#orangeGradientLogin)"/>
        <g transform="translate(12, 12) scale(1.66)" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <g transform="translate(-2, 2)">
                <path d="M14 14H18.5a1.5 1.5 0 0 0 1.5-1.5v-1a1.5 1.5 0 0 0-1.5-1.5H15"/>
                <path d="M15 10a5 5 0 1 0-6 5"/>
                <circle cx="9" cy="10" r="1.5"/>
                <path d="M11 5V3a1.5 1.5 0 0 0-3 0v2"/>
            </g>
            <path d="M21 8l2-2"/>
            <path d="M21 12h2"/>
            <path d="M21 16l2 2"/>
        </g>
        </svg>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 relative overflow-hidden">
        {/* Sfondo decorativo */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] bg-gradient-to-br from-orange-200/30 to-slate-200/30 rounded-full blur-[120px]"></div>
             <div className="absolute top-[60%] -left-[10%] w-[50%] h-[50%] bg-gradient-to-tr from-blue-200/20 to-slate-200/20 rounded-full blur-[100px]"></div>
        </div>

        <div className="bg-white/70 backdrop-blur-xl p-8 md:p-12 rounded-3xl shadow-2xl border border-white/50 w-full max-w-md relative z-10 flex flex-col items-center animate-fadeIn">
          <Logo />
          <div className="text-center mb-6">
             <h1 className="text-3xl font-black text-slate-900 mb-2">RefPublic</h1>
             <p className="text-slate-500">La casa per gli arbitri di pallavolo.</p>
          </div>

          <div className="w-full space-y-5">
            
            {/* 1. GOOGLE LOGIN (Sempre visibile come opzione primaria) */}
            {supabase ? (
                <div className="bg-white p-1 rounded-2xl shadow-sm border border-slate-100">
                    <Auth 
                        supabaseClient={supabase} 
                        appearance={{ 
                            theme: ThemeSupa, 
                            variables: { 
                                default: { 
                                    colors: { 
                                        brand: '#0f172a', 
                                        brandAccent: '#ea580c', 
                                        inputBorder: '#e2e8f0', 
                                        inputText: '#1e293b' 
                                    },
                                    radii: { borderRadiusButton: '12px' },
                                    space: { buttonPadding: '12px' }
                                } 
                            },
                            className: { button: 'shadow-none font-bold' }
                        }}
                        localization={{
                            variables: {
                                sign_in: {
                                    social_provider_text: 'Accedi con {{provider}}'
                                }
                            }
                        }}
                        theme="default"
                        providers={['google']}
                        onlyThirdPartyProviders={true} 
                        redirectTo={window.location.origin} 
                    />


                </div>
            ) : (
                <div className="text-center text-red-500 text-sm font-bold bg-red-50 p-4 rounded-xl border border-red-200">
                    ERRORE CONFIGURAZIONE<br/> 
                </div>
            )}

            <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-slate-300"></div>
                <span className="flex-shrink-0 mx-4 text-slate-400 text-xs font-bold uppercase">Oppure con Email</span>
                <div className="flex-grow border-t border-slate-300"></div>
            </div>

            {/* 2. TOGGLE PASSWORD / MAGIC LINK */}
            <div className="flex bg-slate-200/50 p-1 rounded-xl">
                <button 
                    onClick={() => { setAuthMode('password'); setAuthAction('register'); setAuthError(''); }}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all ${authMode === 'password' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <Key className="h-3 w-3"/> Password
                </button>
                <button 
                    onClick={() => { setAuthMode('magic'); setAuthAction('login'); setAuthError(''); }}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all ${authMode === 'magic' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <Mail className="h-3 w-3"/> Magic Link
                </button>
            </div>

{/* 3. FORM DINAMICO */}
<div className="animate-fadeIn">
    {authMode === 'password' ? (
        authAction === 'login' ? (
            // --- LOGIN EMAIL/PASSWORD ---
            <form onSubmit={handlePasswordLogin} className="space-y-3">
                <div>
                    <input 
                        type="email" 
                        required
                        placeholder="Email"
                        className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-white/80"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div>
                    <input 
                        type="password" 
                        required
                        placeholder="Password"
                        className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-white/80"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>

                {authError && (
                    <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl flex items-center gap-2 border border-red-100 animate-shake">
                        <AlertTriangle className="h-4 w-4 shrink-0"/> {authError}
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 flex justify-center items-center gap-2 shadow-lg transition-transform active:scale-95"
                >
                    {loading ? <Loader2 className="animate-spin h-5 w-5"/> : <LogIn className="h-5 w-5"/>} 
                    Accedi
                </button>

                <div className="text-center space-y-2">
                    <button 
                        type="button" 
                        onClick={() => { setAuthAction('register'); setAuthError(''); setPassword(''); setPassword2(''); }}
                        className="text-xs text-slate-500 hover:text-orange-600 hover:underline"
                    >
                        Non sei ancora registrato? Registrati
                    </button>
                    <div>
                        <button type="button" onClick={() => { setAuthMode('magic'); setAuthAction('login'); setAuthError(''); }} className="text-xs text-slate-500 hover:text-orange-600 hover:underline">
                            Password dimenticata? Usa il Magic Link
                        </button>
                    </div>
                </div>
            </form>
        ) : (
            // --- REGISTRAZIONE ---
            <form onSubmit={handleRegister} className="space-y-3">
                <div>
                    <input 
                        type="email" 
                        required
                        placeholder="Email"
                        className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-white/80"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div>
                    <input 
                        type="password" 
                        required
                        minLength={6}
                        placeholder="Password (min 6 caratteri)"
                        className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-white/80"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>
                <div>
                    <input 
                        type="password" 
                        required
                        minLength={6}
                        placeholder="Ripeti Password"
                        className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-white/80"
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                    />
                </div>

                {authError && (
                    <div className={`p-3 ${authError.startsWith('Registrazione avviata') ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-100'} text-xs font-bold rounded-xl flex items-center gap-2 border animate-shake`}>
                        <AlertTriangle className="h-4 w-4 shrink-0"/> {authError}
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 flex justify-center items-center gap-2 shadow-lg transition-transform active:scale-95"
                >
                    {loading ? <Loader2 className="animate-spin h-5 w-5"/> : <UserPlus className="h-5 w-5"/>} 
                    Registrati
                </button>

                <p className="text-[11px] text-center text-slate-500">
                    Registrandoti accetti la <a href="/privacy.html" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline font-bold">Privacy Policy</a>.
                </p>

                <div className="text-center">
                    <button 
                        type="button" 
                        onClick={() => { setAuthAction('login'); setAuthError(''); setPassword(''); setPassword2(''); }}
                        className="text-xs text-slate-500 hover:text-orange-600 hover:underline"
                    >
                        Hai già un account? Accedi
                    </button>
                </div>
            </form>
        )
    ) : (
        // --- MAGIC LINK ---
        <>
            {magicSent ? (
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200 animate-slide-in">
                    <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2"/>
                    <h3 className="font-bold text-green-800">Link Inviato!</h3>
                    <p className="text-sm text-green-700 mt-1">Controlla la posta di <strong>{magicEmail}</strong> per accedere.</p>
                    <button onClick={() => setMagicSent(false)} className="mt-4 text-xs font-bold text-green-600 hover:underline">Indietro</button>
                </div>
            ) : (
                <form onSubmit={handleMagicLogin} className="space-y-3">
                    <div>
                        <input 
                            type="email" 
                            required
                            placeholder="Tua Email per il Magic Link"
                            className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white/80"
                            value={magicEmail}
                            onChange={(e) => setMagicEmail(e.target.value)}
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={magicLoading}
                        className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 flex justify-center items-center gap-2 shadow-lg transition-transform active:scale-95"
                    >
                        {magicLoading ? <Loader2 className="animate-spin h-5 w-5"/> : <Mail className="h-5 w-5"/>} 
                        Invia Link di Accesso
                    </button>
                    <p className="text-[10px] text-center text-slate-400">Ti invieremo un'email per accedere senza password. Utile anche se l'hai dimenticata.</p>
                </form>
            )}
        </>
    )}
</div>


          </div>
          
          <div className="mt-8 text-center text-xs text-slate-400">
             RefPublic - Copyright &copy; {new Date().getFullYear()} KekkoTech. Tutti i diritti riservati.
          </div>
        </div>
    </div>
  );
};

const Onboarding = ({ session, onComplete }) => {
  const [matricola, setMatricola] = useState('');
  const [annoInizio, setAnnoInizio] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    if (!matricola || matricola.length < 5) {
        setErrorMsg("Inserisci una matricola valida (min 5 cifre).");
        return;
    }

    const yearNum = parseInt(annoInizio, 10);
    const currentYear = new Date().getFullYear();
    if (!annoInizio || !/^[0-9]{4}$/.test(annoInizio) || isNaN(yearNum) || yearNum < 1950 || yearNum > currentYear) {
        setErrorMsg(`Inserisci un anno di inizio valido (1950-${currentYear}).`);
        return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
        // 1. Cerca la matricola nella whitelist
        const { data: authData, error: authError } = await supabase
            .from('authorized_referees')
            .select('*')
            .eq('matricola', matricola)
            .single();

        if (authError || !authData) {
            throw new Error("Matricola non trovata. Contatta il tuo Manager per l'autorizzazione.");
        }

        // 2. Crea il profilo utente usando i dati pre-caricati
        const { error: profileError } = await supabase.from('profiles').upsert({
            id: session.user.id,
            matricola: authData.matricola,
            full_name: `${authData.first_name} ${authData.last_name}`.trim(),
            comitato: authData.comitato,
            role: authData.role,
            anno_inizio: String(yearNum),
            updated_at: new Date()
        });

        if (profileError) throw profileError;

        // 3. Segna come registrato
        await supabase.from('authorized_referees')
            .update({ is_registered: true, anno_inizio: yearNum })
            .eq('matricola', matricola);

        // 4. Completa
        onComplete({ 
            id: session.user.id, 
            full_name: `${authData.first_name} ${authData.last_name}`,
            matricola: authData.matricola,
            comitato: authData.comitato,
            role: authData.role
        });

    } catch (err) {
        setErrorMsg(err.message);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-slate-200">
        <div className="text-center mb-6">
            <ShieldCheck className="h-12 w-12 text-orange-500 mx-auto mb-3"/>
            <h2 className="text-2xl font-black text-slate-900">Attiva Account</h2>
            <p className="text-slate-500 text-sm mt-1">Inserisci la tua matricola per verificare l'abilitazione.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
              <label className="text-xs font-bold uppercase text-slate-500 ml-1 mb-1 block">Matricola</label>
              <input 
                required 
                type="text" 
                inputMode="numeric"
                className="w-full p-4 border border-slate-300 rounded-xl text-lg font-mono tracking-widest text-center focus:ring-2 focus:ring-orange-500 outline-none transition-all" 
                value={matricola} 
                onChange={(e) => { const val = e.target.value; if (/^\d*$/.test(val)) setMatricola(val); }} 
                placeholder="000000"
              />
          </div>

          <div>
    <label className="text-xs font-bold uppercase text-slate-500 ml-1 mb-1 block">Anno inizio arbitraggio</label>
    <input 
      required 
      type="text"
      inputMode="numeric"
      maxLength={4}
      className="w-full p-4 border border-slate-300 rounded-xl text-lg font-mono tracking-widest text-center focus:ring-2 focus:ring-orange-500 outline-none transition-all" 
      value={annoInizio} 
      onChange={(e) => { const val = e.target.value; if (/^\d*$/.test(val)) setAnnoInizio(val); }} 
      placeholder="2020"
    />
</div>

{errorMsg && (
              <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl flex items-center gap-2 border border-red-100 animate-shake">
                  <AlertTriangle className="h-4 w-4 shrink-0"/> {errorMsg}
              </div>
          )}

          <button disabled={loading} className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-slate-800 flex justify-center items-center gap-2 shadow-lg transition-transform active:scale-95">
              {loading ? <Loader2 className="animate-spin h-5 w-5"/> : <Rocket className="h-5 w-5"/>} 
              Verifica e Accedi
          </button>
        </form>
        
        <p className="text-center text-xs text-slate-400 mt-6">
            Non sei autorizzato? Chiedi al tuo Referente Arbitrale.
        </p>
      </div>
    </div>
  );
};

// ==================================================================================
// 📔 DIARIO GARE — API
// ==================================================================================

const CREW_LABELS = {
    first_referee:  '1° Arbitro',
    second_referee: '2° Arbitro',
    scorekeeper:    'Segnapunti',
    videocheck:     'Addetto VideoCheck'
};

// Ruolo dell'utente -> campo in cui finisce il suo nome.
const ROLE_TO_CREW_FIELD = {
    '1° Arbitro':            'first_referee',
    '2° Arbitro':            'second_referee',
    'Segnapunti':            'scorekeeper',
    'Addetto al VideoCheck': 'videocheck'
};

// Il videocheck esiste solo in ambito nazionale.
const levelHasVideocheck = (level) => level === 'Nazionale';

// Campi effettivamente compilati per una voce gia' salvata: serve a
// rileggerla senza doversi ricordare com'era composta la terna.
const crewFieldsOf = (entry) =>
    ['first_referee', 'second_referee', 'scorekeeper', 'videocheck'].filter(f => (entry[f] || '').trim());

const isMissingTableError = (error) =>
    !!error && (error.code === '42P01' || /does not exist|schema cache/i.test(error.message || ''));

const fetchDiaryEntries = async (userId) => {
    if (!supabase) return { entries: [], notReady: false };
    const { data, error } = await supabase
        .from('match_diary')
        .select('*, sets:match_diary_sets(*)')
        .eq('user_id', userId)
        .order('match_date', { ascending: false });
    if (error) {
        console.error("fetchDiaryEntries error:", error);
        return { entries: [], notReady: isMissingTableError(error) };
    }
    return {
        entries: (data || []).map(d => ({
            ...d,
            sets: (d.sets || []).sort((a, b) => a.set_number - b.set_number)
        })),
        notReady: false
    };
};

const saveDiaryEntry = async (userId, entry, sets) => {
    if (!supabase) return { error: 'No client' };

    const payload = {
        user_id: userId,
        designation_id: entry.designation_id || null,
        match_date: entry.match_date,
        home_team: (entry.home_team || '').trim(),
        away_team: (entry.away_team || '').trim(),
        category: entry.category || null,
        level: entry.level,
        gender: entry.gender,
        series: (entry.series || '').trim(),
        location: (entry.location || '').trim(),
        is_friendly: !!entry.is_friendly,
        my_role: entry.my_role,
        first_referee: entry.first_referee || '',
        second_referee: entry.second_referee || '',
        scorekeeper: entry.scorekeeper || '',
        videocheck: entry.videocheck || '',
        home_sets: Number(entry.home_sets) || 0,
        away_sets: Number(entry.away_sets) || 0,
        went_well: entry.went_well || '',
        went_bad: entry.went_bad || '',
        action_points: entry.action_points || '',
        overall_rating: entry.overall_rating || null,
        overall_stress: entry.overall_stress || null
    };

    let diaryId = entry.id;
    if (diaryId) {
        const { error } = await supabase.from('match_diary').update(payload).eq('id', diaryId);
        if (error) return { error };
    } else {
        const { data, error } = await supabase.from('match_diary').insert([payload]).select('id').single();
        if (error) return { error };
        diaryId = data.id;
    }

    // I set vengono riscritti per intero: sono pochi (max 5) e così non serve
    // inseguire quali righe siano state aggiunte, modificate o tolte.
    const { error: delError } = await supabase.from('match_diary_sets').delete().eq('diary_id', diaryId);
    if (delError) return { error: delError };

    const setRows = (sets || [])
        .filter(s => s && (s.home_score !== '' || s.away_score !== '' || s.rating || s.stress || s.difficulty || s.notes))
        .map((s, idx) => ({
            diary_id: diaryId,
            set_number: idx + 1,
            home_score: s.home_score === '' || s.home_score === null ? null : Number(s.home_score),
            away_score: s.away_score === '' || s.away_score === null ? null : Number(s.away_score),
            rating: s.rating || null,
            stress: s.stress || null,
            difficulty: s.difficulty || null,
            notes: s.notes || ''
        }));

    if (setRows.length > 0) {
        const { error: insError } = await supabase.from('match_diary_sets').insert(setRows);
        if (insError) return { error: insError };
    }

    return { error: null, id: diaryId };
};

const deleteDiaryEntry = async (id) => {
    if (!supabase) return { error: 'No client' };
    // I set spariscono in cascata (ON DELETE CASCADE).
    const { error } = await supabase.from('match_diary').delete().eq('id', id);
    return { error };
};

// Gare già inserite in app, passate, non ancora presenti nel diario.
const fetchImportableMatches = async (userId, existingDesignationIds) => {
    if (!supabase) return [];
    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from('designations')
        .select(`
            *,
            profile:user_id (full_name),
            partner:partner_id (full_name),
            second_referee:second_referee_id (full_name),
            scorekeeper:scorekeeper_id (full_name),
            videocheck:videocheck_id (full_name)
        `)
        .or(`user_id.eq.${userId},partner_id.eq.${userId},second_referee_id.eq.${userId},scorekeeper_id.eq.${userId},videocheck_id.eq.${userId}`)
        .lte('match_date', now)
        .order('match_date', { ascending: false })
        .limit(60);
    if (error) { console.error("fetchImportableMatches error:", error); return []; }
    return (data || []).filter(d => !existingDesignationIds.includes(d.id));
};

// Traduce una designazione nella bozza di una voce di diario.
const designationToDiaryDraft = (d, user) => {
    // Il ruolo dell'utente dipende dallo slot in cui è stato inserito.
    let myRole = d.my_role || '1° Arbitro';
    if (d.user_id !== user.id) {
        if (d.partner_id === user.id) myRole = d.partner_role || '2° Arbitro';
        else if (d.second_referee_id === user.id) myRole = '2° Arbitro';
        else if (d.scorekeeper_id === user.id) myRole = 'Segnapunti';
        else if (d.videocheck_id === user.id) myRole = 'Addetto al VideoCheck';
    }

    // Ricostruisce la terna mettendo l'utente al posto giusto.
    const creator = d.user_id === user.id ? user.full_name : (d.profile?.full_name || '');
    const crew = { first_referee: '', second_referee: '', scorekeeper: '', videocheck: '' };

    const place = (role, name) => {
        if (!name) return;
        if (role === '1° Arbitro') crew.first_referee = name;
        else if (role === '2° Arbitro') crew.second_referee = name;
        else if (role === 'Segnapunti') crew.scorekeeper = name;
        else if (role === 'Addetto al VideoCheck') crew.videocheck = name;
    };

    place(d.my_role || '1° Arbitro', creator);
    place(d.partner_role || '2° Arbitro', d.partner?.full_name);
    if (d.second_referee?.full_name) crew.second_referee = d.second_referee.full_name;
    if (d.scorekeeper?.full_name) crew.scorekeeper = d.scorekeeper.full_name;
    if (d.videocheck?.full_name) crew.videocheck = d.videocheck.full_name;

    return {
        designation_id: d.id,
        match_date: d.match_date,
        home_team: d.home_team,
        away_team: d.away_team,
        category: d.category || '',
        level: d.level || 'Regionale',
        gender: d.gender || 'Maschile',
        series: d.series || '',
        location: d.location || '',
        is_friendly: !!d.is_friendly,
        my_role: myRole,
        ...crew,
        home_sets: 0,
        away_sets: 0,
        went_well: '',
        went_bad: '',
        action_points: '',
        overall_rating: 3,
        overall_stress: 3,
        sets: []
    };
};

// ==================================================================================
// 🔔 NOTIFICHE PUSH (app chiusa)
// ==================================================================================

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const isPushSupported = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

// La chiave VAPID viaggia in base64url: il browser la vuole come byte array.
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

const savePushSubscription = async (userId, subscription) => {
    if (!supabase) return { error: 'No client' };
    const json = subscription.toJSON();
    if (!json.keys?.p256dh || !json.keys?.auth) return { error: 'Iscrizione incompleta' };

    // L'endpoint identifica il dispositivo: se cambia utente sullo stesso
    // browser la riga viene riassegnata invece di duplicarsi.
    const { error } = await supabase.from('push_subscriptions').upsert({
        user_id: userId,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        user_agent: navigator.userAgent.slice(0, 300),
        last_used_at: new Date().toISOString()
    }, { onConflict: 'endpoint' });
    return { error };
};

const getPushStatus = async () => {
    if (!isPushSupported()) return 'unsupported';
    if (!VAPID_PUBLIC_KEY) return 'not_configured';
    if (Notification.permission === 'denied') return 'denied';
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return 'off';
        const sub = await reg.pushManager.getSubscription();
        return sub ? 'on' : 'off';
    } catch {
        return 'off';
    }
};

const enablePushNotifications = async (userId) => {
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
    if (!VAPID_PUBLIC_KEY) return { ok: false, reason: 'not_configured' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    try {
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });
        }
        const { error } = await savePushSubscription(userId, sub);
        if (error) return { ok: false, reason: 'save_failed', error };
        return { ok: true };
    } catch (err) {
        console.error("Iscrizione push non riuscita:", err);
        return { ok: false, reason: 'failed', error: err };
    }
};

const disablePushNotifications = async () => {
    if (!isPushSupported()) return { ok: false };
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg && await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            await sub.unsubscribe();
            if (supabase) await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
        }
        return { ok: true };
    } catch (err) {
        console.error("Disiscrizione push non riuscita:", err);
        return { ok: false };
    }
};

// ==================================================================================
// 🎯 OBIETTIVI PERSONALI
// ==================================================================================

const fetchGoals = async (userId) => {
    if (!supabase) return { goals: [], notReady: false };
    const { data, error } = await supabase
        .from('diary_goals')
        .select('*, progress:diary_goal_progress(id, diary_id, rating)')
        .eq('user_id', userId)
        .order('status', { ascending: true })
        .order('created_at', { ascending: false });
    if (error) {
        console.error("fetchGoals error:", error);
        return { goals: [], notReady: isMissingTableError(error) };
    }
    return { goals: data || [], notReady: false };
};

const createGoal = async (userId, title, notes = '', sourceDiaryId = null) => {
    if (!supabase) return { error: 'No client' };
    const clean = (title || '').trim();
    if (!clean) return { error: { message: 'Titolo mancante' } };
    const { data, error } = await supabase.from('diary_goals').insert([{
        user_id: userId,
        title: clean.slice(0, 200),
        notes: notes || '',
        source_diary_id: sourceDiaryId
    }]).select().single();
    return { data, error };
};

const setGoalStatus = async (goalId, status) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase.from('diary_goals').update({
        status,
        completed_at: status === 'done' ? new Date().toISOString() : null
    }).eq('id', goalId);
    return { error };
};

const deleteGoal = async (goalId) => {
    if (!supabase) return { error: 'No client' };
    const { error } = await supabase.from('diary_goals').delete().eq('id', goalId);
    return { error };
};

// Riscrive per intero i progressi legati a una voce di diario.
const saveGoalProgress = async (diaryId, entries) => {
    if (!supabase || !diaryId) return { error: null };
    const { error: delError } = await supabase.from('diary_goal_progress').delete().eq('diary_id', diaryId);
    if (delError) return { error: delError };

    const rows = (entries || [])
        .filter(e => e && e.goal_id)
        .map(e => ({ goal_id: e.goal_id, diary_id: diaryId, rating: e.rating || null }));
    if (rows.length === 0) return { error: null };

    const { error } = await supabase.from('diary_goal_progress').insert(rows);
    return { error };
};

// ==================================================================================
// 🔁 RIPASSO DELLE DOMANDE SBAGLIATE
// ==================================================================================

const fetchReviewQueue = async (userId, limit = 200) => {
    if (!supabase) return { rows: [], notReady: false };
    const { data, error } = await supabase
        .from('quiz_review_queue')
        .select('question_id, wrong_count, streak, due_at')
        .eq('user_id', userId)
        .eq('resolved', false)
        .lte('due_at', new Date().toISOString())
        .order('due_at', { ascending: true })
        .limit(limit);
    if (error) {
        console.error("fetchReviewQueue error:", error);
        return { rows: [], notReady: isMissingTableError(error) };
    }
    return { rows: data || [], notReady: false };
};

const fetchQuestionsByIds = async (ids) => {
    if (!supabase || !ids || ids.length === 0) return [];
    const { data, error } = await supabase.from('quiz_questions').select('*').in('id', ids);
    if (error) { console.error("fetchQuestionsByIds error:", error); return []; }
    return data || [];
};

// Invia gli esiti dell'intera sessione in una sola chiamata.
const recordReviewResults = async (results) => {
    if (!supabase || !results || results.length === 0) return;
    const { error } = await supabase.rpc('quiz_review_record_batch', { p_results: results });
    if (error && !isMissingTableError(error)) console.error("recordReviewResults error:", error);
};

// ==================================================================================
// 📅 PROMEMORIA PRE-GARA
// ==================================================================================

// Gare di domani in cui l'utente è coinvolto e ha accettato.
const fetchTomorrowDesignations = async (userId) => {
    if (!supabase) return [];

    // La finestra è calcolata sul giorno italiano, non su UTC.
    const italyParts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const get = (t) => italyParts.find(p => p.type === t).value;
    const todayItaly = new Date(`${get('year')}-${get('month')}-${get('day')}T00:00:00`);

    const start = new Date(todayItaly); start.setDate(start.getDate() + 1);
    const end = new Date(start); end.setDate(end.getDate() + 1);

    const { data, error } = await supabase
        .from('designations')
        .select(`
            id, home_team, away_team, match_date, location, series, level, category, my_role,
            user_id, partner_id, partner_status, partner_role,
            second_referee_id, second_referee_status,
            scorekeeper_id, scorekeeper_status,
            videocheck_id, videocheck_status,
            profile:user_id (full_name),
            partner:partner_id (full_name),
            second_referee:second_referee_id (full_name),
            scorekeeper:scorekeeper_id (full_name),
            videocheck:videocheck_id (full_name)
        `)
        .gte('match_date', start.toISOString())
        .lt('match_date', end.toISOString())
        .or(`user_id.eq.${userId},partner_id.eq.${userId},second_referee_id.eq.${userId},scorekeeper_id.eq.${userId},videocheck_id.eq.${userId}`);

    if (error) { console.error("fetchTomorrowDesignations error:", error); return []; }

    // Un invito non ancora accettato non è una gara confermata: niente promemoria.
    return (data || []).filter(d => {
        if (d.user_id === userId) return true;
        if (d.partner_id === userId) return d.partner_status === 'accepted';
        if (d.second_referee_id === userId) return d.second_referee_status === 'accepted';
        if (d.scorekeeper_id === userId) return d.scorekeeper_status === 'accepted';
        if (d.videocheck_id === userId) return d.videocheck_status === 'accepted';
        return false;
    });
};

const describeCrew = (d) => {
    const names = [];
    if (d.profile?.full_name) names.push(`${d.my_role || '1° Arbitro'}: ${d.profile.full_name}`);
    if (d.partner?.full_name && d.partner_status === 'accepted') names.push(`${d.partner_role || 'Collega'}: ${d.partner.full_name}`);
    if (d.second_referee?.full_name && d.second_referee_status === 'accepted') names.push(`2° Arbitro: ${d.second_referee.full_name}`);
    if (d.scorekeeper?.full_name && d.scorekeeper_status === 'accepted') names.push(`Segnapunti: ${d.scorekeeper.full_name}`);
    if (d.videocheck?.full_name && d.videocheck_status === 'accepted') names.push(`VideoCheck: ${d.videocheck.full_name}`);
    return names.join(' · ');
};

// ==================================================================================
// 🗓️ STAGIONE SPORTIVA
// ==================================================================================

// La stagione di pallavolo va dal 1° settembre al 31 agosto.
const seasonOf = (date) => {
    const d = new Date(date);
    return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
};

const seasonLabel = (startYear) => `${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`;

const seasonRange = (startYear) => ({
    from: new Date(startYear, 8, 1),
    to: new Date(startYear + 1, 7, 31, 23, 59, 59)
});

// ==================================================================================
// 📔 DIARIO GARE — GRAFICI (SVG inline, nessuna libreria esterna)
// ==================================================================================

// Palette a due serie verificata per daltonismo (ΔE 32 protan / 36 tritan):
// indigo = rendimento, arancio = stress. Il testo resta sempre in grigio,
// il colore lo portano solo i segni del grafico.
const CHART_RATING = '#4f46e5';
const CHART_STRESS = '#ea580c';
const CHART_GRID   = '#e2e8f0';
const CHART_AXIS   = '#94a3b8';

const ChartLegend = ({ items }) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
        {items.map(it => (
            <span key={it.label} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: it.color }} aria-hidden="true" />
                {it.label}
            </span>
        ))}
    </div>
);

const ChartEmpty = ({ text }) => (
    <div className="h-40 flex flex-col items-center justify-center text-slate-400 text-xs italic gap-2">
        <BarChart3 className="h-8 w-8 opacity-20" />
        {text}
    </div>
);

// --- Andamento del rendimento nel tempo ---------------------------------
const DiaryTrendChart = ({ points }) => {
    const [hover, setHover] = useState(null);
    if (!points || points.length === 0) return <ChartEmpty text="Annota qualche gara per vedere l'andamento." />;

    const W = 660, H = 210, padL = 30, padR = 14, padT = 12, padB = 30;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    // Con un solo punto lo centriamo invece di dividere per zero.
    const x = (i) => points.length === 1 ? padL + plotW / 2 : padL + (i / (points.length - 1)) * plotW;
    const y = (v) => padT + plotH - ((v - 1) / 4) * plotH;

    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`).join(' ');
    const area = `${path} L ${x(points.length - 1).toFixed(1)} ${padT + plotH} L ${x(0).toFixed(1)} ${padT + plotH} Z`;

    return (
        <div className="relative">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Andamento del rendimento per gara">
                {[1, 2, 3, 4, 5].map(v => (
                    <g key={v}>
                        <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke={CHART_GRID} strokeWidth="1" />
                        <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill={CHART_AXIS}>{v}</text>
                    </g>
                ))}

                {points.length > 1 && (
                    <>
                        <defs>
                            <linearGradient id="rpTrendFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={CHART_RATING} stopOpacity="0.18" />
                                <stop offset="100%" stopColor={CHART_RATING} stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path d={area} fill="url(#rpTrendFill)" />
                    </>
                )}
                <path d={path} fill="none" stroke={CHART_RATING} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={x(i)} cy={y(p.value)} r="4" fill="#fff" stroke={CHART_RATING} strokeWidth="2" />
                        {/* Area di aggancio più larga del punto, per il tocco su mobile */}
                        <rect
                            x={x(i) - 14} y={padT} width="28" height={plotH}
                            fill="transparent"
                            onMouseEnter={() => setHover({ ...p, i })}
                            onMouseLeave={() => setHover(null)}
                        />
                    </g>
                ))}

                {hover && <line x1={x(hover.i)} x2={x(hover.i)} y1={padT} y2={padT + plotH} stroke={CHART_RATING} strokeWidth="1" strokeDasharray="3 3" />}

                {/* Solo prima e ultima data: una etichetta per punto sarebbe illeggibile */}
                <text x={x(0)} y={H - 8} textAnchor="start" fontSize="10" fill={CHART_AXIS}>{points[0].label}</text>
                {points.length > 1 && <text x={x(points.length - 1)} y={H - 8} textAnchor="end" fontSize="10" fill={CHART_AXIS}>{points[points.length - 1].label}</text>}
            </svg>

            {hover && (
                <div
                    className="absolute -translate-x-1/2 -translate-y-full pointer-events-none bg-slate-900 text-white text-[11px] rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap z-10"
                    style={{ left: `${(x(hover.i) / W) * 100}%`, top: `${(y(hover.value) / H) * 100}%`, marginTop: '-8px' }}
                >
                    <div className="font-bold">{hover.title}</div>
                    <div className="opacity-80">{hover.label} · rendimento {hover.value}/5</div>
                </div>
            )}
        </div>
    );
};

// --- Rendimento e stress set per set ------------------------------------
const DiarySetBars = ({ rows }) => {
    const [hover, setHover] = useState(null);
    const active = (rows || []).filter(r => r.count > 0);
    if (active.length === 0) return <ChartEmpty text="Valuta i singoli set per confrontarli." />;

    const W = 660, H = 210, padL = 30, padR = 14, padT = 12, padB = 30;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const group = plotW / active.length;
    const barW = Math.min(26, (group - 14) / 2);
    const y = (v) => padT + plotH - (v / 5) * plotH;

    return (
        <div className="relative">
            <ChartLegend items={[{ label: 'Rendimento', color: CHART_RATING }, { label: 'Stress', color: CHART_STRESS }]} />
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Rendimento e stress medi per numero di set">
                {[0, 1, 2, 3, 4, 5].map(v => (
                    <g key={v}>
                        <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke={CHART_GRID} strokeWidth="1" />
                        <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill={CHART_AXIS}>{v}</text>
                    </g>
                ))}

                {active.map((r, i) => {
                    const cx = padL + group * i + group / 2;
                    // 2px di respiro fra le due barre del gruppo
                    const xR = cx - barW - 1;
                    const xS = cx + 1;
                    return (
                        <g key={r.set}
                           onMouseEnter={() => setHover({ ...r, cx })}
                           onMouseLeave={() => setHover(null)}>
                            <rect x={padL + group * i} y={padT} width={group} height={plotH} fill="transparent" />
                            <rect x={xR} y={y(r.rating)} width={barW} height={Math.max(0, plotH - (y(r.rating) - padT))} rx="4" fill={CHART_RATING} />
                            <rect x={xS} y={y(r.stress)} width={barW} height={Math.max(0, plotH - (y(r.stress) - padT))} rx="4" fill={CHART_STRESS} />
                            <text x={cx} y={H - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill={CHART_AXIS}>Set {r.set}</text>
                        </g>
                    );
                })}
            </svg>

            {hover && (
                <div
                    className="absolute -translate-x-1/2 pointer-events-none bg-slate-900 text-white text-[11px] rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap z-10"
                    style={{ left: `${(hover.cx / W) * 100}%`, top: 0 }}
                >
                    <div className="font-bold">Set {hover.set} · {hover.count} {hover.count === 1 ? 'gara' : 'gare'}</div>
                    <div className="opacity-80">Rendimento {hover.rating} · Stress {hover.stress}</div>
                </div>
            )}
        </div>
    );
};

// --- Distribuzione per categoria / livello ------------------------------
const DiaryDistributionBars = ({ rows, accent = CHART_RATING }) => {
    if (!rows || rows.length === 0) return <ChartEmpty text="Nessun dato da raggruppare." />;
    const max = Math.max(...rows.map(r => r.count), 1);
    return (
        <div className="space-y-2.5">
            {rows.map(r => (
                <div key={r.label} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-600 w-32 shrink-0 truncate" title={r.label}>{r.label}</span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden">
                        <div
                            className="h-full rounded-md transition-all duration-500"
                            style={{ width: `${Math.max((r.count / max) * 100, 4)}%`, background: accent }}
                        />
                    </div>
                    <span className="text-xs font-bold text-slate-700 w-14 text-right shrink-0">
                        {r.count} {r.avg ? <span className="text-slate-400 font-medium">· {r.avg}</span> : null}
                    </span>
                </div>
            ))}
        </div>
    );
};

// --- Tessera statistica -------------------------------------------------
const DiaryStatTile = ({ label, value, suffix, icon: Icon, tone = 'slate' }) => {
    const tones = {
        slate:  'bg-slate-50 border-slate-200 text-slate-700',
        indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
        orange: 'bg-orange-50 border-orange-200 text-orange-700',
        green:  'bg-green-50 border-green-200 text-green-700'
    };
    return (
        <div className={`rounded-xl border p-4 ${tones[tone]}`}>
            <div className="flex items-center gap-2 mb-2 opacity-70">
                <Icon className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
            </div>
            <p className="text-2xl font-black leading-none">
                {value}
                {suffix && <span className="text-sm font-bold opacity-50 ml-1">{suffix}</span>}
            </p>
        </div>
    );
};

// --- Rapporto di fine stagione ---
// Pensato per essere salvato in PDF dalla stampa del browser: nessuna
// libreria, e quello che si vede a schermo è quello che finisce sul foglio.
const SeasonReport = ({ user, entries, goals, season, onSeasonChange, seasons, onClose }) => {
    const { from, to } = seasonRange(season);

    const list = useMemo(
        () => entries
            .filter(e => { const d = new Date(e.match_date); return d >= from && d <= to; })
            .sort((a, b) => new Date(a.match_date) - new Date(b.match_date)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entries, season]
    );

    const data = useMemo(() => {
        const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        const ratings = list.map(e => e.overall_rating).filter(v => v != null);
        const stresses = list.map(e => e.overall_stress).filter(v => v != null);
        const allSets = list.flatMap(e => e.sets || []);

        const count = (key) => {
            const m = {};
            list.forEach(e => {
                const k = (e[key] || '').trim() || 'Non indicato';
                m[k] = (m[k] || 0) + 1;
            });
            return Object.entries(m).sort((a, b) => b[1] - a[1]);
        };

        const roleCount = {};
        list.forEach(e => { const r = e.my_role || 'Non indicato'; roleCount[r] = (roleCount[r] || 0) + 1; });

        const perSet = [1, 2, 3, 4, 5].map(n => {
            const ss = allSets.filter(x => x.set_number === n);
            const r = ss.map(x => x.rating).filter(v => v != null);
            const st = ss.map(x => x.stress).filter(v => v != null);
            return {
                set: n,
                count: ss.length,
                rating: r.length ? Number(avg(r).toFixed(2)) : 0,
                stress: st.length ? Number(avg(st).toFixed(2)) : 0
            };
        });

        const trend = list
            .filter(e => e.overall_rating != null)
            .map(e => ({
                value: e.overall_rating,
                label: new Date(e.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
                title: `${e.home_team} - ${e.away_team}`
            }));

        const seasonGoals = (goals || []).filter(g => {
            const d = new Date(g.created_at);
            return d >= from && d <= to;
        });

        return {
            total: list.length,
            avgRating: avg(ratings),
            avgStress: avg(stresses),
            setsLogged: allSets.length,
            byLevel: count('level'),
            byCategory: count('category'),
            byRole: Object.entries(roleCount).sort((a, b) => b[1] - a[1]),
            perSet,
            trend,
            goalsDone: seasonGoals.filter(g => g.status === 'done'),
            goalsOpen: seasonGoals.filter(g => g.status === 'open')
        };
    }, [list, goals, from, to]);

    // La stampa nasconde tutto tranne questo pannello (regole in index.css).
    useEffect(() => {
        document.body.classList.add('rp-printing');
        return () => document.body.classList.remove('rp-printing');
    }, []);

    const Row = ({ label, value }) => (
        <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
            <span className="text-slate-500">{label}</span>
            <span className="font-bold text-slate-800">{value}</span>
        </div>
    );

    return (
        <div id="rp-report" className="fixed inset-0 z-[1500] bg-white overflow-y-auto">
            <div className="max-w-3xl mx-auto p-6 md:p-10">

                {/* Barra azioni: non finisce sul foglio */}
                <div className="rp-print-hide flex flex-wrap items-center justify-between gap-3 mb-8 pb-4 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stagione</label>
                        <select
                            value={season}
                            onChange={e => onSeasonChange(Number(e.target.value))}
                            className="border border-slate-300 rounded-lg p-2 text-sm font-bold bg-white"
                        >
                            {seasons.map(y => <option key={y} value={y}>{seasonLabel(y)}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">
                            Chiudi
                        </button>
                        <button onClick={() => window.print()} className="px-5 py-2 rounded-lg bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition-colors flex items-center gap-2">
                            <Printer className="h-4 w-4" /> Stampa o salva in PDF
                        </button>
                    </div>
                </div>

                {/* Intestazione */}
                <header className="mb-8">
                    <p className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-1">Rapporto di stagione {seasonLabel(season)}</p>
                    <h1 className="text-3xl font-black text-slate-900 leading-tight">{user.full_name}</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {[user.role, user.comitato, user.matricola ? `Matricola ${user.matricola}` : null]
                            .filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-3">
                        Dal 1° settembre {season} al 31 agosto {season + 1}. Dati tratti dal diario gare personale su RefPublic.
                    </p>
                </header>

                {data.total === 0 ? (
                    <p className="text-slate-500 italic py-10 text-center">Nessuna gara annotata in questa stagione.</p>
                ) : (
                    <>
                        {/* Numeri principali */}
                        <section className="mb-8">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Gare dirette</p>
                                    <p className="text-2xl font-black text-slate-900">{data.total}</p>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Rendimento medio</p>
                                    <p className="text-2xl font-black text-slate-900">{data.avgRating ? data.avgRating.toFixed(1) : '—'}<span className="text-sm text-slate-400">/5</span></p>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Stress medio</p>
                                    <p className="text-2xl font-black text-slate-900">{data.avgStress ? data.avgStress.toFixed(1) : '—'}<span className="text-sm text-slate-400">/5</span></p>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Set valutati</p>
                                    <p className="text-2xl font-black text-slate-900">{data.setsLogged}</p>
                                </div>
                            </div>
                        </section>

                        {/* Ripartizioni */}
                        <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 mb-2">Per livello</h2>
                                {data.byLevel.map(([k, v]) => <Row key={k} label={k} value={v} />)}
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 mb-2">Per ruolo ricoperto</h2>
                                {data.byRole.map(([k, v]) => <Row key={k} label={k} value={v} />)}
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 mb-2">Per categoria</h2>
                                {data.byCategory.slice(0, 8).map(([k, v]) => <Row key={k} label={k} value={v} />)}
                            </div>
                        </section>

                        {/* Grafici */}
                        <section className="mb-8 rp-break-inside-avoid">
                            <h2 className="text-sm font-bold text-slate-800 mb-3">Andamento del rendimento</h2>
                            <DiaryTrendChart points={data.trend} />
                        </section>

                        <section className="mb-8 rp-break-inside-avoid">
                            <h2 className="text-sm font-bold text-slate-800 mb-3">Rendimento e stress per set</h2>
                            <DiarySetBars rows={data.perSet} />
                        </section>

                        {/* Obiettivi */}
                        {(data.goalsDone.length > 0 || data.goalsOpen.length > 0) && (
                            <section className="mb-8 rp-break-inside-avoid">
                                <h2 className="text-sm font-bold text-slate-800 mb-2">Obiettivi della stagione</h2>
                                {data.goalsDone.length > 0 && (
                                    <>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-3 mb-1">Raggiunti</p>
                                        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                                            {data.goalsDone.map(g => <li key={g.id}>{g.title}</li>)}
                                        </ul>
                                    </>
                                )}
                                {data.goalsOpen.length > 0 && (
                                    <>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-4 mb-1">Ancora aperti</p>
                                        <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                                            {data.goalsOpen.map(g => <li key={g.id}>{g.title}</li>)}
                                        </ul>
                                    </>
                                )}
                            </section>
                        )}

                        {/* Elenco gare */}
                        <section className="mb-8">
                            <h2 className="text-sm font-bold text-slate-800 mb-3">Elenco delle gare</h2>
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-slate-400 uppercase text-[9px] tracking-wider border-b border-slate-200">
                                        <th className="text-left font-bold py-2">Data</th>
                                        <th className="text-left font-bold py-2">Gara</th>
                                        <th className="text-left font-bold py-2">Campionato</th>
                                        <th className="text-left font-bold py-2">Ruolo</th>
                                        <th className="text-center font-bold py-2">Set</th>
                                        <th className="text-center font-bold py-2">Rend.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map(e => (
                                        <tr key={e.id} className="border-b border-slate-100">
                                            <td className="py-2 whitespace-nowrap text-slate-600">{new Date(e.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                                            <td className="py-2 font-bold text-slate-800">{e.home_team} - {e.away_team}</td>
                                            <td className="py-2 text-slate-500">{[e.series, e.level].filter(Boolean).join(' · ')}</td>
                                            <td className="py-2 text-slate-500">{e.my_role}</td>
                                            <td className="py-2 text-center text-slate-600 whitespace-nowrap">{e.home_sets}-{e.away_sets}</td>
                                            <td className="py-2 text-center font-bold text-slate-800">{e.overall_rating ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    </>
                )}

                <footer className="pt-5 mt-8 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed">
                    Documento generato da RefPublic il {new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}.
                    Le valutazioni sono autovalutazioni dell&apos;arbitro e non hanno valore ufficiale.
                </footer>
            </div>
        </div>
    );
};

// ==================================================================================
// 📔 DIARIO GARE — VISTA
// ==================================================================================

// Selettore 1-5 riutilizzato per rendimento, stress e difficoltà.
const ScaleInput = ({ value, onChange, lowLabel, highLabel, tone = 'indigo', id }) => {
    const tones = {
        indigo: 'bg-indigo-600 border-indigo-600 text-white',
        orange: 'bg-orange-500 border-orange-500 text-white',
        slate:  'bg-slate-700 border-slate-700 text-white'
    };
    return (
        <div>
            <div className="flex gap-1.5" role="radiogroup" aria-labelledby={id}>
                {[1, 2, 3, 4, 5].map(v => (
                    <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={value === v}
                        onClick={() => onChange(value === v ? null : v)}
                        className={`flex-1 h-9 rounded-lg border text-sm font-bold transition-all ${value === v ? tones[tone] : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                    >
                        {v}
                    </button>
                ))}
            </div>
            {(lowLabel || highLabel) && (
                <div className="flex justify-between mt-1 text-[10px] text-slate-400 font-medium">
                    <span>{lowLabel}</span><span>{highLabel}</span>
                </div>
            )}
        </div>
    );
};

const emptyDiaryEntry = () => ({
    designation_id: null,
    match_date: '', match_time: '',
    home_team: '', away_team: '', category: '',
    level: 'Regionale', gender: 'Maschile', series: '', location: '',
    is_friendly: false,
    my_role: '1° Arbitro',
    first_referee: '', second_referee: '', scorekeeper: '', videocheck: '',
    home_sets: 0, away_sets: 0,
    went_well: '', went_bad: '', action_points: '',
    overall_rating: 3, overall_stress: 3,
    hasSecond: true, hasScorer: false, hasVideocheck: false,
    sets: []
});

const emptySet = () => ({ home_score: '', away_score: '', rating: null, stress: null, difficulty: null, notes: '' });

const MatchDiaryView = ({ user, onOpenSearch }) => {
    const [tab, setTab] = useState('diary');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);       // null = nessun editor aperto
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const [importOpen, setImportOpen] = useState(false);
    const [importable, setImportable] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [period, setPeriod] = useState('all');
    const [notReady, setNotReady] = useState(false);

    // Obiettivi personali
    const [goals, setGoals] = useState([]);
    const [goalsReady, setGoalsReady] = useState(true);
    const [newGoalTitle, setNewGoalTitle] = useState('');

    // Rapporto di fine stagione
    const [reportSeason, setReportSeason] = useState(null);

    useEffect(() => { loadEntries(); loadGoals(); /* eslint-disable-next-line */ }, [user.id]);

    const loadGoals = async () => {
        const res = await fetchGoals(user.id);
        setGoals(res.goals);
        setGoalsReady(!res.notReady);
    };

    // Progressi indicizzati per voce di diario: servono a ripopolare l'editor.
    const goalProgressByDiary = useMemo(() => {
        const map = {};
        goals.forEach(g => (g.progress || []).forEach(pr => {
            if (!map[pr.diary_id]) map[pr.diary_id] = [];
            map[pr.diary_id].push({ goal_id: g.id, rating: pr.rating });
        }));
        return map;
    }, [goals]);

    const openGoals = useMemo(() => goals.filter(g => g.status === 'open'), [goals]);

    // Stagioni presenti nel diario, dalla piu' recente.
    const availableSeasons = useMemo(() => {
        const set = new Set(entries.map(e => seasonOf(e.match_date)));
        if (set.size === 0) set.add(seasonOf(new Date()));
        return [...set].sort((a, b) => b - a);
    }, [entries]);

    const loadEntries = async () => {
        setLoading(true);
        const res = await fetchDiaryEntries(user.id);
        setEntries(res.entries);
        setNotReady(res.notReady);
        setLoading(false);
    };

    // --- Editor ---------------------------------------------------------
    const splitDate = (iso) => {
        if (!iso) return { d: '', t: '' };
        const dt = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return {
            d: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
            t: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
        };
    };

    const openNew = () => {
        // Chi scrive il diario e' quasi sempre 1° arbitro: si parte da li',
        // col proprio nome gia' al posto giusto.
        setForm({
            ...emptyDiaryEntry(),
            my_role: '1° Arbitro',
            first_referee: profileName,
            hasSecond: true,
            hasScorer: false,
            hasVideocheck: false,
            goalProgress: []
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const openEdit = (e) => {
        const { d, t } = splitDate(e.match_date);
        setForm({
            ...e,
            match_date: d,
            match_time: t,
            sets: (e.sets || []).map(s => ({
                home_score: s.home_score ?? '', away_score: s.away_score ?? '',
                rating: s.rating, stress: s.stress, difficulty: s.difficulty, notes: s.notes || ''
            })),
            goalProgress: goalProgressByDiary[e.id] || [],
            hasSecond: !!(e.second_referee || '').trim(),
            hasScorer: !!(e.scorekeeper || '').trim(),
            hasVideocheck: !!(e.videocheck || '').trim()
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const openFromDesignation = (d) => {
        const draft = designationToDiaryDraft(d, user);
        const { d: dd, t } = splitDate(draft.match_date);
        setForm({
            ...draft, match_date: dd, match_time: t, goalProgress: [],
            hasSecond: !!(draft.second_referee || '').trim(),
            hasScorer: !!(draft.scorekeeper || '').trim(),
            hasVideocheck: !!(draft.videocheck || '').trim()
        });
        setImportOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const loadImportable = async () => {
        setImportLoading(true);
        setImportOpen(true);
        const used = entries.map(e => e.designation_id).filter(Boolean);
        setImportable(await fetchImportableMatches(user.id, used));
        setImportLoading(false);
    };

    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Cambiando ruolo il proprio nome si sposta nella casella giusta e
    // sparisce da quella vecchia, cosi' non resta duplicato.
    const setMyRole = (role) => setForm(f => {
        const target = ROLE_TO_CREW_FIELD[role];
        const next = { ...f, my_role: role };
        Object.values(ROLE_TO_CREW_FIELD).forEach(field => {
            if (next[field] && next[field].trim() === profileName) next[field] = '';
        });
        if (target) next[target] = profileName;
        return next;
    });

    // Spegnendo un interruttore si svuota anche il nome, altrimenti
    // resterebbe salvato un collega che non c'era.
    const toggleCrewSlot = (flag, field) => setForm(f => {
        const on = !f[flag];
        const next = { ...f, [flag]: on };
        if (!on) {
            next[field] = '';
            // Se il ruolo dichiarato era proprio quello, si torna a 1° arbitro.
            if (ROLE_TO_CREW_FIELD[f.my_role] === field) {
                next.my_role = '1° Arbitro';
                next.first_referee = next.first_referee || profileName;
            }
        }
        return next;
    });
    const setSetField = (i, k, v) => setForm(f => ({
        ...f, sets: f.sets.map((s, idx) => idx === i ? { ...s, [k]: v } : s)
    }));

    // Il numero di set giocati si ricava dal risultato: evita di doverli
    // aggiungere a mano uno per uno.
    const syncSetsFromResult = () => {
        const total = (Number(form.home_sets) || 0) + (Number(form.away_sets) || 0);
        if (total < 1) { alert("Inserisci prima il risultato in set."); return; }
        setForm(f => {
            const next = [...f.sets];
            while (next.length < total) next.push(emptySet());
            return { ...f, sets: next.slice(0, Math.min(total, 5)) };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.match_date) { alert("Inserisci la data della gara."); return; }
        setSaving(true);
        const iso = new Date(`${form.match_date}T${form.match_time || '00:00'}:00`).toISOString();
        const { error, id } = await saveDiaryEntry(user.id, { ...form, match_date: iso }, form.sets);
        if (error) { setSaving(false); alert("Errore nel salvataggio: " + (error.message || error)); return; }

        if (goalsReady && id) {
            const { error: gErr } = await saveGoalProgress(id, form.goalProgress || []);
            if (gErr) console.error("Progressi obiettivi non salvati:", gErr);
        }
        setSaving(false);
        setForm(null);
        loadEntries();
        loadGoals();
    };

    // --- Obiettivi ---
    const toggleGoalWorked = (goalId) => {
        setForm(f => {
            const list = f.goalProgress || [];
            return list.some(g => g.goal_id === goalId)
                ? { ...f, goalProgress: list.filter(g => g.goal_id !== goalId) }
                : { ...f, goalProgress: [...list, { goal_id: goalId, rating: null }] };
        });
    };

    const setGoalWorkedRating = (goalId, rating) => {
        setForm(f => ({
            ...f,
            goalProgress: (f.goalProgress || []).map(g => g.goal_id === goalId ? { ...g, rating } : g)
        }));
    };

    const handleAddGoal = async (title, sourceId = null) => {
        const { error } = await createGoal(user.id, title, '', sourceId);
        if (error) { alert("Errore: " + (error.message || error)); return false; }
        loadGoals();
        return true;
    };

    const handleCreateGoalFromForm = async () => {
        const text = (form.action_points || '').trim();
        if (!text) { alert("Scrivi prima su cosa vuoi lavorare."); return; }
        // La prima riga fa da titolo: il resto resta nella nota del diario.
        const title = text.split(String.fromCharCode(10))[0].slice(0, 200);
        if (await handleAddGoal(title, form.id || null)) {
            alert("Obiettivo creato. Lo trovi nella scheda Obiettivi.");
        }
    };

    const handleToggleGoalStatus = async (g) => {
        const { error } = await setGoalStatus(g.id, g.status === 'open' ? 'done' : 'open');
        if (error) { alert("Errore: " + error.message); return; }
        loadGoals();
    };

    const handleDeleteGoal = async (g) => {
        if (!window.confirm(`Eliminare l'obiettivo "${g.title}"?`)) return;
        const { error } = await deleteGoal(g.id);
        if (error) { alert("Errore: " + error.message); return; }
        loadGoals();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questa voce del diario? L'operazione non è reversibile.")) return;
        const { error } = await deleteDiaryEntry(id);
        if (error) { alert("Errore: " + error.message); return; }
        setEntries(prev => prev.filter(e => e.id !== id));
    };

    // --- Dati per il Recap ----------------------------------------------
    const profileName = (user.full_name || '').trim();

    const periodEntries = useMemo(() => {
        if (period === 'all') return entries;
        const months = period === '3m' ? 3 : 12;
        const from = new Date();
        from.setMonth(from.getMonth() - months);
        return entries.filter(e => new Date(e.match_date) >= from);
    }, [entries, period]);

    const stats = useMemo(() => {
        const list = periodEntries;
        const ratings = list.map(e => e.overall_rating).filter(v => v != null);
        const stresses = list.map(e => e.overall_stress).filter(v => v != null);
        const allSets = list.flatMap(e => e.sets || []);
        const avg = (arr) => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;

        const perSet = [1, 2, 3, 4, 5].map(n => {
            const s = allSets.filter(x => x.set_number === n);
            const r = s.map(x => x.rating).filter(v => v != null);
            const st = s.map(x => x.stress).filter(v => v != null);
            return {
                set: n,
                count: s.length,
                rating: r.length ? Number(avg(r).toFixed(2)) : 0,
                stress: st.length ? Number(avg(st).toFixed(2)) : 0
            };
        });

        const groupBy = (key) => {
            const map = {};
            list.forEach(e => {
                const k = (e[key] || '').trim() || 'Non indicato';
                if (!map[k]) map[k] = { label: k, count: 0, ratings: [] };
                map[k].count++;
                if (e.overall_rating != null) map[k].ratings.push(e.overall_rating);
            });
            return Object.values(map)
                .map(r => ({ label: r.label, count: r.count, avg: r.ratings.length ? avg(r.ratings).toFixed(1) : null }))
                .sort((a, b) => b.count - a.count);
        };

        // Con chi hai lavorato. I nomi sono testo libero: raggruppo su una
        // chiave normalizzata e mostro la grafia più frequente.
        const partnerMap = {};
        list.forEach(e => {
            crewFieldsOf(e).forEach(f => {
                const raw = (e[f] || '').trim().replace(/\s+/g, ' ');
                if (!raw) return;
                // Il proprio nome non è un collega.
                if (raw.toLowerCase() === (profileName || '').toLowerCase()) return;
                const key = raw.toLowerCase();
                if (!partnerMap[key]) partnerMap[key] = { names: {}, count: 0, ratings: [], stresses: [], roles: {} };
                const p = partnerMap[key];
                p.names[raw] = (p.names[raw] || 0) + 1;
                p.roles[CREW_LABELS[f]] = (p.roles[CREW_LABELS[f]] || 0) + 1;
                p.count++;
                if (e.overall_rating != null) p.ratings.push(e.overall_rating);
                if (e.overall_stress != null) p.stresses.push(e.overall_stress);
            });
        });

        const partners = Object.values(partnerMap).map(p => ({
            label: Object.entries(p.names).sort((a, b) => b[1] - a[1])[0][0],
            role: Object.entries(p.roles).sort((a, b) => b[1] - a[1])[0][0],
            count: p.count,
            rating: p.ratings.length ? Number(avg(p.ratings).toFixed(2)) : null,
            stress: p.stresses.length ? Number(avg(p.stresses).toFixed(2)) : null
        })).sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1) || b.count - a.count);

        // Il grafico va letto da sinistra (più vecchia) a destra (più recente).
        const trend = [...list]
            .filter(e => e.overall_rating != null)
            .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
            .slice(-14)
            .map(e => ({
                value: e.overall_rating,
                label: new Date(e.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
                title: `${e.home_team} - ${e.away_team}`
            }));

        return {
            total: list.length,
            avgRating: avg(ratings),
            avgStress: avg(stresses),
            setsLogged: allSets.length,
            perSet,
            byCategory: groupBy('category'),
            byLevel: groupBy('level'),
            partners,
            trend
        };
    }, [periodEntries, profileName]);

    // Ruoli che l'utente puo' dichiarare per se stesso, coerenti con come e'
    // stata composta la terma di questa gara.
    const availableRoles = form ? [
        '1° Arbitro',
        ...(form.hasSecond ? ['2° Arbitro'] : []),
        ...(form.hasScorer ? ['Segnapunti'] : []),
        ...(form.hasVideocheck && levelHasVideocheck(form.level) ? ['Addetto al VideoCheck'] : [])
    ] : [];

    const activeCrewFields = form ? [
        'first_referee',
        ...(form.hasSecond ? ['second_referee'] : []),
        ...(form.hasScorer ? ['scorekeeper'] : []),
        ...(form.hasVideocheck && levelHasVideocheck(form.level) ? ['videocheck'] : [])
    ] : [];

    // --- Render ---------------------------------------------------------
    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn pb-20">

            {/* Intestazione */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3"><NotebookPen className="h-7 w-7 text-indigo-600" /> Diario Gare</h2>
                    <p className="text-sm text-slate-500 mt-1 font-medium">Le tue annotazioni post-gara. Visibili solo a te.</p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <button onClick={loadImportable} className="flex-1 md:flex-none px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold flex items-center justify-center gap-2 text-sm transition-colors text-slate-700 whitespace-nowrap">
                        <Download className="h-4 w-4" /> Importa gara
                    </button>
                    <button onClick={openNew} className="flex-1 md:flex-none py-2.5 px-6 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all active:scale-95 whitespace-nowrap">
                        <Plus className="h-5 w-5" /> Nuova voce
                    </button>
                </div>
            </div>

            {notReady && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-amber-900">Diario non ancora attivo</p>
                        <p className="text-sm text-amber-800 mt-1">
                            Le tabelle del diario non sono presenti sul database. Un amministratore deve eseguire
                            il patch <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-xs">patch_diario_gare.sql</code>.
                            Fino ad allora questa sezione non salva nulla.
                        </p>
                    </div>
                </div>
            )}

            {/* Diario / Recap */}
            <div className="rp-card p-2 rounded-2xl flex gap-1.5">
                <button onClick={() => setTab('diary')} className={`flex-1 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors ${tab === 'diary' ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <NotebookPen className="h-5 w-5" /> <span className="hidden xs:inline">Diario</span>
                </button>
                <button onClick={() => setTab('goals')} className={`flex-1 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors ${tab === 'goals' ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <Target className="h-5 w-5" /> <span className="hidden xs:inline">Obiettivi</span>
                    {openGoals.length > 0 && <span className="text-[10px] font-black bg-indigo-600 text-white rounded-full px-1.5 py-0.5 leading-none">{openGoals.length}</span>}
                </button>
                <button onClick={() => setTab('recap')} className={`flex-1 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors ${tab === 'recap' ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <BarChart3 className="h-5 w-5" /> <span className="hidden xs:inline">Recap</span>
                </button>
            </div>

            {/* Modale importazione */}
            {importOpen && (
                <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn" onClick={() => setImportOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="bg-slate-900 p-4 text-white flex justify-between items-center shrink-0">
                            <h3 className="font-bold flex items-center gap-2"><Calendar className="h-5 w-5" /> Importa da Gare</h3>
                            <button onClick={() => setImportOpen(false)} aria-label="Chiudi"><X className="h-5 w-5 hover:text-slate-300" /></button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
                            {importLoading ? (
                                <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
                            ) : importable.length === 0 ? (
                                <p className="text-center text-slate-400 italic py-10 text-sm">
                                    Nessuna gara passata da importare.<br />Le gare già annotate non compaiono qui.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {importable.map(d => (
                                        <button key={d.id} onClick={() => openFromDesignation(d)} className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                                            <p className="font-bold text-slate-800 text-sm">{d.home_team} - {d.away_team}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                {new Date(d.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                                                {d.series ? ` · ${d.series}` : ''}{d.level ? ` · ${d.level}` : ''}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Editor */}
            {form && (
                <form onSubmit={handleSave} className="bg-slate-50 p-5 md:p-6 rounded-2xl border border-slate-200 shadow-inner space-y-5 animate-slide-in">
                    <div className="flex justify-between items-center">
                        <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                            <PenTool className="h-5 w-5 text-indigo-600" /> {form.id ? 'Modifica voce' : 'Nuova voce di diario'}
                        </h3>
                        <button type="button" onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700" aria-label="Chiudi editor"><X className="h-5 w-5" /></button>
                    </div>

                    {/* Gara */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">La gara</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input required placeholder="Squadra di casa *" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full focus:ring-2 focus:ring-indigo-500 outline-none" value={form.home_team} onChange={e => setField('home_team', e.target.value)} />
                            <input required placeholder="Squadra ospite *" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full focus:ring-2 focus:ring-indigo-500 outline-none" value={form.away_team} onChange={e => setField('away_team', e.target.value)} />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Data *</label>
                                <input required type="date" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full" value={form.match_date} onChange={e => setField('match_date', e.target.value)} />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Ora</label>
                                <input type="time" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full" value={form.match_time} onChange={e => setField('match_time', e.target.value)} />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Livello</label>
                                <select className="border border-slate-300 p-2.5 rounded-lg text-sm w-full bg-white" value={form.level} onChange={e => setField('level', e.target.value)}>
                                    {LIVELLI_GARA.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Genere</label>
                                <select className="border border-slate-300 p-2.5 rounded-lg text-sm w-full bg-white" value={form.gender} onChange={e => setField('gender', e.target.value)}>
                                    {GENERI_GARA.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <input placeholder="Campionato (es. Serie C)" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full" value={form.series} onChange={e => setField('series', e.target.value)} />
                            <input placeholder="Categoria (es. Under 18)" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full" value={form.category} onChange={e => setField('category', e.target.value)} />
                            <input placeholder="Luogo" className="border border-slate-300 p-2.5 rounded-lg text-sm w-full" value={form.location} onChange={e => setField('location', e.target.value)} />
                        </div>
                    </div>

                    {/* Terna */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Come era composta la terna</p>

                        <div className="space-y-2">
                            <label className="flex items-center gap-2.5 cursor-pointer">
                                <input type="checkbox" checked={!!form.hasSecond} onChange={() => toggleCrewSlot('hasSecond', 'second_referee')} className="accent-indigo-600 h-4 w-4 cursor-pointer" />
                                <span className="text-sm font-medium text-slate-700">Gara diretta da due arbitri</span>
                            </label>
                            <label className="flex items-center gap-2.5 cursor-pointer">
                                <input type="checkbox" checked={!!form.hasScorer} onChange={() => toggleCrewSlot('hasScorer', 'scorekeeper')} className="accent-indigo-600 h-4 w-4 cursor-pointer" />
                                <span className="text-sm font-medium text-slate-700">Segnapunti designato</span>
                            </label>
                            {levelHasVideocheck(form.level) && (
                                <label className="flex items-center gap-2.5 cursor-pointer">
                                    <input type="checkbox" checked={!!form.hasVideocheck} onChange={() => toggleCrewSlot('hasVideocheck', 'videocheck')} className="accent-indigo-600 h-4 w-4 cursor-pointer" />
                                    <span className="text-sm font-medium text-slate-700">Addetto al VideoCheck</span>
                                </label>
                            )}
                        </div>

                        <div className="pt-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Il tuo ruolo</label>
                            <div className="flex flex-wrap gap-2">
                                {availableRoles.map(r => (
                                    <button key={r} type="button" onClick={() => setMyRole(r)} className={`px-3 py-1.5 text-xs rounded-lg border font-bold transition-colors ${form.my_role === r ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>{r}</button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {activeCrewFields.map(f => {
                                const isMe = ROLE_TO_CREW_FIELD[form.my_role] === f;
                                return (
                                    <div key={f}>
                                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">
                                            {CREW_LABELS[f]}{isMe && <span className="text-indigo-500 normal-case font-semibold"> · tu</span>}
                                        </label>
                                        <div className="flex gap-1.5">
                                            <input
                                                placeholder="Nome e cognome"
                                                className="border border-slate-300 p-2.5 rounded-lg text-sm w-full min-w-0"
                                                value={form[f] || ''}
                                                onChange={e => setField(f, e.target.value)}
                                            />
                                            {!isMe && onOpenSearch && (
                                                <button
                                                    type="button"
                                                    title="Cerca fra gli iscritti"
                                                    aria-label={`Cerca ${CREW_LABELS[f]} fra gli iscritti`}
                                                    onClick={() => onOpenSearch((u) => setField(f, u.full_name))}
                                                    className="shrink-0 px-3 rounded-lg border border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                                                >
                                                    <Search className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Risultato e set */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-4">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Risultato in set</p>
                                <div className="flex items-center gap-2">
                                    <input type="number" min="0" max="3" className="border border-slate-300 p-2 rounded-lg text-center text-lg font-black w-16" value={form.home_sets} onChange={e => setField('home_sets', e.target.value)} aria-label="Set squadra di casa" />
                                    <span className="text-slate-400 font-black">-</span>
                                    <input type="number" min="0" max="3" className="border border-slate-300 p-2 rounded-lg text-center text-lg font-black w-16" value={form.away_sets} onChange={e => setField('away_sets', e.target.value)} aria-label="Set squadra ospite" />
                                </div>
                            </div>
                            <button type="button" onClick={syncSetsFromResult} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors">
                                Genera i set dal risultato
                            </button>
                        </div>

                        {form.sets.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Nessun set valutato. Inserisci il risultato e premi "Genera i set dal risultato", oppure aggiungili a mano.</p>
                        ) : (
                            <div className="space-y-3">
                                {form.sets.map((s, i) => (
                                    <div key={i} className="border border-slate-200 rounded-xl p-3 bg-slate-50/60">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-xs font-black text-slate-700 uppercase tracking-wider">Set {i + 1}</span>
                                            <div className="flex items-center gap-2">
                                                <input type="number" min="0" placeholder="—" className="border border-slate-300 p-1.5 rounded text-center text-sm w-14 bg-white" value={s.home_score} onChange={e => setSetField(i, 'home_score', e.target.value)} aria-label={`Punti casa set ${i + 1}`} />
                                                <span className="text-slate-400 text-xs font-bold">-</span>
                                                <input type="number" min="0" placeholder="—" className="border border-slate-300 p-1.5 rounded text-center text-sm w-14 bg-white" value={s.away_score} onChange={e => setSetField(i, 'away_score', e.target.value)} aria-label={`Punti ospite set ${i + 1}`} />
                                                <button type="button" onClick={() => setForm(f => ({ ...f, sets: f.sets.filter((_, idx) => idx !== i) }))} className="text-slate-300 hover:text-red-500 ml-1" aria-label={`Rimuovi set ${i + 1}`}><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Rendimento</label>
                                                <ScaleInput value={s.rating} onChange={v => setSetField(i, 'rating', v)} lowLabel="Male" highLabel="Ottimo" tone="indigo" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Stress</label>
                                                <ScaleInput value={s.stress} onChange={v => setSetField(i, 'stress', v)} lowLabel="Sereno" highLabel="Teso" tone="orange" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Difficoltà</label>
                                                <ScaleInput value={s.difficulty} onChange={v => setSetField(i, 'difficulty', v)} lowLabel="Facile" highLabel="Difficile" tone="slate" />
                                            </div>
                                        </div>
                                        <input placeholder="Note sul set (episodi, decisioni difficili...)" className="border border-slate-200 p-2 rounded-lg text-sm w-full mt-3 bg-white" value={s.notes} onChange={e => setSetField(i, 'notes', e.target.value)} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {form.sets.length < 5 && (
                            <button type="button" onClick={() => setForm(f => ({ ...f, sets: [...f.sets, emptySet()] }))} className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                                + Aggiungi set
                            </button>
                        )}
                    </div>

                    {/* Riflessione */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-4">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">La tua analisi</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold text-green-600 uppercase mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Cosa è andato bene</label>
                                <textarea rows="4" placeholder="Posizionamento, gestione della panchina, fischio..." className="border border-slate-300 p-2.5 rounded-lg text-sm w-full resize-none focus:ring-2 focus:ring-green-500 outline-none" value={form.went_well} onChange={e => setField('went_well', e.target.value)} />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-red-600 uppercase mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Cosa è andato male</label>
                                <textarea rows="4" placeholder="Episodi dubbi, cali di concentrazione..." className="border border-slate-300 p-2.5 rounded-lg text-sm w-full resize-none focus:ring-2 focus:ring-red-500 outline-none" value={form.went_bad} onChange={e => setField('went_bad', e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <label className="text-[10px] font-bold text-indigo-600 uppercase flex items-center gap-1"><Target className="h-3 w-3" /> Su cosa lavorare</label>
                                {goalsReady && (
                                    <button type="button" onClick={handleCreateGoalFromForm} className="text-[11px] font-bold text-indigo-600 hover:underline">
                                        Trasforma in obiettivo
                                    </button>
                                )}
                            </div>
                            <textarea rows="2" placeholder="Obiettivi concreti per la prossima gara..." className="border border-slate-300 p-2.5 rounded-lg text-sm w-full resize-none focus:ring-2 focus:ring-indigo-500 outline-none" value={form.action_points} onChange={e => setField('action_points', e.target.value)} />
                        </div>

                        {goalsReady && openGoals.length > 0 && (
                            <div className="pt-3 border-t border-slate-100">
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Obiettivi su cui hai lavorato</label>
                                <div className="space-y-2">
                                    {openGoals.map(g => {
                                        const worked = (form.goalProgress || []).find(x => x.goal_id === g.id);
                                        return (
                                            <div key={g.id} className={`rounded-xl border p-3 transition-colors ${worked ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200'}`}>
                                                <label className="flex items-start gap-2.5 cursor-pointer">
                                                    <input type="checkbox" checked={!!worked} onChange={() => toggleGoalWorked(g.id)} className="accent-indigo-600 h-4 w-4 mt-0.5 cursor-pointer shrink-0" />
                                                    <span className="text-sm font-medium text-slate-700 leading-snug">{g.title}</span>
                                                </label>
                                                {worked && (
                                                    <div className="mt-2.5 pl-7">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Come è andata su questo</span>
                                                        <ScaleInput value={worked.rating} onChange={v => setGoalWorkedRating(g.id, v)} lowLabel="Male" highLabel="Ottimo" tone="indigo" />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Rendimento complessivo</label>
                                <ScaleInput value={form.overall_rating} onChange={v => setField('overall_rating', v)} lowLabel="Male" highLabel="Ottimo" tone="indigo" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Stress complessivo</label>
                                <ScaleInput value={form.overall_stress} onChange={v => setField('overall_stress', v)} lowLabel="Sereno" highLabel="Teso" tone="orange" />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button type="button" onClick={() => setForm(null)} className="px-5 py-3 bg-white border border-slate-300 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-100 transition-colors">Annulla</button>
                        <button type="submit" disabled={saving} className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-60">
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : <><Save className="h-5 w-5" /> Salva nel diario</>}
                        </button>
                    </div>
                </form>
            )}

            {/* --- DIARIO --- */}
            {tab === 'diary' && (
                loading ? (
                    <div className="flex justify-center p-12"><Loader2 className="h-10 w-10 animate-spin text-indigo-500" /></div>
                ) : entries.length === 0 ? (
                    <div className="text-center py-16 rp-card rounded-2xl flex flex-col items-center">
                        <NotebookPen className="h-16 w-16 text-slate-300 mb-4" />
                        <p className="text-slate-500 font-medium text-lg">Il diario è ancora vuoto.</p>
                        <p className="text-slate-400 text-sm mt-1 max-w-md">Dopo ogni gara annota com'è andata: nel giro di qualche partita il Recap inizia a mostrarti gli andamenti.</p>
                        <button onClick={openNew} className="mt-6 text-indigo-600 font-bold hover:underline">Scrivi la prima voce</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {entries.map(e => {
                            const isOpen = expanded === e.id;
                            const dateObj = new Date(e.match_date);
                            return (
                                <div key={e.id} className="rp-card rounded-2xl overflow-hidden">
                                    <button onClick={() => setExpanded(isOpen ? null : e.id)} className="w-full text-left p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors" aria-expanded={isOpen}>
                                        <div className="flex flex-col items-center justify-center bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 min-w-[54px] shrink-0">
                                            <span className="text-lg font-black text-indigo-600 leading-none">{dateObj.getDate()}</span>
                                            <span className="text-[10px] font-bold uppercase text-slate-500 leading-none mt-1">{dateObj.toLocaleString('it-IT', { month: 'short' })}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-slate-800 text-sm truncate">{e.home_team} - {e.away_team}</p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {[e.series, e.category, e.level].filter(Boolean).join(' · ') || 'Gara'}
                                                {(e.home_sets || e.away_sets) ? ` · ${e.home_sets}-${e.away_sets}` : ''}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {e.overall_rating != null && (
                                                <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 border border-indigo-100" title="Rendimento complessivo">
                                                    <Star className="h-3.5 w-3.5 text-indigo-500" />
                                                    <span className="text-xs font-black text-indigo-700">{e.overall_rating}</span>
                                                </span>
                                            )}
                                            {e.overall_stress != null && (
                                                <span className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-50 border border-orange-100" title="Stress complessivo">
                                                    <Activity className="h-3.5 w-3.5 text-orange-500" />
                                                    <span className="text-xs font-black text-orange-700">{e.overall_stress}</span>
                                                </span>
                                            )}
                                            {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                                        </div>
                                    </button>

                                    {isOpen && (
                                        <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4 animate-fadeIn">
                                            <div className="flex flex-wrap gap-2 text-[11px]">
                                                <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-bold">Il tuo ruolo: {e.my_role}</span>
                                                {crewFieldsOf(e).map(f => e[f] ? (
                                                    <span key={f} className="px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-slate-600">
                                                        <strong className="text-slate-500">{CREW_LABELS[f]}:</strong> {e[f]}
                                                    </span>
                                                ) : null)}
                                            </div>

                                            {e.sets?.length > 0 && (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-xs min-w-[380px]">
                                                        <thead>
                                                            <tr className="text-slate-400 uppercase text-[10px] tracking-wider">
                                                                <th className="text-left font-bold py-1.5">Set</th>
                                                                <th className="text-left font-bold py-1.5">Punti</th>
                                                                <th className="text-left font-bold py-1.5">Rendimento</th>
                                                                <th className="text-left font-bold py-1.5">Stress</th>
                                                                <th className="text-left font-bold py-1.5">Difficoltà</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {e.sets.map(s => (
                                                                <tr key={s.set_number} className="border-t border-slate-100">
                                                                    <td className="py-2 font-bold text-slate-700">{s.set_number}</td>
                                                                    <td className="py-2 text-slate-600">{s.home_score != null && s.away_score != null ? `${s.home_score}-${s.away_score}` : '—'}</td>
                                                                    <td className="py-2 text-slate-600">{s.rating ?? '—'}</td>
                                                                    <td className="py-2 text-slate-600">{s.stress ?? '—'}</td>
                                                                    <td className="py-2 text-slate-600">{s.difficulty ?? '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {e.went_well && (
                                                    <div className="bg-green-50 border border-green-100 rounded-xl p-3">
                                                        <p className="text-[10px] font-bold text-green-700 uppercase mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Andato bene</p>
                                                        <p className="text-xs text-green-900 whitespace-pre-wrap leading-relaxed">{e.went_well}</p>
                                                    </div>
                                                )}
                                                {e.went_bad && (
                                                    <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                                                        <p className="text-[10px] font-bold text-red-700 uppercase mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Da migliorare</p>
                                                        <p className="text-xs text-red-900 whitespace-pre-wrap leading-relaxed">{e.went_bad}</p>
                                                    </div>
                                                )}
                                            </div>
                                            {e.action_points && (
                                                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
                                                    <p className="text-[10px] font-bold text-indigo-700 uppercase mb-1 flex items-center gap-1"><Target className="h-3 w-3" /> Su cosa lavorare</p>
                                                    <p className="text-xs text-indigo-900 whitespace-pre-wrap leading-relaxed">{e.action_points}</p>
                                                </div>
                                            )}

                                            <div className="flex gap-2 pt-1">
                                                <button onClick={() => openEdit(e)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold text-slate-700 flex items-center gap-1.5 transition-colors"><Edit className="h-3.5 w-3.5" /> Modifica</button>
                                                <button onClick={() => handleDelete(e.id)} className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {reportSeason !== null && (
                <SeasonReport
                    user={user}
                    entries={entries}
                    goals={goals}
                    season={reportSeason}
                    seasons={availableSeasons}
                    onSeasonChange={setReportSeason}
                    onClose={() => setReportSeason(null)}
                />
            )}

            {/* --- OBIETTIVI --- */}
            {tab === 'goals' && (
                !goalsReady ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                        <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold text-amber-900">Obiettivi non ancora attivi</p>
                            <p className="text-sm text-amber-800 mt-1">
                                Un amministratore deve eseguire il patch <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-xs">patch_refpublic_41.sql</code>.
                            </p>
                        </div>
                    </div>
                ) : (
                <div className="space-y-4">
                    <div className="rp-card p-4 rounded-2xl">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Nuovo obiettivo</label>
                        <div className="flex gap-2">
                            <input
                                placeholder="Es. Migliorare il posizionamento sul muro"
                                className="flex-1 border border-slate-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                value={newGoalTitle}
                                onChange={e => setNewGoalTitle(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && newGoalTitle.trim()) { handleAddGoal(newGoalTitle).then(ok => ok && setNewGoalTitle('')); } }}
                            />
                            <button
                                onClick={() => handleAddGoal(newGoalTitle).then(ok => ok && setNewGoalTitle(''))}
                                disabled={!newGoalTitle.trim()}
                                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-40 shrink-0"
                            >
                                Aggiungi
                            </button>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                            Gli obiettivi aperti compaiono quando scrivi una voce di diario: spunta quelli su cui hai lavorato.
                        </p>
                    </div>

                    {goals.length === 0 ? (
                        <div className="text-center py-16 rp-card rounded-2xl flex flex-col items-center">
                            <Target className="h-16 w-16 text-slate-300 mb-4" />
                            <p className="text-slate-500 font-medium text-lg">Nessun obiettivo.</p>
                            <p className="text-slate-400 text-sm mt-1 max-w-md">Scrivi qui cosa vuoi migliorare, oppure crealo dal campo "su cosa lavorare" di una voce di diario.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {['open', 'done'].map(status => {
                                const list = goals.filter(g => g.status === status);
                                if (list.length === 0) return null;
                                return (
                                    <div key={status}>
                                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 px-1">
                                            {status === 'open' ? `Aperti (${list.length})` : `Raggiunti (${list.length})`}
                                        </h3>
                                        <div className="space-y-2">
                                            {list.map(g => {
                                                const worked = (g.progress || []).length;
                                                const rated = (g.progress || []).map(x => x.rating).filter(v => v != null);
                                                const avgRating = rated.length ? (rated.reduce((a, b) => a + b, 0) / rated.length) : null;
                                                const done = g.status === 'done';
                                                return (
                                                    <div key={g.id} className={`bg-white rounded-xl border p-4 flex items-start gap-3 ${done ? 'border-slate-200 opacity-70' : 'border-slate-200'}`}>
                                                        <button
                                                            onClick={() => handleToggleGoalStatus(g)}
                                                            aria-label={done ? 'Riapri obiettivo' : 'Segna come raggiunto'}
                                                            className={`shrink-0 mt-0.5 transition-colors ${done ? 'text-green-500' : 'text-slate-300 hover:text-indigo-500'}`}
                                                        >
                                                            {done ? <CircleCheck className="h-6 w-6" /> : <Square className="h-6 w-6" />}
                                                        </button>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`font-bold text-sm leading-snug ${done ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{g.title}</p>
                                                            <p className="text-xs text-slate-500 mt-1">
                                                                {worked === 0
                                                                    ? 'Non ancora ripreso in una gara.'
                                                                    : `Ripreso in ${worked} ${worked === 1 ? 'gara' : 'gare'}${avgRating ? ` \u00b7 resa media ${avgRating.toFixed(1)}/5` : ''}`}
                                                            </p>
                                                        </div>
                                                        <button onClick={() => handleDeleteGoal(g)} aria-label="Elimina obiettivo" className="text-slate-300 hover:text-red-500 shrink-0">
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                )
            )}

            {/* --- RECAP --- */}
            {tab === 'recap' && (
                <div className="space-y-6">
                    <div className="rp-card p-3 rounded-2xl flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1">Periodo</span>
                        {[{ id: 'all', label: 'Tutto' }, { id: '12m', label: 'Ultimi 12 mesi' }, { id: '3m', label: 'Ultimi 3 mesi' }].map(p => (
                            <button key={p.id} onClick={() => setPeriod(p.id)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${period === p.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{p.label}</button>
                        ))}
                        {entries.length > 0 && (
                            <button
                                onClick={() => setReportSeason(availableSeasons[0])}
                                className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                            >
                                <Printer className="h-3.5 w-3.5" /> Rapporto di stagione
                            </button>
                        )}
                    </div>

                    {stats.total === 0 ? (
                        <div className="text-center py-16 rp-card rounded-2xl flex flex-col items-center">
                            <BarChart3 className="h-16 w-16 text-slate-300 mb-4" />
                            <p className="text-slate-500 font-medium text-lg">Nessun dato per questo periodo.</p>
                            <p className="text-slate-400 text-sm mt-1">Annota qualche gara e qui comparirà il tuo andamento.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <DiaryStatTile label="Gare annotate" value={stats.total} icon={NotebookPen} tone="slate" />
                                <DiaryStatTile label="Rendimento medio" value={stats.avgRating ? stats.avgRating.toFixed(1) : '—'} suffix={stats.avgRating ? '/5' : ''} icon={Star} tone="indigo" />
                                <DiaryStatTile label="Stress medio" value={stats.avgStress ? stats.avgStress.toFixed(1) : '—'} suffix={stats.avgStress ? '/5' : ''} icon={Activity} tone="orange" />
                                <DiaryStatTile label="Set valutati" value={stats.setsLogged} icon={Layers} tone="green" />
                            </div>

                            <div className="rp-card p-5 rounded-2xl">
                                <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><TrendingUp className="h-5 w-5 text-indigo-600" /> Andamento del rendimento</h3>
                                <p className="text-xs text-slate-500 mb-4">Le ultime {Math.min(stats.trend.length, 14)} gare valutate, dalla più vecchia alla più recente.</p>
                                <DiaryTrendChart points={stats.trend} />
                            </div>

                            <div className="rp-card p-5 rounded-2xl">
                                <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><BarChart3 className="h-5 w-5 text-indigo-600" /> Rendimento e stress per set</h3>
                                <p className="text-xs text-slate-500 mb-4">Media su tutte le gare del periodo: utile per capire in quale set tendi a calare.</p>
                                <DiarySetBars rows={stats.perSet} />
                            </div>

                            {stats.partners.length > 0 && (
                                <div className="rp-card p-5 rounded-2xl">
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><Handshake className="h-5 w-5 text-indigo-600" /> Con chi sei sceso in campo</h3>
                                    <p className="text-xs text-slate-500 mb-4">Ordinati per rendimento medio nelle gare in cui era in campo con te.</p>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm min-w-[420px]">
                                            <thead>
                                                <tr className="text-slate-400 uppercase text-[10px] tracking-wider">
                                                    <th className="text-left font-bold py-2">Collega</th>
                                                    <th className="text-left font-bold py-2">Ruolo</th>
                                                    <th className="text-right font-bold py-2">Gare</th>
                                                    <th className="text-right font-bold py-2">Rendimento</th>
                                                    <th className="text-right font-bold py-2">Stress</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.partners.map(pt => (
                                                    <tr key={pt.label} className="border-t border-slate-100">
                                                        <td className="py-2.5 font-bold text-slate-700">{pt.label}</td>
                                                        <td className="py-2.5 text-slate-500 text-xs">{pt.role}</td>
                                                        <td className="py-2.5 text-right text-slate-600">{pt.count}</td>
                                                        <td className="py-2.5 text-right font-bold text-indigo-700">{pt.rating ?? '\u2014'}</td>
                                                        <td className="py-2.5 text-right text-orange-700">{pt.stress ?? '\u2014'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                                        Con una sola gara insieme il dato dice ancora poco: diventa utile dopo qualche partita.
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="rp-card p-5 rounded-2xl">
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><Layers className="h-5 w-5 text-indigo-600" /> Per categoria</h3>
                                    <p className="text-xs text-slate-500 mb-4">Numero di gare · rendimento medio</p>
                                    <DiaryDistributionBars rows={stats.byCategory} />
                                </div>
                                <div className="rp-card p-5 rounded-2xl">
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><Award className="h-5 w-5 text-orange-600" /> Per livello</h3>
                                    <p className="text-xs text-slate-500 mb-4">Numero di gare · rendimento medio</p>
                                    <DiaryDistributionBars rows={stats.byLevel} accent={CHART_STRESS} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};


function App() {
  const [session, setSession] = useState(null);
  const [authBlocked, setAuthBlocked] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  const [quizInitialMode, setQuizInitialMode] = useState('training');
  const [isNewUser, setIsNewUser] = useState(false);
  const [profileStack, setProfileStack] = useState([]); 
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [notificationToast, setNotificationToast] = useState(null);
  
  const [searchModalCallback, setSearchModalCallback] = useState(null); 
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIosInstallOpen, setIsIosInstallOpen] = useState(false);

  const [designationPreFilter, setDesignationPreFilter] = useState('');
  const [userLocation, setUserLocation] = useState(null); 
  const [followingList, setFollowingList] = useState([]);
  const [viewMode, setViewMode] = useState('global'); 
  const [committeesList, setCommitteesList] = useState(COMITATI_FALLBACK);

  const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
  };

  // --- GESTIONE NOTIFICHE ---
  // Il timer del toast va azzerato a ogni nuova notifica, altrimenti il
  // timeout della precedente chiudeva anticipatamente quella appena arrivata.
  const toastTimerRef = useRef(null);
  const dismissToast = () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
      setNotificationToast(null);
  };
  const showNotificationUI = (title, text, action) => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      setNotificationToast({ title, text, action });
      toastTimerRef.current = setTimeout(() => { setNotificationToast(null); toastTimerRef.current = null; }, 5000);
      sendSystemNotification(title, text); 
  };
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  const handleNotificationClick = async (notif) => {
      if (!notif.read) {
          setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
          if (notif.id && typeof notif.id === 'number') { 
             await supabase.from('notifications').update({ read: true }).eq('id', notif.id);
          }
      }
      dismissToast();

      if (notif.link_tab === 'forum' && notif.link_id) {
          handleOpenThread(notif.link_id);
      } else if (notif.link_tab === 'designations') {
          setActiveTab('designations');
      } else if (notif.link_tab === 'meetups') {
          setActiveTab('meetups');
      } else if (notif.link_tab === 'quiz') {
          if (notif.link_id === 'challenges') handleOpenChallenges();
          else setActiveTab('quiz');
      } else if (notif.action) {
          notif.action();
      }
  };

  const markAllNotificationsAsRead = async () => { 
      setNotifications(prev => prev.map(n => ({ ...n, read: true }))); 
      await supabase.from('notifications').update({ read: true }).eq('user_id', session.user.id).eq('read', false);
  };
  
  const clearNotifications = async () => { 
      setNotifications([]); 
      await supabase.from('notifications').delete().eq('user_id', session.user.id);
  };

  // --- CORE LOGIC ---
  const checkProfile = async (userId, googleAvatarUrl) => {
    if (!supabase) return;
    const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
    if (data) {
        setUserProfile(data);
        if(data.preferences?.view_mode) setViewMode(data.preferences.view_mode);
        const fList = await fetchFollowing(userId);
        setFollowingList(fList);
        const { data: notifs } = await supabase.from('notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(50);
        if(notifs) {
             setNotifications(notifs.map(n => ({ ...n, time: new Date(n.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) })));
        }
        if (googleAvatarUrl && data.avatar_url !== googleAvatarUrl) {
            await supabase.from('profiles').update({ avatar_url: googleAvatarUrl }).eq('id', userId);
            setUserProfile(prev => ({ ...prev, avatar_url: googleAvatarUrl }));
        }
    }
    setIsLoading(false);
  };

  // I comitati arrivano dalla tabella `committees`: prima erano una costante
  // nel codice, quindi ogni nuovo comitato richiedeva un rilascio dell'app.
  useEffect(() => { fetchCommittees().then(setCommitteesList); }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    if ("geolocation" in navigator) { navigator.geolocation.getCurrentPosition((position) => { setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }); }, (error) => { console.error("Errore geolocalizzazione:", error); }); }

    const handleBeforeInstallPrompt = (e) => {
        e.preventDefault(); setDeferredPrompt(e);
        if (!localStorage.getItem('pwa_install_dismissed')) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);
    if (isIos() && !isInStandaloneMode) {
        if (!localStorage.getItem('pwa_install_dismissed')) setTimeout(() => setShowInstallBanner(true), 3000);
    }
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []); 

  useEffect(() => {
    if (!supabase) { setIsLoading(false); return; }
    // Il frammento va consumato PRIMA di iscriversi a onAuthStateChange:
    // l'evento INITIAL_SESSION arriva con sessione nulla e, se vince la corsa,
    // l'app si crede disconnessa e riparte per la pagina di accesso buttando via
    // i token appena ricevuti. Era da qui che nasceva il rimbalzo infinito.
    let authSub = null, cancelled = false;
    (async () => {
        await consumeAuthFragment();
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(session);
        if (session) { setIsLoading(true); checkProfile(session.user.id, session.user.user_metadata?.avatar_url); }
        else { setIsLoading(false); }
        const sub = supabase.auth.onAuthStateChange((_event, s) => {
            setSession(s);
            if (s) { setIsLoading(true); checkProfile(s.user.id, s.user.user_metadata?.avatar_url); }
            else { setUserProfile(null); setIsLoading(false); }
        });
        authSub = sub.data.subscription;
        if (cancelled) authSub.unsubscribe();
    })();
    return () => { cancelled = true; if (authSub) authSub.unsubscribe(); };
  }, []);

  // Il rimando alla pagina di accesso sta in un effetto, non nel render: un
  // side effect durante il render parte prima ancora che consumeAuthFragment
  // abbia avuto il tempo di stabilire la sessione.
  useEffect(() => {
      if (isLoading || session) return;
      if (canRedirectToLogin()) goToLogin(); else setAuthBlocked(true);
  }, [isLoading, session]);

  // --- EFFETTO QUIZ GIORNALIERO (FIXED) ---
  useEffect(() => {
      if (!userProfile || isLoading) return;
      const settings = userProfile.notification_settings || {};
      if (settings.daily_quiz_reminder === false) return;

      const getItalyDateStr = (dateObj) => {
          return new Intl.DateTimeFormat('en-CA', {
              timeZone: 'Europe/Rome',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
          }).format(dateObj);
      };

      const checkDailyQuiz = async () => {
          const today = getItalyDateStr(new Date());

          // Se oggi hai già fatto il quiz (DB), non notificare
          const lastQuizStr = (typeof userProfile.last_quiz_date === 'string' && userProfile.last_quiz_date.includes('T'))
              ? userProfile.last_quiz_date.split('T')[0]
              : userProfile.last_quiz_date;

          if (lastQuizStr === today) return;

          // Anti-spam robusto: controlla dal DB l'ultima notifica "Allenamento Quotidiano"
          const { data: lastNotif, error } = await supabase
              .from('notifications')
              .select('created_at')
              .eq('user_id', userProfile.id)
              .eq('title', 'Allenamento Quotidiano')
              .order('created_at', { ascending: false })
              .limit(1);

          if (error) {
              console.warn("Impossibile verificare ultima notifica quiz:", error);
          } else if (lastNotif && lastNotif.length > 0) {
              const lastDay = getItalyDateStr(new Date(lastNotif[0].created_at));
              if (lastDay === today) return; // già notificato oggi
          }

          // Inserimento DB -> Attiva Listener -> Attiva Toast/Browser
          const streak = getDisplayStreak(userProfile);
          const { error: insertError } = await supabase.from('notifications').insert([{
              user_id: userProfile.id,
              title: "Allenamento Quotidiano",
              // La colonna si chiama `text` ed è NOT NULL: qui veniva passato
              // `body`, quindi ogni insert falliva e il promemoria giornaliero
              // non è mai stato creato per nessun utente.
              text: streak > 0
                  ? `Hai una streak di ${streak} ${streak === 1 ? 'giorno' : 'giorni'}: completa il quiz di oggi per non perderla!`
                  : "È ora del quiz giornaliero: inizia oggi la tua streak!",
              type: "quiz_reminder",
              link_tab: "quiz",
              read: false
          }]);
          if (insertError) console.error("Errore creazione promemoria quiz:", insertError);
      };

      checkDailyQuiz();
  }, [userProfile, isLoading]); // Niente dipendenza da notifications: evita loop/spam

  // --- PROMEMORIA PRE-GARA ---
  // Il giorno prima della gara ricorda luogo, orario e con chi sei in campo.
  useEffect(() => {
      if (!userProfile || isLoading) return;
      if ((userProfile.notification_settings || {}).match_reminder === false) return;

      const checkTomorrowMatches = async () => {
          const matches = await fetchTomorrowDesignations(userProfile.id);
          if (matches.length === 0) return;

          // Un promemoria per gara, non uno per accesso all'app.
          const { data: already, error } = await supabase
              .from('notifications')
              .select('link_id')
              .eq('user_id', userProfile.id)
              .eq('type', 'match_reminder')
              .in('link_id', matches.map(m => m.id));
          if (error) { console.warn("Verifica promemoria gara non riuscita:", error); return; }

          const sent = new Set((already || []).map(n => n.link_id));
          const pending = matches.filter(m => !sent.has(m.id));
          if (pending.length === 0) return;

          await pushNotifications(pending.map(m => {
              const when = new Date(m.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
              const crew = describeCrew(m);
              return {
                  user_id: userProfile.id,
                  type: 'match_reminder',
                  title: 'Gara domani',
                  text: `${m.home_team} - ${m.away_team}, ore ${when} a ${m.location}.${crew ? ` In campo con — ${crew}.` : ''}`,
                  link_tab: 'designations',
                  link_id: m.id
              };
          }));
      };

      checkTomorrowMatches();
  }, [userProfile, isLoading]);

  // --- CLIC SU UNA NOTIFICA PUSH ---
  // Il service worker riporta l'app in primo piano e le dice dove andare.
  useEffect(() => {
      if (!('serviceWorker' in navigator)) return;
      const onMessage = (event) => {
          const data = event.data;
          if (!data || data.type !== 'RP_NOTIFICATION_CLICK') return;
          if (data.link_tab === 'forum' && data.link_id) handleOpenThread(data.link_id);
          else if (data.link_tab === 'quiz' && data.link_id === 'challenges') handleOpenChallenges();
          else if (data.link_tab) setActiveTab(data.link_tab);
      };
      navigator.serviceWorker.addEventListener('message', onMessage);
      return () => navigator.serviceWorker.removeEventListener('message', onMessage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- ISCRIZIONE PUSH ---
  // Se il permesso è già stato dato rinfreschiamo l'iscrizione a ogni accesso:
  // l'endpoint può cambiare e le righe scadute vanno sostituite.
  useEffect(() => {
      if (!userProfile || isLoading) return;
      if (!isPushSupported() || !VAPID_PUBLIC_KEY) return;
      if (Notification.permission !== 'granted') return;
      enablePushNotifications(userProfile.id);
  }, [userProfile, isLoading]);
 // Dipende da notifications per il check anti-spam

  // --- REALTIME NOTIFICHE ---
  useEffect(() => {
    if(!session?.user?.id || !supabase || !userProfile) return;
    
    const channel = supabase.channel('notifications_listener')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${session.user.id}` }, (payload) => { 
            const newN = payload.new;
            const settings = userProfile.notification_settings || {};
            const title = newN.title || '';
            let shouldNotify = true;

            // Ogni tipo di notifica ha ora la sua preferenza. Prima venivano
            // filtrate solo `challenge` e `meetup`: i quattro interruttori
            // dell'Arena nelle impostazioni non avevano alcun effetto.
            if (newN.type === 'challenge') {
                if (title.includes('Nuova Sfida')) shouldNotify = settings.challenges_invite !== false;
                else shouldNotify = settings.challenges_update !== false;
            } else if (newN.type === 'meetup') {
                shouldNotify = settings.meetups_friends !== false;
            } else if (newN.type === 'thread_new') {
                shouldNotify = settings.threads_friends !== false;
            } else if (newN.type === 'thread_reply_own') {
                shouldNotify = settings.my_thread_replies !== false;
            } else if (newN.type === 'thread_reply_contrib') {
                shouldNotify = settings.contrib_thread_replies !== false;
            } else if (newN.type === 'quiz_reminder') {
                shouldNotify = settings.daily_quiz_reminder !== false;
            } else if (newN.type === 'match_reminder') {
                shouldNotify = settings.match_reminder !== false;
            }

            // Anche quando la notifica non va mostrata a schermo la salviamo in
            // elenco, così il centro notifiche resta completo.
            if (!shouldNotify) {
                setNotifications(prev => prev.some(n => n.id === newN.id) ? prev : [{ ...newN, time: new Date(newN.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) }, ...prev]);
            }

            if (shouldNotify) {
                const uiNotif = { ...newN, time: new Date(newN.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) };
                setNotifications(prev => prev.some(n => n.id === uiNotif.id) ? prev : [uiNotif, ...prev]);
                showNotificationUI(uiNotif.title, uiNotif.text, () => handleNotificationClick(uiNotif));
            }
        })
        .subscribe();
        
    return () => { supabase.removeChannel(channel); }
  }, [session, userProfile]);

  const handleToggleFollow = async (targetId) => {
      if (followingList.includes(targetId)) { await unfollowUser(userProfile.id, targetId); setFollowingList(prev => prev.filter(id => id !== targetId)); } 
      else { await followUser(userProfile.id, targetId); setFollowingList(prev => [...prev, targetId]); }
  };
  const handleSetViewMode = async (mode) => { setViewMode(mode); if(userProfile) await updateViewPreference(userProfile.id, mode); };
  const handleProfileUpdate = (updatedData) => { if (userProfile && userProfile.id === updatedData.id) { setUserProfile({ ...userProfile, ...updatedData }); } setProfileStack(prev => prev.map(p => p.id === updatedData.id ? { ...p, ...updatedData } : p)); };
  const handleLogout = async () => {
      if (supabase) await supabase.auth.signOut();
      setUserProfile(null); setSession(null); setProfileStack([]); setIsNewUser(false);
      // Uscire solo da qui lascerebbe la sessione viva sugli altri servizi:
      // la chiusura passa dall'origine di autenticazione.
      if (isCentralAuth()) goToLogout();
  };
  
  const handleOpenThread = (threadId) => { setCurrentThreadId(threadId); setActiveTab('forum'); setProfileStack([]); setIsFeedbackOpen(false); setIsSettingsOpen(false); };
  const handleNavigateToDesignations = (query) => { setDesignationPreFilter(query); setProfileStack([]); setActiveTab('designations'); setIsFeedbackOpen(false); setIsSettingsOpen(false); };
  const handleOpenProfile = (user) => { setProfileStack(prev => [...prev, user]); };
  const handleCloseProfile = (indexToRemove) => { setProfileStack(prev => prev.slice(0, indexToRemove)); };
  const handleOpenSearch = (callback = null) => { setSearchModalCallback(() => callback || true); };
  const handleOpenChallenges = () => { setQuizInitialMode('challenges'); setActiveTab('quiz'); };
  const handleOpenQuizGeneric = () => { setQuizInitialMode('training'); setActiveTab('quiz'); };

  const handleInstallApp = async () => {
      if (isIos()) { setIsIosInstallOpen(true); setShowInstallBanner(false); } 
      else if (deferredPrompt) { deferredPrompt.prompt(); const { outcome } = await deferredPrompt.userChoice; if (outcome === 'accepted') { setDeferredPrompt(null); setShowInstallBanner(false); } } 
      else { alert("Per installare: Clicca sul menu del browser (⋮) e scegli 'Installa app' o 'Aggiungi a schermata Home'."); }
  };
  const handleDismissInstall = () => { setShowInstallBanner(false); setIsIosInstallOpen(false); localStorage.setItem('pwa_install_dismissed', 'true'); };

  if (!supabase) return <div className="min-h-screen flex flex-col items-center justify-center p-10 text-center bg-slate-50">Configurazione Mancante</div>;
  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-orange-500"/></div>;
  // Senza sessione si va alla pagina di accesso unica.
  if (!session) return authBlocked ? <AuthStuck /> : <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-orange-500"/></div>;

  if (!userProfile) return <Onboarding session={session} onComplete={(data) => { setUserProfile(data); setIsNewUser(true); }} />;

  return (
    <CommitteesContext.Provider value={committeesList}>
    <div className="min-h-screen font-sans text-slate-900 flex flex-col pt-16 md:pt-[5.25rem]">
      <AppBackdrop />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} user={userProfile} avatarUrl={session?.user?.user_metadata?.avatar_url} onLogout={handleLogout} onOpenProfile={handleOpenProfile} onOpenSearch={() => handleOpenSearch(null)} notifications={notifications} onMarkAllRead={markAllNotificationsAsRead} onClearNotifications={clearNotifications} onOpenSettings={() => setIsSettingsOpen(true)} onNotificationClick={handleNotificationClick} onOpenQuiz={handleOpenQuizGeneric} />
      {/* Stessa scatola di barra e footer: max-w-7xl con un gutter di 1rem.
          Prima il padding orizzontale del main rientrava di altri 2rem, e i
          contenuti risultavano piu' stretti della cornice. */}
      <main className="w-full px-4 md:px-0 md:w-[calc(100%-2rem)] max-w-7xl mx-auto py-6 flex-1 pb-28 md:pb-6">
        {!['dashboard', 'feedback_hub', 'quiz', 'quiz_admin', 'following', 'diary'].includes(activeTab) && ( <ToggleViewMode viewMode={viewMode} setViewMode={handleSetViewMode} /> )}
        {activeTab === 'dashboard' && ( <Dashboard user={userProfile} isNewUser={isNewUser} onOpenThread={handleOpenThread} userLocation={userLocation} setActiveTab={setActiveTab} viewMode={viewMode} followingList={followingList} onOpenQuiz={handleOpenQuizGeneric} onOpenChallenges={handleOpenChallenges} onOpenBroadcast={() => setIsBroadcastOpen(true)} /> )}
        {activeTab === 'forum' && ( <Arena currentUser={userProfile} onOpenProfile={handleOpenProfile} currentThreadId={currentThreadId} setCurrentThreadId={setCurrentThreadId} followingList={followingList} viewMode={viewMode} /> )}
        {activeTab === 'designations' && ( <DesignationsManager user={userProfile} onOpenProfile={handleOpenProfile} onOpenSearchModal={handleOpenSearch} initialSearch={designationPreFilter} followingList={followingList} viewMode={viewMode} /> )}
        {activeTab === 'diary' && ( <MatchDiaryView user={userProfile} onOpenSearch={handleOpenSearch} /> )}
        {activeTab === 'meetups' && ( <MeetupsManager user={userProfile} userLocation={userLocation} onOpenProfile={handleOpenProfile} followingList={followingList} viewMode={viewMode} /> )}
        {activeTab === 'following' && ( <FollowingFeed user={userProfile} onOpenProfile={handleOpenProfile} followingList={followingList} /> )}
        {activeTab === 'quiz' && ( <DailyQuizView user={userProfile} onUpdateUserStreak={(res) => setUserProfile(prev => ({ ...prev, streak_count: res?.streak ?? prev.streak_count, last_quiz_date: res?.last_quiz_date ?? prev.last_quiz_date }))} onOpenSearch={handleOpenSearch} initialMode={quizInitialMode} /> )}
        {activeTab === 'feedback_hub' && userProfile.is_super_admin && <FeedbackAdminView />}
        {activeTab === 'quiz_admin' && (userProfile.is_quiz_manager || userProfile.is_super_admin) && <QuizAdminManager />}
      </main>
      {profileStack.map((profile, index) => ( <UserProfileModal key={profile.id + '_' + index} viewedUser={profile} currentUser={userProfile} avatarUrl={session?.user?.user_metadata?.avatar_url} onClose={() => handleCloseProfile(index)} onUpdate={handleProfileUpdate} onNavigateToDesignations={handleNavigateToDesignations} onOpenThread={handleOpenThread} onNavigateToMeetups={() => { setActiveTab('meetups'); setProfileStack([]); }} followingList={followingList} onToggleFollow={handleToggleFollow} onSwitchProfile={handleOpenProfile} isHidden={index !== profileStack.length - 1} /> ))}
      {searchModalCallback && <UserSearchModal onClose={() => setSearchModalCallback(null)} onOpenProfile={(user) => { if (typeof searchModalCallback === 'function') searchModalCallback(user); else handleOpenProfile(user); setSearchModalCallback(null); }} />}
      {isFeedbackOpen && <FeedbackSubmitModal user={userProfile} onClose={() => setIsFeedbackOpen(false)} />}
      {isBroadcastOpen && userProfile.is_super_admin && <BroadcastModal user={userProfile} onClose={() => setIsBroadcastOpen(false)} />}
      {isSettingsOpen && ( <SettingsModal user={userProfile} onClose={() => setIsSettingsOpen(false)} onUpdateSettings={(s) => handleProfileUpdate({ ...userProfile, notification_settings: s })} onOpenFeedback={() => { setIsSettingsOpen(false); setIsFeedbackOpen(true); }} onInstallApp={handleInstallApp} /> )}
      <InstallBanner deferredPrompt={deferredPrompt} showBanner={showInstallBanner || isIosInstallOpen} isIos={isIos() || isIosInstallOpen} onClose={handleDismissInstall} onInstall={handleInstallApp} />
      {notificationToast && ( <div onClick={() => { if(notificationToast.action) notificationToast.action(); dismissToast(); }} className={`fixed bottom-4 right-4 bg-slate-800 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-3 z-[200] animate-bounce-in cursor-pointer hover:bg-slate-700 transition-colors`}><div className="bg-orange-500 p-1 rounded-full"><Bell className="h-4 w-4 text-white" /></div><span className="text-sm font-medium">{notificationToast.title}: {notificationToast.text}</span><button onClick={(e) => { e.stopPropagation(); dismissToast(); }} aria-label="Chiudi notifica" className="ml-2 hover:bg-slate-600 rounded-full p-1"><X className="h-4 w-4"/></button></div> )}
      {/* Su mobile il footer resta nascosto: la barra di navigazione in basso
          occupa gia' quello spazio. */}
      <SiteFooter className="hidden md:block" />
    </div>
    </CommitteesContext.Provider>
  );
}

export default App;

//Se stai leggendo questo smettila di sbirciare il codice. Esci di casa e trova una ragazza. Con affetto - Kekko <3